
import * as THREE from "../../libs/three.js/build/three.module.js";
import { TextSprite } from "../TextSprite.js";
import { YDBoxGeometry } from "./YDBoxGeometry.js";
import { FBXLoader } from '../../jsm/loaders/FBXLoader.js';
import { GLTFLoader } from '../../jsm/loaders/GLTFLoader.js';
import { OBJLoader } from '../../jsm/loaders/OBJLoader.js';
import { STLLoader } from '../../jsm/loaders/STLLoader.js';
import { DRACOLoader } from '../../jsm/loaders/DRACOLoader.js';
import { PCDLoader } from '../../jsm/loaders/PCDLoader.js';

import { PointCloudProcessor } from "./sac_segmentation_plane";
import { Points } from "../Points.js";

import * as pdfjsLib from '../../libs/pdf/pdf.mjs';

// DRACOLoader 单例：workerPool/workerSourceURL 是实例字段，反复 new DRACOLoader 且不 dispose
// 会累积 Web Worker 与 blob objectURL（dispose 才 terminate worker + revoke URL）。
// 全局复用一个实例，首次使用时创建并 setDecoderPath，全生命周期不 dispose。
// Potree 在本文件中是全局引用（与 setPDFMaterial 用法一致）。
let _dracoLoader = null
function getDracoLoader() {
	if (!_dracoLoader) {
		_dracoLoader = new DRACOLoader()
		_dracoLoader.setDecoderPath(Potree.scriptPath + '/libs/three.js/examples/jsm/libs/draco/')
	}
	return _dracoLoader
}

// PPT(pptx) 依赖库按需加载：pptx-preview(把 .pptx 解析渲染成 DOM) + html2canvas(DOM→canvas)。
// 二者体积较大（pptx-preview 的 UMD 内联了 jszip/lodash/echarts 等），不适合打进主包，
// 因此首次使用 PPT 时才以 <script> 注入 UMD 构建，运行期暴露 window.pptxPreview / window.html2canvas。
// 脚本路径基于 Potree.scriptPath 定位到 libs/pptx/（与 setPDFMaterial 用 Potree.scriptPath 一致）。
//
// ⚠️ 本地补丁（重新 vendor pptx-preview 后需重打）：libs/pptx/pptx-preview.umd.js 中
// 幻灯片「图片填充背景(blipFill)」渲染时把 background-size 写成了无单位的 "1280 720"（缺 px），
// 浏览器判为非法值 → 回退成 auto → 背景图按原始尺寸显示、无法铺满幻灯片。
// 已手动改为 backgroundSize=x+"px "+b+"px"（保留库对 fillRect 裁剪的计算）。
// 定位串：r.style.backgroundSize="".concat(x," ").concat(b) → 加 "px" 单位。
let _pptLibsPromise = null
function loadPPTLibs() {
	if (window.pptxPreview && window.html2canvas) {
		return Promise.resolve({ pptxPreview: window.pptxPreview, html2canvas: window.html2canvas })
	}
	if (_pptLibsPromise) return _pptLibsPromise
	const base = ((typeof Potree !== 'undefined' && Potree.scriptPath) ? Potree.scriptPath : '.') + '/libs/pptx/'
	const inject = (src, ready) => new Promise((resolve, reject) => {
		if (ready()) return resolve()
		const el = document.createElement('script')
		el.src = src
		el.onload = () => resolve()
		el.onerror = () => reject(new Error('[YDBoxVolume] PPT 依赖脚本加载失败: ' + src))
		document.head.appendChild(el)
	})
	_pptLibsPromise = Promise.all([
		inject(base + 'html2canvas.min.js', () => !!window.html2canvas),
		inject(base + 'pptx-preview.umd.js', () => !!window.pptxPreview),
	]).then(() => ({ pptxPreview: window.pptxPreview, html2canvas: window.html2canvas }))
		.catch((err) => { _pptLibsPromise = null; throw err })
	return _pptLibsPromise
}

export class Volume extends THREE.Object3D {
	constructor(args = {}) {
		super();

		if (this.constructor.name === "Volume") {
			console.warn("Can't create object of class Volume directly. Use classes BoxVolume or SphereVolume instead.");
		}

		//console.log(this);
		//console.log(this.constructor);
		//console.log(this.constructor.name);

		this._clip = args.clip || false;
		this._visible = true;
		this.showVolumeLabel = true;
		this._modifiable = args.modifiable || true;

		//////////
		this.borderThickness = 4;
		this.fontface = 'Arial';
		this.fontsize = 14;
		this.borderColor = { r: 0, g: 0, b: 0, a: 0 };
		this.backgroundColor = { r: 255, g: 255, b: 255, a: 0 };
		this.textColor = { r: 255, g: 255, b: 255, a: 1.0 };
		this.text = 'hello';

		//////////

		this.label = new TextSprite('0');
		this.label.setBorderColor({ r: 0, g: 255, b: 0, a: 0.0 });
		this.label.setBackgroundColor({ r: 0, g: 255, b: 0, a: 0.0 });
		this.label.material.depthTest = false;
		this.label.material.depthWrite = false;
		this.label.material.transparent = true;
		this.label.position.y -= 0.5;
		this.add(this.label);

		this.label.updateMatrixWorld = () => {
			let volumeWorldPos = new THREE.Vector3();
			volumeWorldPos.setFromMatrixPosition(this.matrixWorld);
			this.label.position.copy(volumeWorldPos);
			this.label.updateMatrix();
			this.label.matrixWorld.copy(this.label.matrix);
			this.label.matrixWorldNeedsUpdate = false;

			for (let i = 0, l = this.label.children.length; i < l; i++) {
				this.label.children[i].updateMatrixWorld(true);
			}
		};

		{ // event listeners
			this.addEventListener('select', e => { });
			this.addEventListener('deselect', e => { });
		}

	}

	get visible() {
		return this._visible;
	}

	/**
	 * 显隐开关。这是隐藏 volume 的正确方式 —— 不要用 scene.removeVolume 当显隐用，
	 * 那是销毁语义（内部会 dispose，把 box/frame/label 置 null，对象再不可用）。
	 *
	 * 覆盖范围：WebGL 部分全部生效（box/frame/标签/3D 模型/全景球），靠 three 的
	 * 层级 visible 剪枝 —— renderer 的 projectObject 遇到 visible===false 直接返回，
	 * 不下探子节点，所以 VolumeTool.renderDepth 里对 box/frame 的强制置 true 不会漏画。
	 * CSS3DLayer（网页 iframe）与 HolePunchLayer（打洞 quad）各自维护 entries Map、
	 * 不是 volume 的子对象，已在其 update/同步循环里显式读本字段跟随。
	 *
	 * 不覆盖：媒体不会暂停。视频 loop=true 继续播放并持续上传 VideoTexture；
	 * THREE.Audio 挂在 listener 上，Object3D.visible 对 WebAudio 无效；图集的
	 * setInterval 自动翻页计时器继续跑。目前会停媒体的只有 deselect 分支与 dispose()。
	 * 隐藏带媒体的盒子时需要业务侧自行暂停，或改用 removeVolume 真正销毁。
	 *
	 * 另注意与 clip 正交：本字段只管盒子画不画，clip 才决定是否裁点云。
	 * visible=false 而 clip=true 时点云仍被裁（viewer.js 收集 clipBoxes 只看 clip），
	 * 要两者都关就分别设置 —— 库自己的属性面板也是两个独立复选框。
	 */
	set visible(value) {
		if (this._visible !== value) {
			this._visible = value;

			this.dispatchEvent({ type: "visibility_changed", object: this });
		}
	}

	getVolume() {
		console.warn("override this in subclass");
	}

	update() {

	};

	raycast(raycaster, intersects) {

	}

	get clip() {
		return this._clip;
	}

	set clip(value) {

		if (this._clip !== value) {
			this._clip = value;

			this.update();

			this.dispatchEvent({
				type: "clip_changed",
				object: this
			});
		}

	}

	get modifieable() {
		return this._modifiable;
	}

	set modifieable(value) {
		this._modifiable = value;

		this.update();
	}
};


export class BoxVolume extends Volume {

	constructor(args = {}) {
		super(args);

		this.constructor.counter = (this.constructor.counter === undefined) ? 0 : this.constructor.counter + 1;
		this.name = 'box_' + this.constructor.counter;

		let boxGeometry = new THREE.BoxGeometry(1, 1, 1);
		boxGeometry.computeBoundingBox();
		// 重构178 核心语法错误修复 Geometry废弃 改用 BufferGeometry
		// let boxFrameGeometry = new THREE.Geometry();
		// {
		// 	let Vector3 = THREE.Vector3;

		// 	boxFrameGeometry.vertices.push(

		// 		// bottom
		// 		new Vector3(-0.5, -0.5, 0.5),
		// 		new Vector3(0.5, -0.5, 0.5),
		// 		new Vector3(0.5, -0.5, 0.5),
		// 		new Vector3(0.5, -0.5, -0.5),
		// 		new Vector3(0.5, -0.5, -0.5),
		// 		new Vector3(-0.5, -0.5, -0.5),
		// 		new Vector3(-0.5, -0.5, -0.5),
		// 		new Vector3(-0.5, -0.5, 0.5),
		// 		// top
		// 		new Vector3(-0.5, 0.5, 0.5),
		// 		new Vector3(0.5, 0.5, 0.5),
		// 		new Vector3(0.5, 0.5, 0.5),
		// 		new Vector3(0.5, 0.5, -0.5),
		// 		new Vector3(0.5, 0.5, -0.5),
		// 		new Vector3(-0.5, 0.5, -0.5),
		// 		new Vector3(-0.5, 0.5, -0.5),
		// 		new Vector3(-0.5, 0.5, 0.5),
		// 		// sides
		// 		new Vector3(-0.5, -0.5, 0.5),
		// 		new Vector3(-0.5, 0.5, 0.5),
		// 		new Vector3(0.5, -0.5, 0.5),
		// 		new Vector3(0.5, 0.5, 0.5),
		// 		new Vector3(0.5, -0.5, -0.5),
		// 		new Vector3(0.5, 0.5, -0.5),
		// 		new Vector3(-0.5, -0.5, -0.5),
		// 		new Vector3(-0.5, 0.5, -0.5),

		// 	);

		// }
		let boxFrameGeometry = new THREE.BufferGeometry();
		// LineSegments 需要「成对顶点」定义线段，直接用 Float32Array 存储
		const vertices = new Float32Array([
			// bottom
			-0.5, -0.5, 0.5, 0.5, -0.5, 0.5,
			0.5, -0.5, 0.5, 0.5, -0.5, -0.5,
			0.5, -0.5, -0.5, -0.5, -0.5, -0.5,
			-0.5, -0.5, -0.5, -0.5, -0.5, 0.5,
			// top
			-0.5, 0.5, 0.5, 0.5, 0.5, 0.5,
			0.5, 0.5, 0.5, 0.5, 0.5, -0.5,
			0.5, 0.5, -0.5, -0.5, 0.5, -0.5,
			-0.5, 0.5, -0.5, -0.5, 0.5, 0.5,
			// sides
			-0.5, -0.5, 0.5, -0.5, 0.5, 0.5,
			0.5, -0.5, 0.5, 0.5, 0.5, 0.5,
			0.5, -0.5, -0.5, 0.5, 0.5, -0.5,
			-0.5, -0.5, -0.5, -0.5, 0.5, -0.5,
		]);
		boxFrameGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

		this.material = new THREE.MeshBasicMaterial({
			color: 0x00ff00,
			transparent: true,
			opacity: 0.3,
			depthTest: true,
			depthWrite: false
		});
		this.box = new THREE.Mesh(boxGeometry, this.material);
		this.box.geometry.computeBoundingBox();
		this.boundingBox = this.box.geometry.boundingBox;
		this.add(this.box);

		this.frame = new THREE.LineSegments(boxFrameGeometry, new THREE.LineBasicMaterial({ color: 0x000000 }));

		this.add(this.frame);

		this.update();
	}

	update() {
		this.boundingBox = this.box.geometry.boundingBox;
		this.boundingSphere = this.boundingBox.getBoundingSphere(new THREE.Sphere());

		if (this._clip) {
			this.box.visible = false;
			this.label.visible = false;
		} else {
			this.box.visible = true;
			this.label.visible = this.showVolumeLabel;
		}
	}

	raycast(raycaster, intersects) {
		let is = [];
		this.box.raycast(raycaster, is);

		if (is.length > 0) {
			let I = is[0];
			intersects.push({
				distance: I.distance,
				object: this,
				point: I.point.clone()
			});
		}
	}

	getVolume() {
		return Math.abs(this.scale.x * this.scale.y * this.scale.z);
	}

};

var hori = [
	[0, 1, 2, 3],//FRONT Adjacency 
	[0, -1, 2, 1],//BACK Adjacency
	[0, 1, -1, -2],//RIGHT Adjacency
	[0, -1, -3, -2],//LEFT Adjacency
	[0, 1, -2, -1],//TOP Adjacency
	[0, -1, -2, -3],//BOTTOM Adjacency
];
var vert = [
	[0, 0, 0, 0, 0, 0],
	[0, 0, 0, 0, 0, 0],
	[0, 0, 0, 0, 0, 0],
	[0, 0, 0, 0, 0, 0],
	[0, 0, 0, 0, 0, 1],
	[0, 0, 0, 0, 0, -1],
];
export class YDBoxVolume extends Volume {

	constructor(args = {}) {
		super(args);
		this.Faces = {
			FRONT: 0,
			BACK: 1,
			RIGHT: 2,
			LEFT: 3,
			TOP: 4,
			BOTTOM: 5,
		};
		this.constructor.counter = (this.constructor.counter === undefined) ? 0 : this.constructor.counter + 1;
		this.name = 'ydbox_' + this.constructor.counter;
		this.res = null;
		this.lock = false;
		this.lockTransX = false;
		this.lockTransY = false;
		this.lockTransZ = false;
		this.lockScaleX = false;
		this.lockScaleY = false;
		this.lockScaleZ = false;

		this.lockRotX = false;
		this.lockRotY = false;
		this.lockRotZ = false;

		// 放置时是否自动正面朝向相机（局部 +X 指向相机位置）。
		// 由 VolumeTool 或消费方在创建后设置，默认仅放置瞬间生效、不持续跟随；
		// 若同时开启下面的 faceToCameraWhilePlacing，则拖拽放置全程逐帧跟随。
		this.faceToCameraOnPlace = false;
		// 简化版：放置时只做水平偏转对准相机（绕世界 +Z 的纯 yaw，盒子高度轴保持竖直、不倾斜）。
		this.faceToCameraSimpleOnPlace = false;
		// 放置跟随鼠标阶段是否逐帧持续朝向相机（默认 false 保持原「仅落位瞬间」行为）。
		// 朝向算法沿用上面两个开关：faceToCameraOnPlace → 完整正对(含俯仰)，
		// faceToCameraSimpleOnPlace → 简化(仅水平偏转、保持竖直)，两者都未开时走简化路径。
		// 仅在放置拖拽期间生效，落位后不再跟随（由 VolumeTool.update 按 inputHandler.drag 判定）。
		this.faceToCameraWhilePlacing = false;

		this.xSize = 1.0;
		this.ySize = 1.0;
		this.zSize = 1.0;
		this.hideCloud = 0;
		this.resourceScale = null;
		this.boderMaterial = new THREE.LineBasicMaterial({ color: 0x000000 });
		this.material = new THREE.MeshBasicMaterial({
			color: 0x00ff00,
			transparent: true,
			opacity: 0.3,
			depthTest: true,
			depthWrite: false
		});

		this.parameterBase = {
			color: 0xffff00,
			transparent: true,
			opacity: 0.5,
			depthTest: true,
			depthWrite: false
		};

		// 资源内容透明度 [0,1]，1=不透明（默认）。典型用途：把图片半透明贴到点云的墙面上，
		// 透过图片看到底下的点云以便对准位置。
		// 只作用于正面的资源贴图，不影响其余 5 个盒面、黑色边框、胶囊控制条与播放按钮。
		// 网页(id=9) 也支持，但走的是打洞层的 canvas alpha 而非材质（见 setWebMaterial 注释）。
		this.resOpacity = 1;

		this.materials = [
			new THREE.MeshBasicMaterial(this.parameterBase),
			new THREE.MeshBasicMaterial(this.parameterBase),
			new THREE.MeshBasicMaterial(this.parameterBase),
			new THREE.MeshBasicMaterial(this.parameterBase),
			new THREE.MeshBasicMaterial(this.parameterBase),
			new THREE.MeshBasicMaterial(this.parameterBase),
		];
		this.materialsBackup = [
			new THREE.MeshBasicMaterial(this.parameterBase),
			new THREE.MeshBasicMaterial(this.parameterBase),
			new THREE.MeshBasicMaterial(this.parameterBase),
			new THREE.MeshBasicMaterial(this.parameterBase),
			new THREE.MeshBasicMaterial(this.parameterBase),
			new THREE.MeshBasicMaterial(this.parameterBase),
		];
		// this.sides = [
		// 		0,//front
		// 		1,//back
		// 		2,//right
		// 		3,//left
		// 		4,//top
		// 		5,//bottom
		// 	  ];
		this.sides = {
			front: 0,//front
			back: 1,//back
			left: 3,//left
			right: 2,//right
			top: 4,//top
			bottom: 5,//bottom
		};
		this.createGeometry();
		this.frame.mode = THREE.LineSegments;

		this.clickBtn = null
		// 交互按钮集合：视频/PDF/音频为单按钮，PPT 为上一页/下一页双按钮。
		// viewer 射线拾取、scale_changed 缩放、切换资源/销毁的清理都以此数组为准。
		this.clickBtns = []
		this.pdf = null
		// PPT(pptx) 离屏渲染状态：容器 DOM、幻灯片节点数组、当前页(1-based)、翻页异步竞态防护
		this.pptContainer = null
			this.pptSlides = null
			this.pptPage = null
			this._pptReqId = 0
			// PPT 后台静默预热开关：
			//   true(默认)：首帧渲染后在空闲时逐页 html2canvas 光栅化并缓存，翻页秒开；
			//   false：关闭静默预热，仅在用户点击翻页时按需光栅化当前页。
			// 因 html2canvas 同步阻塞主线程 ~100ms，静默预热会在飞行/拖动停顿后触发一次卡顿，
			// 对卡顿敏感的场景可将其置 false 改为"点击翻页触发加载"。
			this.pptPrewarmEnabled = (args.pptPrewarm !== false)
			// 翻页胶囊控制条（胶囊底 + ‹ 页码 › 一体）：mesh 与其离屏 canvas。
			// PPT(5) / PDF(3) / 图集(8) 三种资源共用同一套控制条，页码与翻页动作由
			// createCapsuleBar 的回调配置（this._barCfg）注入。
			this.capsuleBar = null
			this.capsuleBarCanvas = null
			this._barCfg = null
			// 图集(id=8) 状态：图片 URL 数组、当前页(1-based)、纹理缓存、异步竞态令牌、自动翻页
			this.galleryUrls = null
			this.galleryPage = null
			this._galleryTexCache = null
			this._galleryReqId = 0
			this.galleryAutoPlay = false
			this.galleryAutoPlayInterval = 3000
			this._galleryTimer = null
		// PDF(id=3) 页面纹理缓存与竞态令牌（结构与 PPT/图集同构）
		this._pdfTexCache = null
		this._pdfReqId = 0
		// 网页(id=9) 地址；iframe 本体由 viewer 的 CSS3D 层持有
		this.webUrl = null
		// 网页(id=9) 是否响应鼠标事件。true(默认)=可交互真网页，false=纯展示不响应鼠标
		this.webInteractive = true
		// 全景视频(id=10) 是否允许点击进入沉浸式。false(默认)=点击盒子不进入，仅作视频球展示；
		// true=主动点击盒子/球体进入沉浸（滑动/拖拽结束落在球上不触发）
		this.panoEnterEnabled = false
		// 全景手势追踪状态（区分"点击"与"滑动"，详见 _installPanoGestureTracking）
		this._panoGestureMoved = false
		this._panoGestureStart = null
		this._panoGestureCleanup = null
		// loadResFromFile 创建的 blob URL（图片/视频/音频），切换资源/销毁时统一 revoke
		this._objectUrls = []
		this.addEventListener('click', e => {
			// 优先处理带 onClick 回调的按钮（如 PPT 上一页/下一页）：命中高亮按钮即触发其回调
			if (this.clickBtns && this.clickBtns.length) {
				for (const btn of this.clickBtns) {
					if (btn.highlight === true && typeof btn.onClick === 'function') {
						btn.onClick()
						return
					}
				}
			}
		// 全景视频(id=10)：点击盒子（含球体）进入沉浸式，但仅在 panoEnterEnabled 开启时。
		// 默认 panoEnterEnabled=false：点击盒子不进入，仅作视频球展示。
		// 必须"主动点击"才进入：指针位移≥5px 的滑动/拖拽结束落在球上不触发
		//（Potree InputHandler 在 mouseup 命中同一对象时仍会派发 click，故按手势位移过滤）。
		if (this.res && this.res.id === 10 && this.panoEnterEnabled && !this._panoGestureMoved) {
			this.enterPanorama();
			return;
		}
			// 既选中了当前包围盒 按钮又是高亮。说明点击到按钮上了
			if (this.clickBtn && this.clickBtn.highlight == true) {
				let texture = null;
				if (this.res.id == 2) {
					if (this.video.paused) {
						this.video.play()
						texture = new THREE.TextureLoader().load(`${exports.resourcePath}/icons/pause.png`)
					} else {
						this.video.pause()
						texture = new THREE.TextureLoader().load(`${exports.resourcePath}/icons/play.png`)
					}
					this.clickBtn.material.map = texture
					this.clickBtn.material.needsUpdate = true;
				} else if (this.res.id == 6) {
					if (this.sound.isPlaying) {
						this.sound.stop()
						texture = new THREE.TextureLoader().load(`${exports.resourcePath}/icons/play.png`)
					} else {
						this.sound.play()
						texture = new THREE.TextureLoader().load(`${exports.resourcePath}/icons/pause.png`)

						setTimeout(() => {
							const source = this.sound.source;
							if (source) {
								source.onended = () => {
									texture = new THREE.TextureLoader().load(`${exports.resourcePath}/icons/play.png`)
									this.clickBtn.material.map = texture
									this.clickBtn.material.needsUpdate = true;

									this.sound.onEnded()
								};
							}
						}, 500);
					}
					this.clickBtn.material.map = texture
					this.clickBtn.material.needsUpdate = true;
				}
			}
		});
		this.addEventListener('deselect', e => {
			if (this.clickBtn) {
				if (this.res.id == 2) {
					let texture = new THREE.TextureLoader().load(`${exports.resourcePath}/icons/play.png`);
					this.clickBtn.material.map = texture
					this.clickBtn.material.needsUpdate = true;
					this.video.pause()
				} else if (this.res.id == 6) {
					let texture = new THREE.TextureLoader().load(`${exports.resourcePath}/icons/play.png`);
					this.clickBtn.material.map = texture
					this.clickBtn.material.needsUpdate = true;
					if (this.sound.isPlaying) {
						this.sound.stop()
					}
				}
			}
		});
		this.addEventListener('scale_changed', e => {
			const btns = (this.clickBtns && this.clickBtns.length) ? this.clickBtns : (this.clickBtn ? [this.clickBtn] : [])
			for (const btn of btns) {
				// 胶囊控制条：按自身宽高比重算缩放，保持水平胶囊形态不被父级 scale 拉变形
				if (btn.configBar && typeof btn.configBar.setBarScale === 'function') {
					btn.configBar.setBarScale()
					continue
				}
				if (!btn.configSize) continue
				const size = btn.configSize
				// this.scale 正面情况下 x 是厚度 y 是 宽度 z是 高度
				if (this.scale.y > this.scale.z) {
					const showHeight = this.scale.z * size.y
					const x = showHeight / this.scale.y
					btn.scale.set(x, size.y, 1);
				} else {
					const showWidth = this.scale.y * size.x
					const y = showWidth / this.scale.z
					btn.scale.set(size.x, y, 1);
				}
			}
			// 全景视频(id=10)：盒子尺寸变化时同步球体局部缩放。
			// 球是盒子的子节点会被父级 this.scale 再乘一次，故按轴取倒数补偿，
			// 使世界半径恒为 0.5*min(|scale|) = 盒子的内切球半径，球与最短轴的两个盒面相切。
			if (this._panoMesh && this._panoMesh.parent === this) {
				const sx = Math.abs(this.scale.x) || 1;
				const sy = Math.abs(this.scale.y) || 1;
				const sz = Math.abs(this.scale.z) || 1;
				const r = 0.5 * Math.min(sx, sy, sz);
				this._panoMesh.scale.set(r / sx, r / sy, r / sz);
			}
			// 缩放导致盒面宽高比变化时，重绘正面文字/占位文字贴图，避免被父级 scale 拉伸压缩
			this._redrawFaceTextOnScale()
		})

		this.update();
	}
	generatePointCloud(numPoints) {
		let points = [];
		for (let i = 0; i < numPoints; i++) {
			let x = Math.random() * 10;
			let y = Math.random() * 10;
			// 假设平面方程为 z = 2x + 3y + 1 + 随机噪声
			let z = 2 * x + 3 * y + 1 + (Math.random() - 0.5); // 添加一些噪声
			let pt = new THREE.Vector3(x, y, z);
			points.push(pt);
		}
		return points;
	}
	computeCentroid(points) {
		let centroid = new THREE.Vector3(0, 0, 0);
		const n = points.length;
		points.forEach(point => {
			centroid.x += point.x;
			centroid.y += point.y;
			centroid.z += point.z;
		});
		centroid.x /= n;
		centroid.y /= n;
		centroid.z /= n;
		return centroid;
	}
	computeCentroidArray(points) {
		let centroid = new THREE.Vector3(0, 0, 0);
		const n = points.length;
		points.forEach(point => {
			centroid.x += point[0];
			centroid.y += point[1];
			centroid.z += point[2];
		});
		centroid.x /= n;
		centroid.y /= n;
		centroid.z /= n;
		return centroid;
	}
	///////////////////
	/**
	 * Extract a plane from a point cloud using RANSAC.
	 * @param {Array} points - The input point cloud, array of {x, y, z}.
	 * @param {number} distanceThreshold - The maximum distance from a point to the plane to be considered inliers.
	 * @param {number} maxIterations - Maximum number of RANSAC iterations.
	 * @returns {Object} - The plane equation and inliers.
	 */
	ransacPlane(points, distanceThreshold = 0.01, maxIterations = 100) {
		let bestInliers = [];
		let bestPlane = null;

		for (let i = 0; i < maxIterations; i++) {
			// Randomly sample 3 points
			const sample = [];
			while (sample.length < 3) {
				const randomIndex = Math.floor(Math.random() * points.length);
				if (!sample.includes(points[randomIndex])) {
					sample.push(points[randomIndex]);
				}
			}

			const [p1, p2, p3] = sample;

			// Calculate the plane equation ax + by + cz + d = 0
			let s1 = this.subtract(p2, p1);
			let s2 = this.subtract(p3, p1);
			const normal = this.crossProduct(this.subtract(p2, p1), this.subtract(p3, p1));
			if (this.magnitude(normal) === 0) continue; // Skip degenerate cases

			// const [a, b, c] = this.normalize(normal);
			let n = this.normalize(normal);

			// const d = -(a * p1.x + b * p1.y + c * p1.z);
			const d = -(n.x * p1.x + n.y * p1.y + n.z * p1.z);

			// Count inliers
			const inliers = [];
			for (const point of points) {
				const distance = Math.abs(n.x * point.x + n.y * point.y + n.z * point.z + d) / this.magnitude({ x: n.x, y: n.y, z: n.z });
				if (distance <= distanceThreshold) {
					inliers.push(point);
				}
			}

			// Update the best model if this one is better
			if (inliers.length > bestInliers.length) {
				bestInliers = inliers;
				// let vec3 = new THREE.Vector3(n.x,n.y,n.z,d);
				let a = n.x;
				let b = n.y;
				let c = n.z;


				bestPlane = { a, b, c, d };
			}
		}

		return { plane: bestPlane, inliers: bestInliers };
	}

	// Helper functions
	subtract(p1, p2) {
		return { x: p1.x - p2.x, y: p1.y - p2.y, z: p1.z - p2.z };
	}

	crossProduct(v1, v2) {
		return {
			x: v1.y * v2.z - v1.z * v2.y,
			y: v1.z * v2.x - v1.x * v2.z,
			z: v1.x * v2.y - v1.y * v2.x,
		};
	}

	magnitude(v) {
		return Math.sqrt(v.x ** 2 + v.y ** 2 + v.z ** 2);
	}

	normalize(v) {
		const mag = this.magnitude(v);
		let ret = new THREE.Vector3(v.x / mag, v.y / mag, v.z / mag);
		return ret;
		// return { x: v.x / mag, y: v.y / mag, z: v.z / mag };
	}

	// Example usage:
	// const pointCloud = [
	//     { x: 0, y: 0, z: 0 },
	//     { x: 1, y: 0, z: 0 },
	//     { x: 0, y: 1, z: 0 },
	//     { x: 0, y: 0, z: 1 },
	//     { x: 0.1, y: 0.1, z: 0.1 },
	//     // Add more points...
	// ];





	///////////////////
	fitPlaneRansac(points) {
		// const random = new Random(MersenneTwister19937.autoSeed());
		// new MersenneTwister19937;
		// var pcdUrl = "path/to/your/file.pcd"; // 替换为你的 PCD 文件路径
		var loader = new PCDLoader();
		var pcdUrl = "../pointclouds/pointcloud_ywy.pcd";  // 替换为你的 PCD 文件路径
		// var pcdUrl = "../pointclouds/output.pcd";  // 替换为你的 PCD 文件路径
		// const pointsPcd = new PCDLoader().parse( pcdUrl );
		// const positionAttribute = pointsPcd.geometry.getAttribute('position');

		// // 获取所有点的位置坐标
		// const pointsParse = [];
		// for (let i = 0; i < positionAttribute.count; i++) {
		// 	const x = positionAttribute.getX(i);
		// 	const y = positionAttribute.getY(i);
		// 	const z = positionAttribute.getZ(i);
		// 	pointsParse.push(new THREE.Vector3(x, y, z));
		// }
		// const processor = new PointCloudProcessor();
		// processor.compute(pointsParse, 1000, 0.05, true);
		loader.load(pcdUrl, function (points) {
			const positionAttribute = points.geometry.getAttribute('position');

			// 获取所有点的位置坐标
			const pointsParse = [];
			for (let i = 0; i < positionAttribute.count; i++) {
				const x = positionAttribute.getX(i);
				const y = positionAttribute.getY(i);
				const z = positionAttribute.getZ(i);
				pointsParse.push(new THREE.Vector3(x, y, z));
			}
			const processor = new PointCloudProcessor();
			let modelCoefficients = processor.compute(pointsParse, 1000, 0.01, true);
			console.info(
				`ok modelCoefficients: [${modelCoefficients[0]}, ${modelCoefficients[1]}, ${modelCoefficients[2]}, ${modelCoefficients[3]}]`
			);
		});
		// var pcdLoader = new PCDLoader();
		// const sacModel = new SACModel(points);
		// const ransac = new RandomSampleConsensus(0.1,3.0, 0.99, 1000, sacModel);
		// const success = ransac.computeModel();
		// if (success) {
		// 	console.log("RANSAC成功找到模型:", ransac.model_);
		// 	console.log("内点:", ransac.inliers_);
		// } else {
		// 	console.log("RANSAC没有找到模型");
		// }

		//   compute(points, output, 1000, 0.05, true);

		// 		const result = this.ransacPlane(points, 0.05, 1000);
		// console.log("Plane equation:", result.plane);
		// console.log("Inliers:", result.inliers);

		// const processor = new PointCloudProcessor();
		// processor.compute(points, 1000, 0.05, true);
	}
	generatePCD(points) {
		// 生成PCD文件的header部分
		let header = `# .PCD v0.7 - Point Cloud Data file format
	VERSION 0.7
	FIELDS x y z
	SIZE 4 4 4
	TYPE F F F
	COUNT 1 1 1
	WIDTH ${points.length}
	HEIGHT 1
	VIEWPOINT 0 0 0 1 0 0 0
	POINTS ${points.length}
	DATA ascii
	`;

		// 生成点数据部分（ASCII格式）
		let pointData = points.map(point => `${point.x} ${point.y} ${point.z}`).join('\n');

		// 合并header和点数据
		let pcdContent = header + pointData;

		// 创建Blob对象并触发下载
		let blob = new Blob([pcdContent], { type: 'application/octet-stream' });
		let link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.download = 'pointcloud.pcd';  // 保存文件的名字
		link.click();
	}
	getProfilePoints(truePosition, pointclouds) {
		let points = new Points();

		for (let [pointcloud, entry] of pointclouds) {
			for (let pointSet of entry.points) {

				let originPos = pointSet.data.position;
				let truePointPosition = new Float64Array(originPos);
				for (let i = 0; i < pointSet.numPoints; i++) {

					if (truePosition === true) {
						truePointPosition[3 * i + 0] += pointcloud.position.x;
						truePointPosition[3 * i + 1] += pointcloud.position.y;
					}

					truePointPosition[3 * i + 2] += pointcloud.position.z;
				}

				pointSet.data.position = truePointPosition;
				points.add(pointSet);
				pointSet.data.position = originPos;
			}
		}

		return points;
	}
	fitPlane(points) {
		const centroid = this.computeCentroid(points);
		let X = [];
		let Z = [];
		// 构建A矩阵 (每个点减去质心)
		// let A = points.map(point => [
		// 	// point.x - centroid.x,
		// 	// point.x - centroid.x,
		// 	// point.x - centroid.x
		// 	X.push([point.x, point.y, 1]),// [x, y, 1]
		// 	Z.push([point.z]), // [z]
		// ]);
		points.forEach(point => {
			X.push([point.x, point.y, 1]); // [x, y, 1]
			Z.push([point.z]); // [z]
		});
		let XT = math.transpose(X);
		let XTX = math.multiply(math.transpose(X), X);
		let XTZ = math.multiply(math.transpose(X), Z);
		let result = math.lusolve(XTX, XTZ);  // 求解线性方程 ATA * normal = ATb
		// const a = math.matrix([1, 4, 9, 16, 25])
		// let ps = math.transpose(A);
		// 使用最小二乘法拟合平面
		// let ATA = math.multiply(math.transpose(A), A);
		// let ATb = math.multiply(math.transpose(A), math.zeros(points.length, 1));

		// // 计算法向量 [A, B, C]
		// let result = math.lusolve(ATA, ATb);  // 求解线性方程 ATA * normal = ATb
		// const normal = result._data;
		let A = 0, B = 0, C = 0, D = 0;
		A = result[0];
		B = result[1];
		D = result[2];
		C = -1; // 假设 C 为 -1，因为我们要拟合平面 0 = Ax + By + Cz + D
		// 计算D
		// const D = -math.dot(normal, centroid);
		console.log(`Fitted Plane Equation: ${A}x + ${B}y + ${C}z + ${D} = 0`);
		let normal = new THREE.Vector3(A, B, C);
		normal.normalize();
		console.log(`Fitted Plane normal: normal.x:${normal.x} + y:${normal.y} + z:${normal.z} + mean.x:${centroid.x} ,mean.y:${centroid.y},mean.z:${centroid.z}`);
		return normal;
		// return { A, B, C, D };
	}
	fitPlaneSVD(points) {
		const mean = this.computeCentroid(points);
		// let X = [];
		// let Z = [];
		// 构建A矩阵 (每个点减去质心)
		// let A = points.map(point => [
		// 	// point.x - centroid.x,
		// 	// point.x - centroid.x,
		// 	// point.x - centroid.x
		// 	X.push([point.x, point.y, 1]),// [x, y, 1]
		// 	Z.push([point.z]), // [z]
		// ]);
		// points.forEach(point => {
		// 	X.push([point.x, point.y, 1]); // [x, y, 1]
		// 	Z.push([point.z]); // [z]
		// });
		// let mean1 = [0, 0, 0];
		// points.forEach(point => {
		// 	mean1[0] += point.x;
		// 	mean1[1] += point.y;
		// 	mean1[2] += point.z;
		// });
		// mean1 = mean1.map(value => value / points.length);


		// let mean = new THREE.Vector3(0, 0, 0);
		// points.forEach(point => {
		// 	mean.x += point.x;
		// 	mean.y += point.y;
		// 	mean.z += point.z;
		// });
		// mean = mean.map(value => value / points.length);
		let centeredPoints = points.map(point => [
			point.x - mean.x,
			point.y - mean.y,
			point.z - mean.z
		]);
		let X = centeredPoints.map(point => [point[0], point[1], point[2]]);
		// let XT = math.transpose(X);
		let covarianceMatrix = math.multiply(math.transpose(X), X);
		// const svdResult = math.eigs(covarianceMatrix);
		const svdResult = numeric.svd(covarianceMatrix);
		// const svdResult = math.svd(covarianceMatrix);
		let V = svdResult.V; // 主成分方向

		let normal = new THREE.Vector3(V[2][0], V[2][1], V[2][2]);
		console.log(`Fitted Plane SVD normal: normal.x:${normal[0]} + y:${normal[1]} + z:${normal[2]} + mean.x:${mean.x} ,mean.y:${mean.y},mean.z:${mean.z}`);
		return {
			normal: normal, // 平面的法向量
			point: mean     // 平面上的一个点
		};
		// let XTZ = math.multiply(math.transpose(X), Z);
		let result = math.lusolve(XTX, XTZ);  // 求解线性方程 ATA * normal = ATb
		// const a = math.matrix([1, 4, 9, 16, 25])
		// let ps = math.transpose(A);
		// 使用最小二乘法拟合平面
		// let ATA = math.multiply(math.transpose(A), A);
		// let ATb = math.multiply(math.transpose(A), math.zeros(points.length, 1));

		// // 计算法向量 [A, B, C]
		// let result = math.lusolve(ATA, ATb);  // 求解线性方程 ATA * normal = ATb
		// const normal = result._data;
		let A = 0, B = 0, C = 0, D = 0;
		A = result[0];
		B = result[1];
		D = result[2];
		C = -1; // 假设 C 为 -1，因为我们要拟合平面 0 = Ax + By + Cz + D
		// 计算D
		// const D = -math.dot(normal, centroid);
		console.log(`Fitted Plane Equation: ${A}x + ${B}y + ${C}z + ${D} = 0`);
		// return { normal: normal, D: D };
		return { A, B, C, D };
	}
	computePCA(points) {
		const centroid = this.computeCentroid(points);

		// 将点云数据转换为中心化数据
		const centeredPoints = points.map(point => [
			point.x - centroid.x,
			point.y - centroid.y,
			point.z - centroid.z
		]);

		// 计算协方差矩阵
		const covarianceMatrix = math.multiply(math.transpose(centeredPoints), centeredPoints);

		// 进行特征值分解
		const eig = math.eigs(covarianceMatrix);

		// 获取最小的特征值对应的特征向量（平面法向量）
		const normal = eig.vectors[math.argmin(eig.values)];

		return normal;
	}
	getPointsInBoxByCloudNode(octreeNode, globalBoundingBox, points, tightBoxMin, tightBoxMax) {
		// let points = [];
		let box = octreeNode.getBoundingBox();

		let insideFrustum = globalBoundingBox.intersectsBox(box);
		if (insideFrustum) {
			const geometry = octreeNode.geometryNode.geometry;

			var numPoints = geometry.attributes.position.count;
			let view = new Float32Array(geometry.attributes.position.array);
			for (let j = 0; j < numPoints; j++) {

				// let x, y, z;

				// x = geometry.attributes.position.array[3 * j + 0];
				// y = geometry.attributes.position.array[3 * j + 1] ;
				// z = geometry.attributes.position.array[3 * j + 2] ;
				let pos = new THREE.Vector3();
				pos.set(
					view[j * 3 + 0],
					view[j * 3 + 1],
					view[j * 3 + 2]);
				let world = octreeNode.sceneNode.matrixWorld;
				pos.applyMatrix4(world);

				var isInside = globalBoundingBox.containsPoint(pos);
				if (isInside) {
					tightBoxMin[0] = Math.min(tightBoxMin[0], pos.x);
					tightBoxMin[1] = Math.min(tightBoxMin[1], pos.y);
					tightBoxMin[2] = Math.min(tightBoxMin[2], pos.z);

					tightBoxMax[0] = Math.max(tightBoxMax[0], pos.x);
					tightBoxMax[1] = Math.max(tightBoxMax[1], pos.y);
					tightBoxMax[2] = Math.max(tightBoxMax[2], pos.z);
					points.push(pos);
				}
			}

		}
		// return points;
	}
	getPointsInBox(octreeNodes) {
		let points = [];
		let tightBoxMin = [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY];
		let tightBoxMax = [Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY];

		var minTmp = new THREE.Vector3(this.boundingBox.min.x, this.boundingBox.min.y, this.boundingBox.min.z);
		var maxTmp = new THREE.Vector3(this.boundingBox.max.x, this.boundingBox.max.y, this.boundingBox.max.z);

		var globalBoxMin = this.localToWorld(minTmp);
		var globalBoxMax = this.localToWorld(maxTmp);
		var globalBoundingBox = new THREE.Box3(
			globalBoxMin,
			globalBoxMax
		);

		if (globalBoundingBox.min.x > globalBoundingBox.max.x) {
			let temp = globalBoundingBox.min.x;
			globalBoundingBox.min.x = globalBoundingBox.max.x;
			globalBoundingBox.max.x = temp;
		}
		if (globalBoundingBox.min.y > globalBoundingBox.max.y) {
			let temp = globalBoundingBox.min.y;
			globalBoundingBox.min.y = globalBoundingBox.max.y;
			globalBoundingBox.max.y = temp;
		}
		if (globalBoundingBox.min.z > globalBoundingBox.max.z) {
			let temp = globalBoundingBox.min.z;
			globalBoundingBox.min.z = globalBoundingBox.max.z;
			globalBoundingBox.max.z = temp;
		}

		for (const octree of octreeNodes) {
			let nodes = octree.visibleNodes;

			for (let node of nodes) {
				// console.log(`#############node.pointcloud: _visible:${node.pointcloud._visible} + uuid :${node.pointcloud.uuid } + name :${node.name }`);
				// const start = performance.now();
				this.getPointsInBoxByCloudNode(node, globalBoundingBox, points, tightBoxMin, tightBoxMax);
				// const end = performance.now();
				// console.log(`代码执行时间: ${end - start} 毫秒`);

			}
		}

		if (Number.isNaN(tightBoxMax[0]) || Number.isNaN(tightBoxMax[0]) || Number.isNaN(tightBoxMax[0])) {
			console.info(
				`isNaN 1: [${tightBoxMax[0]}}]`
			);
		}
		else if (Number.isNaN(tightBoxMin[0]) || Number.isNaN(tightBoxMin[0]) || Number.isNaN(tightBoxMin[0])) {
			console.info(
				`isNaN 2: [${tightBoxMin[0]}}]`
			);
		}
		else if (Number.isNaN(tightBoxMax[0] - tightBoxMin[0]) && Number.isNaN(tightBoxMax[0] - tightBoxMin[0]) && Number.isNaN(tightBoxMax[0] - tightBoxMin[0])) {
			console.info(
				`isNaN 3: [tightBoxMax[0]:${tightBoxMax[0]}}], [tightBoxMin[0]:${tightBoxMin[0]}}]`
			);
		} else {
			if (isFinite(tightBoxMin[0]) && isFinite(tightBoxMin[1]) && isFinite(tightBoxMin[2]) &&
				isFinite(tightBoxMax[0]) && isFinite(tightBoxMax[1]) && isFinite(tightBoxMax[2])
			) {
				console.info(
					`isFinite 3: [tightBoxMin[0]:${tightBoxMin[0]}}], [tightBoxMin[1]:${tightBoxMin[1]}}],[tightBoxMin[2]:${tightBoxMin[2]}}]`
				);
				console.info(
					`isFinite 3: [tightBoxMax[0]:${tightBoxMax[0]}}], [tightBoxMax[1]:${tightBoxMax[1]}}],[tightBoxMax[2]:${tightBoxMax[2]}}]`
				);
				// let tightBoxMinGlobal = new THREE.Vector3(tightBoxMin[0], tightBoxMin[1], tightBoxMin[2]);
				// let tightBoxMaxGlobal = new THREE.Vector3( tightBoxMax[0], tightBoxMax[1], tightBoxMax[2]);
				// var localBoxMin = this.worldToLocal(tightBoxMinGlobal);
				// var localBoxMax = this.worldToLocal(tightBoxMaxGlobal);
				// var unitSize = new THREE.Vector3(localBoxMax.x-localBoxMin.x,localBoxMax.y-localBoxMin.y,localBoxMax.z-localBoxMin.z);
				// var scaleSize = new THREE.Vector3(this.scale.x,this.scale.y,this.scale.z);
				// this.setScaleSize(unitSize.x * scaleSize.x,unitSize.y * scaleSize.y,unitSize.z * scaleSize.z);	
			}

		}
		return points;
	}
	alignByPointPlane(octreeNodes, side) {
		let points = this.getPointsInBox(octreeNodes);
		const normal = this.fitPlane(points);
		var thisSideGlobalNormal = this.getFaceNormal(side);
		var normalTop = normal.clone().applyQuaternion(this.getWorldQuaternion(new THREE.Quaternion()));
		var angle = thisSideGlobalNormal.angleTo(normalTop);
			if (angle !== 0) {
				var quaternion = new THREE.Quaternion();
				quaternion.setFromUnitVectors(thisSideGlobalNormal, normal);
				this.applyQuaternion(quaternion);
				this._syncOrientationChanged();
			}
		}
		// 让盒子正面（局部 +X 面）朝向相机：一次性设置旋转，不持续跟随。
		// 用相机世界位置构造朝向：局部 +X → 盒子指向相机方向，局部 +Z（高度轴）尽量对齐世界上方向，
		// 避免绕正面轴翻滚。放置资源盒时调用，使图片/视频/PPT 等正面朝向观察者。
		faceToCamera(camera) {
			if (!camera) return;
			const boxPos = this.getWorldPosition(new THREE.Vector3());
			const camPos = camera.getWorldPosition(new THREE.Vector3());
			const xAxis = camPos.clone().sub(boxPos);
			if (xAxis.lengthSq() < 1e-12) return;   // 相机与盒子重合，无法确定朝向
			xAxis.normalize();
			// up 参考：世界 +Z（Potree 场景 Z 轴向上）。仅当朝向与之近乎平行（相机几乎
			// 正上/正下方）时换用世界 +X —— 此时 xAxis 已近 ±Z，与 +X 必然不平行。
			// 注意：原实现退化时换用 camera.up，但 camera.up 本身就是 (0,0,1)
			// （scene.js:485/487），等于拿刚被判定退化的同一向量去替换，毫无保护作用。
			let up = new THREE.Vector3(0, 0, 1);
			if (Math.abs(xAxis.dot(up)) > 0.999) up.set(1, 0, 0);
			// 正交化构造右手基：X'=xAxis(正面) / Z'≈up(盒子高度轴) / Y'=up×xAxis
			const yAxis = new THREE.Vector3().crossVectors(up, xAxis);
			// 叉乘退化时 normalize() 会因 three 的 divideScalar(length() || 1) 静默放过零向量，
			// 使基矩阵行列式为 0、产出非单位四元数（|q|=0.707），盒子会压成一条线。此处兜底。
			if (yAxis.lengthSq() < 1e-12) return;
			yAxis.normalize();
			const zAxis = new THREE.Vector3().crossVectors(xAxis, yAxis).normalize();
			const m = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
			const q = new THREE.Quaternion().setFromRotationMatrix(m);
			// 若挂在有旋转的父级下，把世界朝向换算成局部旋转
			if (this.parent) {
				const parentQ = this.parent.getWorldQuaternion(new THREE.Quaternion());
				q.premultiply(parentQ.invert());
			}
		this.quaternion.copy(q);
			// 统一收口：刷新矩阵 + 同步全景球朝向 + 派发 orientation_changed
			this._syncOrientationChanged();
		}
		// 简化版朝向相机：只做水平偏转（绕世界 +Z 轴的纯 yaw），盒子高度轴恒保持竖直、不做俯仰倾斜。
		// 相比 faceToCamera 的完整正对，这里盒子始终"竖直"，只把正面(局部+X)水平对准相机。
		faceToCameraYaw(camera) {
			if (!camera) return;
			const boxPos = this.getWorldPosition(new THREE.Vector3());
			const camPos = camera.getWorldPosition(new THREE.Vector3());
			// 场景为 Z-up（scene.js:485/487、View.js:38、Compass.js:15）：水平方位角在 X-Y
			// 平面内，竖直轴是世界 +Z。故取水平位移 (dx, dy)，忽略高度差 dz。
			const dx = camPos.x - boxPos.x;
			const dy = camPos.y - boxPos.y;
			if (dx * dx + dy * dy < 1e-12) return;   // 相机在盒子正上/正下方，无法确定水平朝向
			// 直接按方位角绕世界 +Z 构造，而不用 setFromUnitVectors((1,0,0), dir)：后者在相机
			// 绕到盒子背面(-X)时会踩 180° 退化分支，而 dot+1 ≈ dy²/(2dx²) 是二次的，1e-8 阈值
			// 对应方位角 ±0.008° 的一整条带，相机平滑绕行必然穿过，会导致盒子上下翻转。
			// 绕 +Z 旋转时局部 +Z 恒为该旋转的不动轴，高度轴结构性保持竖直。
			const q = new THREE.Quaternion().setFromAxisAngle(
				new THREE.Vector3(0, 0, 1), Math.atan2(dy, dx));
			if (this.parent) {
				const parentQ = this.parent.getWorldQuaternion(new THREE.Quaternion());
				q.premultiply(parentQ.invert());
			}
			this.quaternion.copy(q);
			// 统一收口：刷新矩阵 + 同步全景球朝向 + 派发 orientation_changed
			this._syncOrientationChanged();
		}
		alignByPointPlaneRansac(octreeNodes, side) {
			let points = this.getPointsInBox(octreeNodes);
			// this.generatePCD(points);
			// const normal = this.fitPlane(points);
		const processor = new PointCloudProcessor();
		let modelCoefficients = processor.compute(points, 1000, 0.01, true);
		let normal = new THREE.Vector3(modelCoefficients[0], modelCoefficients[1], modelCoefficients[2]);

		var thisSideGlobalNormal = this.getFaceNormal(side);
		// var normalTop = normal.clone().applyQuaternion(this.getWorldQuaternion(new THREE.Quaternion()));
		var angle = thisSideGlobalNormal.angleTo(normal);
		if (angle !== 0) {
			if (angle > Math.PI / 2) {
				// angle = Math.PI + angle;
				normal.x = -normal.x;
				normal.y = -normal.y;
				normal.z = -normal.z;
			}
			var quaternion = new THREE.Quaternion();
			quaternion.setFromUnitVectors(thisSideGlobalNormal, normal);
			this.applyQuaternion(quaternion);
			this._syncOrientationChanged();
		}
	}
	shrinkByCloudNode(octreeNode, globalBoundingBox, tightBoxMin, tightBoxMax) {
		let box = octreeNode.getBoundingBox();

		let insideFrustum = globalBoundingBox.intersectsBox(box);
		if (insideFrustum) {
			const geometry = octreeNode.geometryNode.geometry;

			var numPoints = geometry.attributes.position.count;
			for (let j = 0; j < numPoints; j++) {

				// let x, y, z;

				// x = geometry.attributes.position.array[3 * j + 0];
				// y = geometry.attributes.position.array[3 * j + 1] ;
				// z = geometry.attributes.position.array[3 * j + 2] ;
				let view = new Float32Array(geometry.attributes.position.array);
				let pos = new THREE.Vector3();
				pos.set(
					view[j * 3 + 0],
					view[j * 3 + 1],
					view[j * 3 + 2]);
				let world = octreeNode.sceneNode.matrixWorld;
				pos.applyMatrix4(world);

				var isInside = globalBoundingBox.containsPoint(pos);
				if (isInside) {
					tightBoxMin[0] = Math.min(tightBoxMin[0], pos.x);
					tightBoxMin[1] = Math.min(tightBoxMin[1], pos.y);
					tightBoxMin[2] = Math.min(tightBoxMin[2], pos.z);

					tightBoxMax[0] = Math.max(tightBoxMax[0], pos.x);
					tightBoxMax[1] = Math.max(tightBoxMax[1], pos.y);
					tightBoxMax[2] = Math.max(tightBoxMax[2], pos.z);
				}
			}

		}
	}
	shrinkByCloud(octreeNodes) {
		// this.updateMatrixWorld();

		let tightBoxMin = [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY];
		let tightBoxMax = [Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY];

		var minTmp = new THREE.Vector3(this.boundingBox.min.x, this.boundingBox.min.y, this.boundingBox.min.z);
		var maxTmp = new THREE.Vector3(this.boundingBox.max.x, this.boundingBox.max.y, this.boundingBox.max.z);
		var globalBoxMin = this.localToWorld(minTmp);
		var globalBoxMax = this.localToWorld(maxTmp);
		var globalBoundingBox = new THREE.Box3(
			globalBoxMin,
			globalBoxMax
		);
		for (const octree of octreeNodes) {
			let nodes = octree.visibleNodes;

			for (let node of nodes) {
				this.shrinkByCloudNode(node, globalBoundingBox, tightBoxMin, tightBoxMax);
			}
		}
		if (Number.isNaN(tightBoxMax[0]) || Number.isNaN(tightBoxMax[0]) || Number.isNaN(tightBoxMax[0])) {
			console.info(
				`isNaN 1: [${tightBoxMax[0]}}]`
			);
		}
		else if (Number.isNaN(tightBoxMin[0]) || Number.isNaN(tightBoxMin[0]) || Number.isNaN(tightBoxMin[0])) {
			console.info(
				`isNaN 2: [${tightBoxMin[0]}}]`
			);
		}
		else if (Number.isNaN(tightBoxMax[0] - tightBoxMin[0]) && Number.isNaN(tightBoxMax[0] - tightBoxMin[0]) && Number.isNaN(tightBoxMax[0] - tightBoxMin[0])) {
			console.info(
				`isNaN 3: [tightBoxMax[0]:${tightBoxMax[0]}}], [tightBoxMin[0]:${tightBoxMin[0]}}]`
			);

		} else {
			if (isFinite(tightBoxMin[0]) && isFinite(tightBoxMin[1]) && isFinite(tightBoxMin[2]) &&
				isFinite(tightBoxMax[0]) && isFinite(tightBoxMax[1]) && isFinite(tightBoxMax[2])
			) {
				console.info(
					`isFinite 3: [tightBoxMin[0]:${tightBoxMin[0]}}], [tightBoxMin[1]:${tightBoxMin[1]}}],[tightBoxMin[2]:${tightBoxMin[2]}}]`
				);
				console.info(
					`isFinite 3: [tightBoxMax[0]:${tightBoxMax[0]}}], [tightBoxMax[1]:${tightBoxMax[1]}}],[tightBoxMax[2]:${tightBoxMax[2]}}]`
				);
				let tightBoxMinGlobal = new THREE.Vector3(tightBoxMin[0], tightBoxMin[1], tightBoxMin[2]);
				let tightBoxMaxGlobal = new THREE.Vector3(tightBoxMax[0], tightBoxMax[1], tightBoxMax[2]);
				var localBoxMin = this.worldToLocal(tightBoxMinGlobal);
				var localBoxMax = this.worldToLocal(tightBoxMaxGlobal);
				var unitSize = new THREE.Vector3(localBoxMax.x - localBoxMin.x, localBoxMax.y - localBoxMin.y, localBoxMax.z - localBoxMin.z);
				var scaleSize = new THREE.Vector3(this.scale.x, this.scale.y, this.scale.z);
				this.setScaleSize(unitSize.x * scaleSize.x, unitSize.y * scaleSize.y, unitSize.z * scaleSize.z);
			}

		}
	}
	setlock(value) {
		this.lock = value;
	}

	isLock() {
		return this.lock;
	}

	setlockScaleX(value) {
		this.lockScaleX = value;
	}
	islockScaleX() {
		return this.lockScaleX;
	}
	setlockScaleY(value) {
		this.lockScaleY = value;
	}
	islockScaleY() {
		return this.lockScaleY;
	}
	setlockScaleZ(value) {
		this.lockScaleZ = value;
	}
	islockScaleZ() {
		return this.lockScaleZ;
	}


	setLockRotX(value) {
		this.lockRotX = value;
	}
	isLockRotX() {
		return this.lockRotX;
	}
	setLockRotY(value) {
		this.lockRotY = value;
	}
	isLockRotY() {
		return this.lockRotY;
	}
	setLockRotZ(value) {
		this.lockRotZ = value;
	}
	isLockRotZ() {
		return this.lockRotZ;
	}

	setLockTransX(value) {
		this.lockTransX = value;
	}

	isLockTransX() {
		return this.lockTransX;
	}

	setLockTransX(value) {
		this.lockTransX = value;
	}

	isLockTransX() {
		return this.lockTransX;
	}
	setLockTransY(value) {
		this.lockTransY = value;
	}

	isLockTransY() {
		return this.lockTransY;
	}

	setLockTransZ(value) {
		this.lockTransZ = value;
	}

	isLockTransZ() {
		return this.lockTransZ;
	}

	autoAttach(base, enumface) {
		var thisBack;
		var baseRight;
		var baseFront;
		var baseTop;
		var thisFront;
		var thisRight4Normal;
		var thisFront4Normal;
		var thisTop4Normal;
		if (enumface == this.Faces.FRONT) {
			thisBack = this.getBack();
			baseFront = base.getFront();
			baseRight = base.getRight();
			baseTop = base.getTop();
			thisFront = this.getFront();

			thisFront4Normal = this.getFront();
			thisRight4Normal = this.getRight();
			thisTop4Normal = this.getTop();

		} else if (enumface == this.Faces.TOP) {
			thisBack = this.getBottom();
			baseFront = base.getTop();
			baseRight = base.getRight();
			baseTop = base.getFront();
			thisFront = this.getFront();

			thisFront4Normal = this.getTop();
			thisRight4Normal = this.getRight();
			thisTop4Normal = this.getFront();
		}
		else if (enumface == this.Faces.BOTTOM) {
			thisBack = this.getTop();
			baseFront = base.getBottom();
			baseRight = base.getRight();
			baseTop = base.getFront();
			thisFront = this.getFront();

			thisFront4Normal = this.getBottom();
			thisRight4Normal = this.getRight();
			thisTop4Normal = this.getFront();
		}
		else if (enumface == this.Faces.RIGHT) {
			thisBack = this.getLeft();
			baseFront = base.getRight();
			baseRight = base.getFront();
			baseTop = base.getTop();
			thisFront = this.getFront();

			thisFront4Normal = this.getRight();
			thisRight4Normal = this.getFront();
			thisTop4Normal = this.getTop();
		}
		else if (enumface == this.Faces.LEFT) {
			thisBack = this.getRight();
			baseFront = base.getLeft();
			baseRight = base.getFront();
			baseTop = base.getTop();
			thisFront = this.getFront();

			thisFront4Normal = this.getLeft();
			thisRight4Normal = this.getFront();
			thisTop4Normal = this.getTop();

		}
		else {
			console.error('the face is unsupported');
			return;
		}
		var baseFrontGlobalNormal = base.getFaceNormal(baseFront);
		var baseRightGlobalNormal = base.getFaceNormal(baseRight);
		var baseTopGlobalNormal = base.getFaceNormal(baseTop);

		var thisFrontGlobalNormal = this.getFaceNormal(thisFront);
		var thisBackGlobalNormal = this.getFaceNormal(thisBack);
		var thisBackCenter0 = this.getFaceCenter(this, thisBack);
		var angle = thisFrontGlobalNormal.angleTo(baseFrontGlobalNormal);

		var angle2 = thisBackGlobalNormal.angleTo(baseFrontGlobalNormal);//why???

		if (angle !== 0) {
			var quaternion = new THREE.Quaternion();
			quaternion.setFromUnitVectors(thisFrontGlobalNormal, baseFrontGlobalNormal);
			this.applyQuaternion(quaternion);
			this._syncOrientationChanged();
		}

		var baseFrontCenter = base.getFaceCenter(base, baseFront);
		var thisBackCenter = this.getFaceCenter(this, thisBack);
		var centerdiff = new THREE.Vector3(baseFrontCenter.x - thisBackCenter.x, baseFrontCenter.y - thisBackCenter.y, baseFrontCenter.z - thisBackCenter.z);


		var thisFrontGlobalNormal = this.getFaceNormal(thisFront4Normal);
		var thisRightGlobalNormal = this.getFaceNormal(thisRight4Normal);
		var thisTopGlobalNormal = this.getFaceNormal(thisTop4Normal);

		const projectionVector1 = new THREE.Vector3().copy(thisFrontGlobalNormal).multiplyScalar(centerdiff.dot(thisFrontGlobalNormal));
		this.position.add(projectionVector1);
		const projectionVector2 = new THREE.Vector3().copy(thisRightGlobalNormal).multiplyScalar(centerdiff.dot(thisRightGlobalNormal));
		this.position.add(projectionVector2);
		const projectionVector3 = new THREE.Vector3().copy(thisTopGlobalNormal).multiplyScalar(centerdiff.dot(thisTopGlobalNormal));
		this.position.add(projectionVector3);

	}
	autoPaste(base, enumface) {
		// var thisFront = this.getFront();
		// var baseFront = base.getFront();
		// var baseRight = base.getRight();
		// var baseTop = base.getTop();
		var thisFront;
		var baseRight;
		var baseFront;
		var baseTop;

		if (enumface == this.Faces.FRONT) {
			thisFront = this.getFront();
			baseFront = base.getFront();
			baseRight = base.getRight();
			baseTop = base.getTop();

		} else if (enumface == this.Faces.TOP) {
			thisFront = this.getTop();
			baseFront = base.getTop();
			baseRight = base.getRight();
			baseTop = base.getFront();
		}
		else if (enumface == this.Faces.BOTTOM) {
			thisFront = this.getBottom();
			baseFront = base.getBottom();
			baseRight = base.getRight();
			baseTop = base.getFront();
		}
		else if (enumface == this.Faces.RIGHT) {
			thisFront = this.getRight();
			baseFront = base.getRight();
			baseRight = base.getFront();
			baseTop = base.getTop();
		}
		else if (enumface == this.Faces.LEFT) {
			thisFront = this.getLeft();
			baseFront = base.getLeft();
			baseRight = base.getFront();
			baseTop = base.getTop();
		}
		else {
			console.error('the face is unsupported');
			return;
		}
		var baseFrontGlobalNormal = base.getFaceNormal(baseFront);
		var baseRightGlobalNormal = base.getFaceNormal(baseRight);
		var baseTopGlobalNormal = base.getFaceNormal(baseTop);

		var baseFrontCenter = base.getFaceCenter(base, baseFront);
		var thisFrontCenter = this.getFaceCenter(this, thisFront);
		var centerdiff = new THREE.Vector3(baseFrontCenter.x - thisFrontCenter.x, baseFrontCenter.y - thisFrontCenter.y, baseFrontCenter.z - thisFrontCenter.z);

		const projectionVector1 = new THREE.Vector3().copy(baseFrontGlobalNormal).multiplyScalar(centerdiff.dot(baseFrontGlobalNormal));
		this.position.add(projectionVector1);
		const projectionVector2 = new THREE.Vector3().copy(baseRightGlobalNormal).multiplyScalar(centerdiff.dot(baseRightGlobalNormal));
		this.position.add(projectionVector2);
		const projectionVector3 = new THREE.Vector3().copy(baseTopGlobalNormal).multiplyScalar(centerdiff.dot(baseTopGlobalNormal));
		this.position.add(projectionVector3);
	}
	setBorderMaterial(parameters) {
		this.boderMaterial.setValues(parameters);
	}
	setHideCloud(hide) {
		this.hideCloud = hide;
	}
	setFront(frontIdx) {
		this.sides['front'] = frontIdx + hori[frontIdx][this.Faces.FRONT];//front
		// var parameter0={
		// 	color: 0xffffff,
		// 	transparent: true,
		// 	opacity: 0.5,
		// 	depthTest: true,
		// 	depthWrite: false};
		// this.setMaterial(this.sides['front'],parameter0);

		this.sides['back'] = frontIdx + hori[frontIdx][this.Faces.BACK];//back
		// var parameter1={
		// 	color: 0xff0000,
		// 	transparent: true,
		// 	opacity: 0.5,
		// 	depthTest: true,
		// 	depthWrite: false};
		// this.setMaterial(this.sides['back'],parameter1);

		this.sides['right'] = frontIdx + hori[frontIdx][this.Faces.RIGHT];//right
		// var parameter2={
		// 	color: 0x00ff00,
		// 	transparent: true,
		// 	opacity: 0.5,
		// 	depthTest: true,
		// 	depthWrite: false};
		// this.setMaterial(this.sides['right'],parameter2);

		this.sides['left'] = frontIdx + hori[frontIdx][this.Faces.LEFT];//left
		// var parameter3={
		// 	color: 0x0000ff,
		// 	transparent: true,
		// 	opacity: 0.5,
		// 	depthTest: true,
		// 	depthWrite: false};
		// this.setMaterial(this.sides['left'],parameter3);
	}
	setTop(topIdx) {
		// this.setMaterial(this.sides[0],this.parameterBase);
		this.sides['top'] = topIdx + vert[topIdx][this.Faces.TOP];
		// var parameter0={
		// 	color: 0xffffff,
		// 	transparent: true,
		// 	opacity: 0.5,
		// 	depthTest: true,
		// 	depthWrite: false};
		// this.setMaterial(this.sides['top'],parameter0);
		this.sides['bottom'] = topIdx + vert[topIdx][this.Faces.BOTTOM];

		// var parameter1={
		// 	color: 0xff0000,
		// 	transparent: true,
		// 	opacity: 0.5,
		// 	depthTest: true,
		// 	depthWrite: false};
		// this.setMaterial(this.sides['bottom'],parameter1);
	}
	createGeometry() {
		// let boxGeometry = new THREE.BoxGeometry(this.xSize, this.ySize, this.zSize);

		let boxGeometry = new YDBoxGeometry(this.xSize, this.ySize, this.zSize);//copy from new THREE.BoxGeometry
		boxGeometry.computeBoundingBox();
		// 重构178 核心语法错误修复 Geometry废弃 改用 BufferGeometry
		// let boxFrameGeometry = new THREE.Geometry();
		// {
		// 	let Vector3 = THREE.Vector3;

		// 	boxFrameGeometry.vertices.push(

		// 		// bottom
		// 		new Vector3(-this.xSize * 0.5, -this.ySize * 0.5, this.zSize * 0.5),
		// 		new Vector3(this.xSize * 0.5, -this.ySize * 0.5, this.zSize * 0.5),
		// 		new Vector3(this.xSize * 0.5, -this.ySize * 0.5, this.zSize * 0.5),
		// 		new Vector3(this.xSize * 0.5, -this.ySize * 0.5, -this.zSize * 0.5),
		// 		new Vector3(this.xSize * 0.5, -this.ySize * 0.5, -this.zSize * 0.5),
		// 		new Vector3(-this.xSize * 0.5, -this.ySize * 0.5, -this.zSize * 0.5),
		// 		new Vector3(-this.xSize * 0.5, -this.ySize * 0.5, -this.zSize * 0.5),
		// 		new Vector3(-this.xSize * 0.5, -this.ySize * 0.5, this.zSize * 0.5),
		// 		// top
		// 		new Vector3(-this.xSize * 0.5, this.ySize * 0.5, this.zSize * 0.5),
		// 		new Vector3(this.xSize * 0.5, this.ySize * 0.5, this.zSize * 0.5),
		// 		new Vector3(this.xSize * 0.5, this.ySize * 0.5, this.zSize * 0.5),
		// 		new Vector3(this.xSize * 0.5, this.ySize * 0.5, -this.zSize * 0.5),
		// 		new Vector3(this.xSize * 0.5, this.ySize * 0.5, -this.zSize * 0.5),
		// 		new Vector3(-this.xSize * 0.5, this.ySize * 0.5, -this.zSize * 0.5),
		// 		new Vector3(-this.xSize * 0.5, this.ySize * 0.5, -this.zSize * 0.5),
		// 		new Vector3(-this.xSize * 0.5, this.ySize * 0.5, this.zSize * 0.5),
		// 		// sides
		// 		new Vector3(-this.xSize * 0.5, -this.ySize * 0.5, this.zSize * 0.5),
		// 		new Vector3(-this.xSize * 0.5, this.ySize * 0.5, this.zSize * 0.5),
		// 		new Vector3(this.xSize * 0.5, -this.ySize * 0.5, this.zSize * 0.5),
		// 		new Vector3(this.xSize * 0.5, this.ySize * 0.5, this.zSize * 0.5),
		// 		new Vector3(this.xSize * 0.5, -this.ySize * 0.5, -this.zSize * 0.5),
		// 		new Vector3(this.xSize * 0.5, this.ySize * 0.5, -this.zSize * 0.5),
		// 		new Vector3(-this.xSize * 0.5, -this.ySize * 0.5, -this.zSize * 0.5),
		// 		new Vector3(-this.xSize * 0.5, this.ySize * 0.5, -this.zSize * 0.5),

		// 	);

		// }
		let boxFrameGeometry = new THREE.BufferGeometry();
		// LineSegments 需要「成对顶点」定义线段，直接用 Float32Array 存储
		const vertices = new Float32Array([
			// bottom
			-0.5, -0.5, 0.5, 0.5, -0.5, 0.5,
			0.5, -0.5, 0.5, 0.5, -0.5, -0.5,
			0.5, -0.5, -0.5, -0.5, -0.5, -0.5,
			-0.5, -0.5, -0.5, -0.5, -0.5, 0.5,
			// top
			-0.5, 0.5, 0.5, 0.5, 0.5, 0.5,
			0.5, 0.5, 0.5, 0.5, 0.5, -0.5,
			0.5, 0.5, -0.5, -0.5, 0.5, -0.5,
			-0.5, 0.5, -0.5, -0.5, 0.5, 0.5,
			// sides
			-0.5, -0.5, 0.5, -0.5, 0.5, 0.5,
			0.5, -0.5, 0.5, 0.5, 0.5, 0.5,
			0.5, -0.5, -0.5, 0.5, 0.5, -0.5,
			-0.5, -0.5, -0.5, -0.5, 0.5, -0.5,
		]);
		boxFrameGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

		this.remove(this.frame);
		this.frame = new THREE.LineSegments(boxFrameGeometry, this.boderMaterial);
		this.add(this.frame);


		this.remove(this.box);
		this.box = new THREE.Mesh(boxGeometry, this.materials);
		this.box.geometry.computeBoundingBox();
		this.boundingBox = this.box.geometry.boundingBox;
		this.add(this.box);
	}

	update() {
		// dispose 后 box/label 已置 null。这里是覆盖面最大的入口：基类 set clip 与
		// set modifieable 都会走到（后者无等值守卫，每次赋值都调），所以僵尸对象上
		// 一句 `volume.clip = true` 就会在下一行崩，且调用栈里看不到 dispose。
		if (this._disposed) {
			this._warnDisposed('update');
			return;
		}
		this.box.geometry.computeBoundingBox();
		this.boundingBox = this.box.geometry.boundingBox;
		this.boundingSphere = this.boundingBox.getBoundingSphere(new THREE.Sphere());

		this.label.visible = false;
	}
	setUnitSize(xSize, ySize, zSize) {
		this.xSize = xSize;
		this.ySize = ySize;
		this.zSize = zSize;
		this.createGeometry();
	}
	getUnitSize() {
		return new THREE.Vector3(this.xSize, this.ySize, this.zSize);
	}
	setScaleSize(xSize, ySize, zSize, notify = false) {
		this.scale.x = xSize;
		this.scale.y = ySize;
		this.scale.z = zSize;

		if (notify) {
			this.dispatchEvent({
				type: "scale_changed",
				object: this
			});
		}

	}
	getScaleSize() {
		return this.scale;
	}
	getSize(size) {
		size.x = this.scale.x * this.xSize;
		size.y = this.scale.y * this.ySize;
		size.z = this.scale.z * this.zSize;
	}
	setMaterial(index, parameters) {
		if (parameters === undefined || (this.res && this.res.id && index == this.getFront())) return;

		this.materials[index].setValues(parameters);
		this.materialsBackup[index].setValues(parameters);

	}
	getMaterial(index) {

		return this.materials[index];

	}
	setBaseMaterial(parameters) {
		this.parameterBase = parameters;
		for (var i = 0; i < 6; i++) {
			this.setMaterial(i, this.parameterBase);
		}

	}
	roundRect(ctx, x, y, w, h, r) {
		ctx.beginPath();
		ctx.moveTo(x + r, y);
		ctx.lineTo(x + w - r, y);
		ctx.quadraticCurveTo(x + w, y, x + w, y + r);
		ctx.lineTo(x + w, y + h - r);
		ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
		ctx.lineTo(x + r, y + h);
		ctx.quadraticCurveTo(x, y + h, x, y + h - r);
		ctx.lineTo(x, y + r);
		ctx.quadraticCurveTo(x, y, x + r, y);
		ctx.closePath();
		ctx.fill();
		ctx.stroke();
	}
	getResourceScale() {
		return this.resourceScale;
	}
	createdImgBtn() {

	}
	// setRes：统一的资源装载入口。
	// id: 1 图片 / 2 视频 / 3 PDF / 4 3D模型 / 5 PPT / 6 音频 / 7 文本 / 8 图集 / 9 网页 /
	//     10 全景视频 / 11 出生点（程序化星芒标记，无需外部资源）
	// res: 类型对应的资源。id=8 为图片 URL 数组（或 JSON 数组字符串）；id=9 为网页 URL。
	// autoPlay / interval: 仅 id=8 有效，等价于装载后调 setGalleryAutoPlay(true) 并设置间隔。
	// opacity: 资源内容透明度 [0,1]，等价于装载后调 setResOpacity()。不传则沿用当前值
	//   （与 interactive / autoPlay 同语义：属于盒子的显示属性，不因换资源而重置）。id=9 无效。
	setRes({ id, res, name, autoPlay, interval, interactive, opacity }, sizeAdaptive = false, successCallback = null) {
		// 切换资源前，释放上一次 loadResFromFile 创建的 blob URL（图片/视频/音频/图集），避免累积泄漏
		if (this._objectUrls && this._objectUrls.length > 0) {
			for (const url of this._objectUrls) {
				URL.revokeObjectURL(url)
			}
			this._objectUrls = []
		}

		// ── 按旧资源类型做专项清理（不受 clickBtns 是否存在影响：
		//    网页(9) 没有 clickBtns，PDF/图集加载失败时也可能没有控制条） ──
		const oldId = this.res && this.res.id
		if (oldId == 2) {
			if (this.video) {
				this.video.pause()
				this.video = null
			}
		} else if (oldId == 3) {
			// 释放旧 PDF 文档（pdfjs 内部 worker）与页面纹理缓存
			this.removePDF()
		} else if (oldId == 4) {
			// 3D 模型子对象（fbx/glb/gltf/obj/stl 等统一打 "yd_fbx_model" 标签）
			this.removeYdModel()
		} else if (oldId == 5) {
			// PPT 离屏渲染容器与幻灯片引用
			this.removePPT()
		} else if (oldId == 6) {
			if (this.listener) {
				this.remove(this.listener)
			}
			if (this.sound) {
				if (this.sound.isPlaying) {
					this.sound.stop()
				}
				if (this.sound.source) {
					this.sound.disconnect()
				}
				this.sound = null
			}
		} else if (oldId == 8) {
			// 图集：自动翻页计时器 + 图片纹理缓存
			this.removeGallery()
		} else if (oldId == 9) {
			// 网页：CSS3D 层里的 iframe
			this.removeWeb()
		} else if (oldId == 10) {
			// 全景视频：球体 + 视频
			this.removePanoramaVideo()
		} else if (oldId == 11) {
			// 出生点：星芒标记子对象
			this.removeSpawnMarker()
		}

		// 移除并释放全部交互按钮（视频/音频单按钮，PPT/PDF/图集胶囊控制条）
		if (this.clickBtns && this.clickBtns.length) {
			for (const btn of this.clickBtns) {
				if (btn.material) {
					if (btn.material.map) btn.material.map.dispose()
					btn.material.dispose()
				}
				if (btn.geometry) btn.geometry.dispose()
				this.remove(btn)
			}
			this.clickBtns = []
			this.clickBtn = null
		}

		this.res = {
			id,
			res,
			name // 持久化 name：占位文字缩放重绘时需要用到
		};

		// 全景视频(id=10)：记录自动播放，供 setPanoramaVideoMaterial 读取
		if (id === 10) {
			this.res.autoPlay = (autoPlay !== undefined) ? !!autoPlay : true;
		}

		// 资源透明度：必须在下面的贴图分发之前写入，两个 applier 都会读 this.resOpacity
		if (opacity !== undefined) {
			const value = Number(opacity)
			if (isFinite(value)) this.resOpacity = Math.min(1, Math.max(0, value))
		}
		// 图集自动翻页参数：在 setGalleryMaterial 之前写入，让它装载完直接起播
		if (id === 8) {
			if (autoPlay !== undefined) this.galleryAutoPlay = !!autoPlay
			if (interval > 0) this.galleryAutoPlayInterval = interval
		}
		// 网页鼠标响应开关：在 setWebMaterial 之前写入
		if (id === 9) {
			if (interactive !== undefined) this.webInteractive = !!interactive
		}

		// 如果是图片 视频等清空资源时候先释放一下材质 重置一下原始材质。
		var frontIdx = this.getFront();
		if (res === null || res === "") {
			const material = this.materials[frontIdx]
			if (material) {
				if (material.map) {
					material.map.dispose()
				}
				material.dispose()
			}
			this.materials[frontIdx] = new THREE.MeshBasicMaterial().copy(this.materialsBackup[frontIdx]);
			// 如果name也不存在。根本不知道绘制什么内容就不绘制了呗
			// （id=11 出生点例外：无需外部资源与名称，程序化星芒标记照常生成）
			if ((name === null || name === "") && id !== 11) return;
		}

		var material = this.getMaterial(frontIdx);

		// 注意：空数组 [] == "" 为 true，所以空图集会自然落到 placeholder 分支
		if (res != null && res != "" && id === 1) this.setImageMaterial(material, sizeAdaptive)
		else if (res != null && res != "" && id === 2) this.setVideoMaterial(material, sizeAdaptive)
		else if (res != null && res != "" && id === 3) this.setPDFMaterial(material, sizeAdaptive)
		else if (res != null && res != "" && id === 4) this.addModel(sizeAdaptive, successCallback)
		else if (res != null && res != "" && id === 5) this.setPPTMaterial(material, sizeAdaptive)
		else if (res != null && res != "" && id === 6) this.setAudioMaterial(material, sizeAdaptive)
		else if (res != null && res != "" && id === 7) this.setTextMaterial(material, this.res.res, sizeAdaptive)
		else if (res != null && res != "" && id === 8) this.setGalleryMaterial(material, sizeAdaptive)
		else if (res != null && res != "" && id === 9) this.setWebMaterial(material, sizeAdaptive)
		else if (res != null && res != "" && id === 10) this.setPanoramaVideoMaterial(material, sizeAdaptive)
		else if (id === 11) this.setSpawnMarkerMaterial(material, sizeAdaptive)
		else this.setPlaceholderTextMaterial(material, name)
	}
	// ============= 这一部分和业务有很大关联 ==========
	// 加载图片
	setImageMaterial(material, sizeAdaptive) {
		var textureLoader = new THREE.TextureLoader();
		textureLoader.load(this.res.res, (texture) => {

			this.setMaterialMapAndBaseInfo(material, texture)

			// 获取图片的宽高
			const width = texture.image.width;
			const height = texture.image.height;
			this.resourceScale = new THREE.Vector2(1, height / width)

			if (sizeAdaptive) {
				const showHeight = height / width * this.scale.y
				this.setScaleSize(0.01, this.scale.y, showHeight, sizeAdaptive)
			}
		});
	}
	// 加载视频
	setVideoMaterial(material, sizeAdaptive) {
		// 创建HTML5视频元素
		const video = document.createElement('video');
		video.crossOrigin = 'anonymous'; // 设置跨域属性
		video.src = this.res.res; // 替换为你的视频文件路径
		// video.autoplay = true;
		video.loop = true;
		// video.muted = true; // 静音播放
		this.video = video

		// 创建视频纹理
		const texture = new THREE.VideoTexture(video);
		this.setMaterialMapAndBaseInfo(material, texture)

		const loadedmetadata = () => {
			// 获取图片的宽高
			const width = video.videoWidth;
			const height = video.videoHeight;
			this.resourceScale = new THREE.Vector2(1, height / width)

			if (sizeAdaptive) {
				const showHeight = height / width * this.scale.y
				this.setScaleSize(0.01, this.scale.y, showHeight, sizeAdaptive)
			}
			this.createPlayerBtn({ str: "play.png" })
		}
		// 监听视频开始播放事件
		video.addEventListener('loadedmetadata', loadedmetadata);
	}
	// 创建播放/翻页按钮  size.x宽度 size.y高度
	// str: 图标文件名（icons/ 下）；text: 传入则画成加粗文字按钮（如 PPT 上一页/下一页 ‹ ›），优先于 str
	// onClick: 可选，命中该按钮点击时触发（PPT 上一页/下一页用）；不传则沿用 res.id 分支的旧逻辑（视频/PDF/音频）
	// flip: 可选，把图标在自身平面内翻转 180°
	createPlayerBtn({ str, text = null, size = { x: 0.1, y: 0.1 }, position = { x: 0.5, y: 0, z: -0.45 }, opacity = 0.7, flip = false, onClick = null }) {
		let texture;
		if (text != null) {
			// 文字按钮：canvas 绘制加粗符号 → 纹理，半透明圆底提升在任意幻灯片背景上的可读性
			const canvas = document.createElement('canvas');
			canvas.width = 128;
			canvas.height = 128;
			const ctx = canvas.getContext('2d');
			ctx.clearRect(0, 0, 128, 128);
			ctx.fillStyle = 'rgba(0,0,0,0.45)';
			ctx.beginPath();
			ctx.arc(64, 64, 60, 0, Math.PI * 2);
			ctx.fill();
			ctx.font = 'bold 96px Arial';
			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';
			ctx.fillStyle = '#ffffff';
			ctx.fillText(text, 64, 70);
			texture = new THREE.CanvasTexture(canvas);
		} else {
			// 创建图片元素
			texture = new THREE.TextureLoader().load(`${exports.resourcePath}/icons/${str}`);
		}
		// 创建材质（文字按钮带半透明圆底，用极小 alphaTest 只裁掉全透明角）
		const material = new THREE.MeshBasicMaterial({
			map: texture,
			transparent: true, // 确保材质支持透明度
			opacity: opacity,
			alphaTest: text != null ? 0.01 : 0.5, // 设置材质的 alphaTest 
		});
		// 创建几何体
		const buttonGeometry = new THREE.PlaneGeometry(1, 1);
		// 创建网格
		const btn = new THREE.Mesh(buttonGeometry, material);
		btn.name = 'ydbox_' + this.constructor.counter + '_btn_mesh';
		// this.scale 正面情况下 x 是厚度 y 是 宽度 z是 高度
		if (this.scale.y > this.scale.z) {
			const showHeight = this.scale.z * size.y
			const x = showHeight / this.scale.y
			btn.scale.set(x, size.y, 1);
		} else {
			const showWidth = this.scale.y * size.x
			const y = showWidth / this.scale.z
			btn.scale.set(size.x, y, 1);
		}
		btn.configSize = size
		btn.onClick = onClick

		const baseOpacity = opacity
		let setOpacity = (target) => {
			let o = { x: material.opacity };
			let t = new TWEEN.Tween(o).to({ x: target }, 100);
			t.onUpdate(() => {
				material.opacity = o.x;
			});
			t.start();
		};
		btn.setHighlight = (status) => {
			if (status) {
				if (!btn.highlight) {
					setOpacity(1)
					btn.highlight = true
				}
			} else {
				if (btn.highlight) {
					setOpacity(baseOpacity)
					btn.highlight = false
				}
			}
		}

		// 设置按钮的位置，使其位于立方体的一个面上
		btn.position.set(position.x, position.y, position.z);
		// 旋转按钮使其与立方体表面对齐
		btn.rotation.y = Math.PI / 2;
		btn.rotation.x = Math.PI / 2;
		// flip：绕自身法线翻转 180°，让同一张 next.png 显示为反向箭头
		if (flip) btn.rotation.z = Math.PI;
		// 将按钮网格添加到立方体上
		this.add(btn);

		if (!this.clickBtns) this.clickBtns = []
		this.clickBtns.push(btn)
		this.clickBtn = btn
		return btn
	}
	// 加载PDF
	setPDFMaterial(material, sizeAdaptive) {
		// 切换资源时可能有残留的旧文档/缓存
		this.removePDF()
		if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
			pdfjsLib.GlobalWorkerOptions.workerSrc = Potree.scriptPath + '/workers/pdf.worker.js';
		}
		// res 可以是 URL 字符串（远程加载）或 ArrayBuffer（loadResFromFile 本地文件加载）
		const src = (this.res.res instanceof ArrayBuffer || ArrayBuffer.isView(this.res.res))
			? { data: this.res.res }
			: this.res.res
		pdfjsLib.getDocument(src).promise.then(pdf => {
			// 异步期间资源可能已被切换/销毁
			if (!this.res || this.res.id !== 3) { try { pdf.destroy() } catch (e) { /* noop */ } return }
			this.page = 1
			this.pdf = pdf
			this.setPDFPageCountentMaterial(material, sizeAdaptive, true)

			// PDF 翻页控制条：与 PPT/图集同一套胶囊条（‹ 页码 ›），点击左右两段翻页
			this.createCapsuleBar({
				onPrev: () => this.pdfGoPage(-1),
				onNext: () => this.pdfGoPage(1),
				getPage: () => this.page || 1,
				getTotal: () => (this.pdf && this.pdf.numPages) || 1,
			})
		}).catch(error => {
			console.error('Error loading PDF:', error);
		});
	}
	// PDF 翻页：delta=+1 下一页 / -1 上一页，越界回绕，重渲染当前页
	// 与 pptGoPage / galleryGoPage 同构
	pdfGoPage(delta) {
		if (!this.pdf) return
		if (this._barLoading) return   // loading 中防连点：当前页渲染未完成时忽略翻页
		const total = this.pdf.numPages
		this.page += delta
		if (this.page < 1) this.page = total   // 下界回绕到末页
		if (this.page > total) this.page = 1   // 上界回绕到首页
		this.setPDFPageCountentMaterial(this.getMaterial(this.getFront()))
	}
	// 把当前页(this.page) 渲染成 CanvasTexture 贴到正面材质。
	// 页码不再画进 canvas，统一由底部胶囊控制条显示（原先此处硬编码 `1 / N`，页码永远显示 1）。
	// 已缓存的页直接应用，不触发 loading。
	setPDFPageCountentMaterial(material, sizeAdaptive, isFirst = false) {
		if (!this.pdf) return;
		const pageIndex = this.page   // 1-based

		// ── 缓存命中：直接应用，不触发 loading ──
		if (this._pdfTexCache && this._pdfTexCache[pageIndex]) {
			this._applySlideTexture(this._pdfTexCache[pageIndex], material, sizeAdaptive, isFirst)
			this.updateCapsuleBar()
			return
		}

		// 递增请求号：快速翻页时防止旧的异步渲染结果覆盖新页
		const reqId = ++this._pdfReqId
		this.showBarLoading()

		// pdf.js 渲染失败时统一销毁文档（其内部 worker 与页面缓存），避免半死状态反复报错
		const failAndDestroy = (error) => {
			console.error('[YDBoxVolume] PDF 页面渲染失败:', error)
			if (reqId === this._pdfReqId) this.hideBarLoading()
			if (this.pdf) {
				try { this.pdf.destroy() } catch (e) { /* pdfjs 已销毁则忽略 */ }
				this.pdf = null
			}
		}

		this.pdf.getPage(pageIndex).then(page => {
			if (reqId !== this._pdfReqId) { page.cleanup(); return }   // 已被更晚的翻页取代

			const viewport = page.getViewport({ scale: 2 });
			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d');
			canvas.height = viewport.height;
			canvas.width = viewport.width;

			// 渲染PDF页面到canvas
			page.render({ canvasContext: context, viewport: viewport }).promise.then(() => {
				page.cleanup();
				if (reqId !== this._pdfReqId) return   // 已有更新的翻页请求
				if (!this.pdf) return                   // 资源已被切换/销毁

				const texture = new THREE.CanvasTexture(canvas);
				texture.colorSpace = THREE.SRGBColorSpace
				texture.needsUpdate = true

				// 存入缓存，下次翻回同页直接取用
				if (!this._pdfTexCache) this._pdfTexCache = {}
				this._pdfTexCache[pageIndex] = texture

				this._applySlideTexture(texture, material, sizeAdaptive, isFirst)
				// 页码显示在底部胶囊控制条上，此处刷新控制条
				this.updateCapsuleBar()
				this.hideBarLoading()
			}).catch(failAndDestroy)
		}).catch(failAndDestroy)
	}
	// 释放 PDF 文档与页面纹理缓存（切换资源/销毁时调用）
	removePDF() {
		this._teardownBarLoading()
		this._pdfReqId++   // 让在途的异步渲染结果作废
		if (this._pdfTexCache) {
			for (const k in this._pdfTexCache) {
				if (this._pdfTexCache[k]) this._pdfTexCache[k].dispose()
			}
			this._pdfTexCache = null
		}
		if (this.pdf) {
			try { this.pdf.destroy() } catch (e) { /* pdfjs 已销毁则忽略 */ }
			this.pdf = null
		}
		this.page = null
	}
	// 加载PPT(pptx)：真·前端解析
	// res 可以是 URL 字符串（远程加载，fetch 成 ArrayBuffer）或 ArrayBuffer（loadResFromFile 本地文件加载）。
	// 用 pptx-preview 把 pptx 解析并渲染成 DOM 幻灯片（放进离屏隐藏容器），逐张用 html2canvas 截图成
	// CanvasTexture 贴到正面材质，复用 PDF 的分页交互（next.png 翻页按钮 + click 事件）。
	setPPTMaterial(material, sizeAdaptive) {
		// 清理可能残留的旧 PPT 容器
		this.removePPT()

		const res = this.res.res
		// 统一取到 ArrayBuffer：res 为 ArrayBuffer/TypedArray 时直接用；为 URL 字符串时 fetch。
		const getArrayBuffer = (res instanceof ArrayBuffer || ArrayBuffer.isView(res))
			? Promise.resolve(res)
			: fetch(res).then(r => {
				if (!r.ok) throw new Error('HTTP ' + r.status)
				return r.arrayBuffer()
			})

		Promise.all([loadPPTLibs(), getArrayBuffer]).then(([libs, arrayBuffer]) => {
			// 离屏隐藏容器：必须保留布局（不能用 display:none，否则 html2canvas 截图为空白）
			const container = document.createElement('div')
			container.style.cssText = 'position:fixed;left:-100000px;top:0;z-index:-1;pointer-events:none;'
			document.body.appendChild(container)
			this.pptContainer = container

			// 默认模式会把所有幻灯片一次性渲染成 .pptx-preview-slide-wrapper 节点
			const previewer = libs.pptxPreview.init(container, { width: 1280 })
			return previewer.preview(arrayBuffer).then(() => {
				// 异步期间资源可能已被切换/销毁
				if (this.pptContainer !== container) return
				const slides = container.querySelectorAll('.pptx-preview-slide-wrapper')
				if (!slides || slides.length === 0) {
					console.error('[YDBoxVolume] PPT 解析后未找到幻灯片')
					return
				}
this.pptSlides = Array.prototype.slice.call(slides)
			this.pptPage = 1
			this.setPPTPageMaterial(material, sizeAdaptive, true)

			// PPT 翻页控制条：底部正中一体胶囊背景（‹ 页码 ›），点击左右两段翻页
			this.createCapsuleBar({
				onPrev: () => this.pptGoPage(-1),
				onNext: () => this.pptGoPage(1),
				getPage: () => this.pptPage || 1,
				getTotal: () => (this.pptSlides && this.pptSlides.length) || 1,
			})

			// 首帧渲染完成后，在空闲时后台预热其余页（缓存 + 分摊卡顿）
			this._pptPrewarm()
		})
		}).catch(error => {
			console.error('[YDBoxVolume] PPT 加载失败:', error)
		})
	}
	// PPT 翻页：delta=+1 下一页 / -1 上一页，越界回绕，重渲染当前幻灯片
	pptGoPage(delta) {
		if (!this.pptSlides || !this.pptSlides.length) return
		if (this._barLoading) return   // loading 中防连点：当前页光栅化未完成时忽略翻页
		this.pptPage += delta
		if (this.pptPage < 1) this.pptPage = this.pptSlides.length   // 下界回绕到末页
		// 上界回绕(>总页数→1)由 setPPTPageMaterial 处理
		this.setPPTPageMaterial(this.getMaterial(this.getFront()))
	}
	// 创建底部正中「胶囊控制条」：一体的黑色 68% 透明度胶囊底，绘制 ‹ 页码 ›，
	// 点击按命中 uv 拆分：左段上一页 / 右段下一页 / 中段(页码)无动作。视觉与点击共用同一 UV，天然对齐。
	//
	// PPT(5) / PDF(3) / 图集(8) 共用本方法，差异全部通过回调注入：
	//   onPrev/onNext  点击左/右段时触发
	//   getPage        返回当前页(1-based)，用于绘制页码
	//   getTotal       返回总页数
	// 几何/位置/缩放规格三种资源完全一致，见 widthFrac / aspect / position 注释。
	createCapsuleBar({ onPrev, onNext, getPage, getTotal }) {
		this._barCfg = { onPrev, onNext, getPage, getTotal }
		// 离屏 canvas：作为胶囊控制条纹理（内边距收紧，内容更贴近胶囊边缘）
		const canvas = document.createElement('canvas')
		canvas.width = 250
		canvas.height = 60
		this.capsuleBarCanvas = canvas
		this.drawCapsuleBar()

		const texture = new THREE.CanvasTexture(canvas)
		const material = new THREE.MeshBasicMaterial({
			map: texture,
			transparent: true,
			opacity: 1,
			alphaTest: 0.01,
			// 关闭深度测试/写入 + 高 renderOrder：始终画在正面材质之上，
			// 避免与正面幻灯片纹理共面时相机移动引发的透明排序/深度抖动闪烁
			depthTest: false,
			depthWrite: false,
		})
		const bar = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material)
		bar.name = 'ydbox_' + this.constructor.counter + '_capsule_bar'
		bar.renderOrder = 999
		// 默认隐藏，仅当鼠标悬停到包围盒时显示（viewer 每帧射线检测控制）
		bar.visible = false

		const widthFrac = 0.26            // 控制条占正面宽度比例（收窄，内边距更紧凑）
		const aspect = canvas.width / canvas.height
		// 保持胶囊水平形态：高度比按父级 scale.y/scale.z 与宽高比换算，抵消父级非等比缩放
		const setBarScale = () => {
			const heightFrac = widthFrac * this.scale.y / (aspect * this.scale.z)
			bar.scale.set(widthFrac, heightFrac, 1)
		}
		setBarScale()

		bar.position.set(0.5, 0, -0.42)
		bar.rotation.y = Math.PI / 2
		bar.rotation.x = Math.PI / 2

		bar.configBar = { widthFrac, aspect, setBarScale }
		// 控制条整体不做 hover 高亮（避免闪烁），保留空实现以兼容 viewer 的 setHighlight 调用
		bar.setHighlight = () => {}
		bar.highlight = false
		// 点击按 uv.x 拆分：左 1/3 上一页，右 1/3 下一页，中段页码无动作
		bar.onClickAt = (uv) => {
			if (!uv) return
			if (uv.x < 0.34) { if (onPrev) onPrev() }
			else if (uv.x > 0.66) { if (onNext) onNext() }
		}

		this.add(bar)
		this.capsuleBar = bar
		if (!this.clickBtns) this.clickBtns = []
		this.clickBtns.push(bar)
		return bar
	}
	// 在 capsuleBarCanvas 上绘制：黑色 36% 透明度胶囊底 + 加粗白色 ‹ 当前页/总页 ›。
	// 翻页 loading 时：箭头变灰，中间页码区改画旋转 loading 圈（借鉴姊妹项目 renderPpt drawNavCanvas）
	drawCapsuleBar() {
		const canvas = this.capsuleBarCanvas
		if (!canvas) return
		const w = canvas.width
		const h = canvas.height
		const ctx = canvas.getContext('2d')
		ctx.clearRect(0, 0, w, h)

		// 胶囊圆角矩形背景（rgba(0,0,0,0.36)）
		const r = h / 2
		ctx.fillStyle = 'rgba(0,0,0,0.36)'
		ctx.beginPath()
		ctx.moveTo(r, 0)
		ctx.arcTo(w, 0, w, h, r)
		ctx.arcTo(w, h, 0, h, r)
		ctx.arcTo(0, h, 0, 0, r)
		ctx.arcTo(0, 0, w, 0, r)
		ctx.closePath()
		ctx.fill()

		ctx.textAlign = 'center'
		ctx.textBaseline = 'middle'
		const loading = !!this._barLoading
		// loading 时箭头变灰，正常时白色
		ctx.fillStyle = loading ? 'rgba(255,255,255,0.3)' : '#ffffff'
		// ‹ 上一页（左右内边距收紧，箭头更贴近胶囊两端）
		ctx.font = 'bold 44px Arial'
		ctx.fillText('‹', w * 0.14, h * 0.5 - 2)
		// › 下一页
		ctx.fillText('›', w * 0.86, h * 0.5 - 2)

		if (loading) {
			// 中间画旋转 loading 圈（8 段弧，透明度递减模拟旋转尾巴）
			const cx = w * 0.5
			const cy = h * 0.5
			const radius = h * 0.28
			const segments = 8
			const spin = this._barSpinAngle || 0
			ctx.lineCap = 'round'
			for (let s = 0; s < segments; s++) {
				const a0 = spin + (s / segments) * Math.PI * 2
				const a1 = a0 + (Math.PI * 2) / segments * 0.7
				ctx.beginPath()
				ctx.arc(cx, cy, radius, a0, a1)
				ctx.lineWidth = h * 0.1
				ctx.strokeStyle = `rgba(255,255,255,${0.2 + (s / segments) * 0.8})`
				ctx.stroke()
			}
		} else {
			// 页码（来源由 createCapsuleBar 注入的 getPage/getTotal 决定，PPT/PDF/图集通用）
			ctx.fillStyle = '#ffffff'
			ctx.font = 'bold 28px Arial'
			const cfg = this._barCfg
			const cur = (cfg && cfg.getPage && cfg.getPage()) || 1
			const total = (cfg && cfg.getTotal && cfg.getTotal()) || 1
			ctx.fillText(`${cur} / ${total}`, w * 0.5, h * 0.5)
		}
	}
	// 重绘控制条并刷新纹理（翻页后调用）
	updateCapsuleBar() {
		if (!this.capsuleBar || !this.capsuleBarCanvas) return
		this.drawCapsuleBar()
		if (this.capsuleBar.material && this.capsuleBar.material.map) {
			this.capsuleBar.material.map.needsUpdate = true
		}
	}
	// 把当前幻灯片(this.pptPage) 用 html2canvas 截图成 CanvasTexture 贴到正面材质。
	// 已缓存的页直接应用，不触发 loading，不卡顿。
	setPPTPageMaterial(material, sizeAdaptive, isFirst = false) {
		if (!this.pptSlides || this.pptSlides.length === 0) return
		if (!window.html2canvas) return
		const numSlides = this.pptSlides.length
		// 翻页越界回绕到第 1 页（与 PDF 分页一致）
		if (this.pptPage > numSlides) this.pptPage = 1
		const pageIndex = this.pptPage  // 1-based
		const slide = this.pptSlides[pageIndex - 1]
		if (!slide) return

		// ── 缓存命中：直接应用，不触发 loading，不卡顿 ──
		if (this._pptTexCache && this._pptTexCache[pageIndex]) {
			this._applySlideTexture(this._pptTexCache[pageIndex], material, sizeAdaptive, isFirst)
			this.updateCapsuleBar()
			return
		}

		// ── 首次访问该页：渲染并缓存 ──
		// 递增请求号：快速翻页时防止旧的 html2canvas 异步结果覆盖新页
		const reqId = ++this._pptReqId

		// 翻页/首帧渲染前显示 loading（html2canvas 光栅化幻灯片有明显耗时）
		this.showBarLoading()

		// html2canvas 是同步阻塞主线程的 DOM 光栅化，若立即调用，loading 帧来不及上屏，
		// 表现为"点击就卡、渲染完才闪一下 loading"。故延后两帧：先让转圈 loading 画面绘制并上屏，
		// 再执行阻塞渲染（与姊妹项目一致：点击即转圈，渲染那一刻整体卡一下）。
		const runRender = () => {
			if (reqId !== this._pptReqId) return   // 已被更晚的翻页取代
			if (!this.pptSlides) return             // 资源已被切换/销毁
			window.html2canvas(slide, { backgroundColor: '#ffffff', logging: false, useCORS: true }).then(canvas => {
				if (reqId !== this._pptReqId) return   // 已有更新的翻页请求
				if (!this.pptSlides) return             // 资源已被切换/销毁

				const texture = new THREE.CanvasTexture(canvas)
				texture.colorSpace = THREE.SRGBColorSpace
				texture.needsUpdate = true

				// 存入缓存，下次翻回同页直接取用不卡顿
				if (!this._pptTexCache) this._pptTexCache = {}
				this._pptTexCache[pageIndex] = texture

				this._applySlideTexture(texture, material, sizeAdaptive, isFirst)

				// 页码显示在底部胶囊控制条上（非绘制到幻灯片），此处刷新控制条
				this.updateCapsuleBar()

				// 仅最新请求负责关闭 loading（旧请求已在上面 return，不会误关新页的 loading）
				this.hideBarLoading()
			}).catch(error => {
				console.error('[YDBoxVolume] PPT 幻灯片渲染失败:', error)
				if (reqId === this._pptReqId) this.hideBarLoading()
			})
		}
		requestAnimationFrame(() => requestAnimationFrame(runRender))
	}
	// 应用幻灯片纹理到正面材质（不 dispose 旧 map，避免破坏缓存中的纹理）
	_applySlideTexture(texture, material, sizeAdaptive, isFirst) {
		material.map = texture
		this._applyResOpacityToMaterial(material, true)
		material.color = null
		material.colorWrite = true
		material.vertexColors = 0
		material.needsUpdate = true
		if (isFirst) {
			const canvas = texture.image
			if (canvas) {
				const width = canvas.width
				const height = canvas.height
				this.resourceScale = new THREE.Vector2(1, height / width)
				if (sizeAdaptive) {
					const showHeight = height / width * this.scale.y
					this.setScaleSize(0.01, this.scale.y, showHeight, sizeAdaptive)
				}
			}
		}
	}
	// 显示翻页 loading：在底部胶囊控制条内画旋转 loading 圈（箭头变灰），rAF 驱动，加载完即停
	// PPT/PDF/图集共用
	showBarLoading() {
		this._barLoading = true
		if (this._barSpinAngle == null) this._barSpinAngle = 0
		if (this._barLoadingRAF) cancelAnimationFrame(this._barLoadingRAF)
		const tick = () => {
			if (!this._barLoading) return
			this._barSpinAngle += 0.14   // 旋转速度
			this.updateCapsuleBar()
			this._barLoadingRAF = requestAnimationFrame(tick)
		}
		tick()
	}
	// 隐藏翻页 loading：停止动画并把胶囊恢复成页码显示
	hideBarLoading() {
		this._barLoading = false
		if (this._barLoadingRAF) {
			cancelAnimationFrame(this._barLoadingRAF)
			this._barLoadingRAF = null
		}
		this.updateCapsuleBar()
	}
	// 停掉 loading 动画并断开胶囊条配置引用（切换资源/销毁时调用）。
	// 控制条 mesh/材质由 clickBtns 统一清理块处理，这里只负责 rAF 与状态。
	// 注意不要在这里调 updateCapsuleBar（hideBarLoading 会调），否则 canvas 已断开会白跑一次绘制。
	_teardownBarLoading() {
		this._barLoading = false
		if (this._barLoadingRAF) {
			cancelAnimationFrame(this._barLoadingRAF)
			this._barLoadingRAF = null
		}
		this.capsuleBar = null
		this.capsuleBarCanvas = null
		this._barCfg = null
	}
	// 安装"用户是否在操作相机"的探针：监听全局指针按下/移动/滚轮/键盘，交互中置标志，
	// 抬起/停顿后延时清零。仅在首次预热时懒安装，避免无 PPT 时白挂监听。
	// 注意：必须监听 pointermove（拖动期间持续触发）才能持续续命，
	// 否则仅 pointerdown + 短延时会在拖动中途误判为"已停止"，导致预热恢复而卡顿。
	_pptEnsureInteractSensor() {
		if (this._pptInteractSensorOn) return
		this._pptInteractSensorOn = true
		this._pptInteracting = false
		this._pptInteractTimer = null
		// 节流标记：pointermove 高频触发，用 rAF 合并续命，避免每帧都 setTimeout
		let rafPending = false
		const mark = () => {
			this._pptInteracting = true
			if (this._pptInteractTimer) clearTimeout(this._pptInteractTimer)
			// 停止操作 400ms 后视为交互结束（长于预热重试间隔 200ms，避免抖动来回触发）
			this._pptInteractTimer = setTimeout(() => { this._pptInteracting = false }, 400)
		}
		const markMove = () => {
			if (rafPending) return
			rafPending = true
			requestAnimationFrame(() => { rafPending = false; mark() })
		}
		this._pptInteractHandlers = { mark, markMove }
		// 同时监听 pointer/mouse/touch 三类事件：Potree InputHandler 用的是 mouse 事件，
		// 仅靠 pointer 事件在某些环境不触发；多挂几类确保拖动期间能持续续命。
		window.addEventListener('pointerdown', mark, true)
		window.addEventListener('pointermove', markMove, true)
		window.addEventListener('mousedown', mark, true)
		window.addEventListener('mousemove', markMove, true)
		window.addEventListener('touchstart', mark, true)
		window.addEventListener('touchmove', markMove, true)
		window.addEventListener('wheel', mark, true)
		window.addEventListener('keydown', mark, true)
	}
	_isPptInteracting() {
		return !!this._pptInteracting
	}
	// 后台预热：以低优先级逐页光栅化未缓存页并存入缓存，分摊卡顿。
	// 关键：html2canvas 是同步阻塞主线程 ~100ms，即便在 requestIdleCallback 里触发，
	// 一旦执行就会冻结渲染/拖动。因此在"用户正在操作相机(拖动/滚轮/键盘)"期间暂停预热，
	// 仅在停止操作后才推进，避免拖动卡顿。
	_pptPrewarm() {
		if (this.pptPrewarmEnabled === false) return  // 关闭静默预热：仅点击翻页时按需光栅化当前页
		if (!this.pptSlides || this.pptSlides.length < 2) return
		this._pptEnsureInteractSensor()
		// 取消旧预热以防重复（idle 不支持时回退用的是 setTimeout，需用 clearTimeout 取消）
		if (this._pptPrewarmIC) {
			if (window.cancelIdleCallback) cancelIdleCallback(this._pptPrewarmIC)
			else clearTimeout(this._pptPrewarmIC)
			this._pptPrewarmIC = null
		}
		if (this._pptPrewarmTimer) { clearTimeout(this._pptPrewarmTimer); this._pptPrewarmTimer = null }
		const slides = this.pptSlides
		let idx = 0  // 0-based 页号
		const rasterize = () => {
			// 跳过已缓存页，以及当前正在加载的页（由 _barLoading 防连点守卫）
			while (idx < slides.length) {
				if (this._pptTexCache && this._pptTexCache[idx + 1]) { idx++; continue }
				break
			}
			if (idx >= slides.length) return  // 所有页都已缓存
			// 用户正在操作相机 -> 暂停光栅化，稍后再试（避免拖动卡顿）
			if (this._isPptInteracting()) {
				this._pptPrewarmTimer = setTimeout(rasterize, 200)
				return
			}
			if (this._barLoading) {   // 用户正在翻页加载中，暂停预热，稍后重试
				this._pptPrewarmTimer = setTimeout(rasterize, 300)
				return
			}
			const slide = slides[idx]
			const pageNum = idx + 1   // 1-based 页号，供缓存写入
			idx++
			window.html2canvas(slide, { backgroundColor: '#ffffff', logging: false, useCORS: true }).then(canvas => {
				if (!this.pptSlides) return  // 资源已切换/销毁
				const tex = new THREE.CanvasTexture(canvas)
				tex.colorSpace = THREE.SRGBColorSpace
				tex.needsUpdate = true
				if (!this._pptTexCache) this._pptTexCache = {}
				this._pptTexCache[pageNum] = tex
			}).catch(() => { /* 预热失败不影响主体功能 */ })
			// 继续预热下一页（空闲时）
			const scheduler = window.requestIdleCallback || (cb => setTimeout(cb, 50))
			this._pptPrewarmIC = scheduler(rasterize)
		}
		// 首次调度：等空闲
		const scheduler = window.requestIdleCallback || (cb => setTimeout(cb, 50))
		this._pptPrewarmIC = scheduler(rasterize)
	}
	// 移除 PPT 离屏渲染容器并断开幻灯片引用（切换资源/销毁时调用）
	removePPT() {
		// 停掉翻页 loading 动画并断开胶囊条引用（mesh/材质随 clickBtns 统一清理）
		this._teardownBarLoading()
		// 停止后台预热（idle 不支持时回退用的是 setTimeout）
		if (this._pptPrewarmIC) {
			if (window.cancelIdleCallback) cancelIdleCallback(this._pptPrewarmIC)
			else clearTimeout(this._pptPrewarmIC)
			this._pptPrewarmIC = null
		}
		if (this._pptPrewarmTimer) { clearTimeout(this._pptPrewarmTimer); this._pptPrewarmTimer = null }
		// 卸载相机交互探针
		if (this._pptInteractTimer) { clearTimeout(this._pptInteractTimer); this._pptInteractTimer = null }
		if (this._pptInteractSensorOn && this._pptInteractHandlers) {
			const { mark, markMove } = this._pptInteractHandlers
			window.removeEventListener('pointerdown', mark, true)
			window.removeEventListener('pointermove', markMove, true)
			window.removeEventListener('mousedown', mark, true)
			window.removeEventListener('mousemove', markMove, true)
			window.removeEventListener('touchstart', mark, true)
			window.removeEventListener('touchmove', markMove, true)
			window.removeEventListener('wheel', mark, true)
			window.removeEventListener('keydown', mark, true)
			this._pptInteractHandlers = null
			this._pptInteractSensorOn = false
		}
		this._pptInteracting = false
		// 释放所有已缓存的幻灯片纹理
		if (this._pptTexCache) {
			for (const k in this._pptTexCache) {
				if (this._pptTexCache[k]) this._pptTexCache[k].dispose()
			}
			this._pptTexCache = null
		}
		if (this.pptContainer) {
			if (this.pptContainer.parentNode) {
				this.pptContainer.parentNode.removeChild(this.pptContainer)
			}
			this.pptContainer = null
		}
		this.pptSlides = null
		this.pptPage = null
	}
	// ─────────────────────────────────────────────────────────────────────
	// 图集(id=8)
	// res 为图片 URL 数组（也接受 JSON 数组字符串 / 单个 URL 字符串）。
	// 交互与 PPT 一致：底部胶囊控制条手动翻页；额外支持自动翻页（默认 3s 一张）。
	// 与 PPT 不同的是这里用 THREE.TextureLoader 异步加载，不阻塞主线程，
	// 所以不需要 PPT 那套 _pptEnsureInteractSensor / requestIdleCallback 防卡顿机制，
	// 只在当前页加载完后顺手预取相邻页即可。
	// ─────────────────────────────────────────────────────────────────────
	setGalleryMaterial(material, sizeAdaptive) {
		// 切换资源时可能有残留
		this.removeGallery()

		const urls = this._normalizeGalleryUrls(this.res.res)
		if (!urls.length) {
			console.warn('[YDBoxVolume] 图集资源为空:', this.res.res)
			return
		}
		this.galleryUrls = urls
		this.galleryPage = 1
		this._galleryTexCache = {}

		this.createCapsuleBar({
			onPrev: () => this.galleryGoPage(-1),
			onNext: () => this.galleryGoPage(1),
			getPage: () => this.galleryPage || 1,
			getTotal: () => (this.galleryUrls && this.galleryUrls.length) || 1,
		})

		this._loadGalleryPage(material, sizeAdaptive, true)

		// setRes 传入 autoPlay 时立即启动（this.galleryAutoPlay / galleryAutoPlayInterval 已由 setRes 写入）
		if (this.galleryAutoPlay) this._galleryStartTimer()
	}
	// 归一化图集数据：数组 / JSON 数组字符串 / 单个 URL 字符串都收，过滤空项
	_normalizeGalleryUrls(res) {
		let arr = res
		if (typeof res === 'string') {
			const s = res.trim()
			if (s.startsWith('[')) {
				// JSON 数组字符串（便于后续持久化时以字符串形式存取）
				try { arr = JSON.parse(s) } catch (e) { arr = [s] }
			} else {
				arr = s ? [s] : []
			}
		}
		if (!Array.isArray(arr)) return []
		return arr.filter(u => typeof u === 'string' && u.length > 0)
	}
	// 图集翻页：delta=+1 下一张 / -1 上一张，越界回绕。手动翻页会重置自动翻页计时器，
	// 保证用户点击后有完整的一个间隔再自动切换。
	galleryGoPage(delta) {
		if (!this.galleryUrls || !this.galleryUrls.length) return
		if (this._barLoading) return   // loading 中防连点
		const total = this.galleryUrls.length
		this.galleryPage += delta
		if (this.galleryPage < 1) this.galleryPage = total
		if (this.galleryPage > total) this.galleryPage = 1
		this._loadGalleryPage(this.getMaterial(this.getFront()))
		// 手动翻页后重新计时（仅在自动翻页开启时）
		if (this._galleryTimer) this._galleryStartTimer()
	}
	// 跳到指定页（1-based）
	galleryGoTo(page) {
		if (!this.galleryUrls || !this.galleryUrls.length) return
		const total = this.galleryUrls.length
		const target = Math.min(Math.max(1, page | 0), total)
		if (target === this.galleryPage) return
		this.galleryGoPage(target - this.galleryPage)
	}
	// 加载并应用当前页图片。缓存命中直接应用不触发 loading。
	_loadGalleryPage(material, sizeAdaptive, isFirst = false) {
		if (!this.galleryUrls || !this.galleryUrls.length) return
		const pageIndex = this.galleryPage   // 1-based
		const url = this.galleryUrls[pageIndex - 1]
		if (!url) return

		// ── 缓存命中 ──
		if (this._galleryTexCache && this._galleryTexCache[pageIndex]) {
			this._applySlideTexture(this._galleryTexCache[pageIndex], material, sizeAdaptive, isFirst)
			this.updateCapsuleBar()
			this._prefetchGalleryNeighbors()
			return
		}

		// 递增请求号：快速翻页时防止旧的异步加载结果覆盖新页
		const reqId = ++this._galleryReqId
		this.showBarLoading()

		new THREE.TextureLoader().load(url, (texture) => {
			if (reqId !== this._galleryReqId) { texture.dispose(); return }   // 已被更晚的翻页取代
			if (!this.galleryUrls) { texture.dispose(); return }              // 资源已被切换/销毁

			texture.colorSpace = THREE.SRGBColorSpace
			texture.needsUpdate = true
			if (!this._galleryTexCache) this._galleryTexCache = {}
			this._galleryTexCache[pageIndex] = texture

			this._applySlideTexture(texture, material, sizeAdaptive, isFirst)
			this.updateCapsuleBar()
			this.hideBarLoading()
			this._prefetchGalleryNeighbors()
		}, undefined, (error) => {
			console.error('[YDBoxVolume] 图集图片加载失败:', url, error)
			if (reqId === this._galleryReqId) this.hideBarLoading()
		})
	}
	// 预取相邻页（±1，带回绕），让手动/自动翻页几乎无等待。
	// 只写缓存不触碰材质，失败静默。
	_prefetchGalleryNeighbors() {
		const urls = this.galleryUrls
		if (!urls || urls.length < 2) return
		const total = urls.length
		const cur = this.galleryPage
		const next = cur >= total ? 1 : cur + 1
		const prev = cur <= 1 ? total : cur - 1
		for (const p of (next === prev ? [next] : [next, prev])) {
			if (this._galleryTexCache && this._galleryTexCache[p]) continue
			const url = urls[p - 1]
			if (!url) continue
			new THREE.TextureLoader().load(url, (texture) => {
				// 资源已切换/销毁，或该页已被主流程抢先缓存 → 直接丢弃，避免泄漏
				if (!this.galleryUrls || !this._galleryTexCache || this._galleryTexCache[p]) {
					texture.dispose()
					return
				}
				texture.colorSpace = THREE.SRGBColorSpace
				texture.needsUpdate = true
				this._galleryTexCache[p] = texture
			}, undefined, () => { /* 预取失败不影响主体功能 */ })
		}
	}
	// 自动翻页开关（无 UI，仅 API）。间隔见 this.galleryAutoPlayInterval（默认 3000ms）。
	setGalleryAutoPlay(on) {
		this.galleryAutoPlay = !!on
		if (this.galleryAutoPlay) this._galleryStartTimer()
		else this._galleryStopTimer()
	}
	_galleryStartTimer() {
		this._galleryStopTimer()
		if (!this.galleryUrls || this.galleryUrls.length < 2) return
		const interval = this.galleryAutoPlayInterval > 0 ? this.galleryAutoPlayInterval : 3000
		this._galleryTimer = setInterval(() => {
			if (!this.galleryUrls) { this._galleryStopTimer(); return }
			// 当前页仍在加载中 → 跳过本次，等下一个 tick（不推进页号，避免跳页）
			if (this._barLoading) return
			const total = this.galleryUrls.length
			this.galleryPage = this.galleryPage >= total ? 1 : this.galleryPage + 1
			this._loadGalleryPage(this.getMaterial(this.getFront()))
		}, interval)
	}
	_galleryStopTimer() {
		if (this._galleryTimer) {
			clearInterval(this._galleryTimer)
			this._galleryTimer = null
		}
	}
	// 释放图集资源（切换资源/销毁时调用）
	removeGallery() {
		this._galleryStopTimer()
		this._teardownBarLoading()
		this._galleryReqId++   // 让在途的异步加载结果作废
		if (this._galleryTexCache) {
			for (const k in this._galleryTexCache) {
				if (this._galleryTexCache[k]) this._galleryTexCache[k].dispose()
			}
			this._galleryTexCache = null
		}
		this.galleryUrls = null
		this.galleryPage = null
	}
	// ─────────────────────────────────────────────────────────────────────
	// Web 网页(id=9)
	// res 为网页 URL。用 CSS3DRenderer 把一个真 iframe 按包围盒正面的世界位姿摆进
	// 3D 场景，鼠标可直接操作网页。
	//
	// 已知限制（原理层面，无法绕过）：
	//   1) iframe 是 DOM，走浏览器合成层，不进 WebGL 管线，没有共享深度缓冲，
	//      因此【无法被点云/高斯遮挡】——与其它资源类型（真 Mesh，在 render.pass.depth
	//      以 depthTest:true 画到默认 framebuffer）不同。
	//   2) 目标站点若设了 X-Frame-Options: DENY 或 CSP frame-ancestors（Google/百度等），
	//      会拒绝被嵌入并显示空白，这是站点侧限制。
	//   3) iframe 会吞掉覆盖区域的鼠标事件。该盒被 TransformationTool 选中时会自动把
	//      pointerEvents 置 none，以便拖动/缩放；取消选中后恢复可操作。
	//   4) 【透明度】支持 setResOpacity(0~1)，但实现方式与其它资源完全不同：其它资源改
	//      材质 opacity，网页改的是打洞层写进 canvas 的 alpha（uAlpha = 1-opacity），
	//      由浏览器合成 canvas 与 iframe 时完成「点云×(1-o) + 网页×o」。
	//      因此只能整块网页透明，做不到网页内逐元素透明。详见 HolePunchLayer 文件头。
	// ─────────────────────────────────────────────────────────────────────
	setWebMaterial(material, sizeAdaptive) {
		this.removeWeb()
		const url = this.res.res
		if (typeof url !== 'string' || !url) {
			console.warn('[YDBoxVolume] 网页资源为空:', url)
			return
		}
		this.webUrl = url
		// 正面画一张占位贴图（显示 URL），保证 WebGL 层不是空白：
		// 网页本体在 DOM 层，WebGL 层留一个可被遮挡的底衬，也让截图/缩略图不至于全空。
		this.setPlaceholderTextMaterial(material, url)

		const layer = this._getCSS3DLayer(true)
		if (!layer) {
			console.warn('[YDBoxVolume] 未找到 viewer 的 CSS3D 层，网页无法显示。请确认 volume 已加入 viewer.scene')
			return
		}
		layer.attach(this, url)
	}
	// 取得所属 viewer 的 CSS3D 层。viewer 反向引用由 VolumeTool.onAdd 在 volume_added 时注入。
	// create=false（默认）时只读现有层，不会因为清理动作反而把层建起来。
	_getCSS3DLayer(create = false) {
		const viewer = this.viewer
		if (!viewer) return null
		if (!create) return viewer._css3dLayer || null
		if (!viewer.getCSS3DLayer) return null
		return viewer.getCSS3DLayer()
	}
	// 取得所属 viewer 的打洞层（网页的逐像素遮挡与半透明混合都在这一层）。
	// 只读现有层：打洞层由 CSS3DLayer.attach 负责创建，这里不该反过来把它建起来。
	_getHolePunchLayer() {
		const viewer = this.viewer
		if (!viewer) return null
		return viewer._holePunchLayer || null
	}
	// 移除网页 iframe（切换资源/销毁时调用）
	removeWeb() {
		const layer = this._getCSS3DLayer()
		if (layer) layer.detach(this)
		this.webUrl = null
	}
	// 切换网页 iframe 是否吃鼠标事件。
	// 网页盒的交互策略：iframe 默认可交互（保持"真网页可点击"的体验），
	// 仅在 Potree 正在拖拽（相机轨道/盒子放置/gizmo 变换）期间统一让出鼠标，
	// 由 CSS3DLayer 监听 inputHandler 的 drag 状态自动切换，不在此处手动管。
	_setWebInteractive(interactive) {
		if (!this.webUrl) return
		const layer = this._getCSS3DLayer()
		if (layer) layer.setInteractive(this, interactive)
	}
	// 设置网页是否响应鼠标事件。true=可交互真网页，false=纯展示不响应。
	// 也可在 setRes({id:9, res:url, interactive:false}) 时直接传入。
	setWebInteractive(interactive) {
		this.webInteractive = !!interactive
		// CSS3DLayer.update() 每帧轮询 webInteractive 字段，无需立即手动切；
		// 但若网页盒未被选中、不在拖拽中，update 会自动按新值生效。
	}
	// ============ 全景视频 (id=10) ============
	// 等距矩形(equirectangular)视频贴到球体内部(BackSide)，相机置于球心向外看即 360° 沉浸。
	// 锚定态：球体作为子节点挂在盒子内，从外部看呈现"带视频的球"；panoEnterEnabled 开启时
	// 主动点击盒子/球体进入沉浸（滑动/拖拽不触发），关闭时仅作视频球展示，无「进入」按钮。

	// 让全景球体的"视频上"方向跟随盒子当前的世界朝向：把球局部 +Y 旋转到
	// "世界 +Z 在当前 volume 局部系中的方向"。球是盒子的子节点，若在创建时一次性修正
	// 而盒子随后被 faceToCamera/编辑旋转摆斜（俯视/仰视放置时盒子会绕水平轴倾斜），
	// 视频球的世界朝向就与盒子实际旋转不一致——视频"上"不再对齐世界 +Z。
	// 后果：沉浸内水平拖动（相机绕世界竖直轴纯偏航）时，画面沿视频经纬线斜向移动，
	// 表现为"左斜上/右斜上抬头"（左右拖都有向上分量）。此方法在进入沉浸、盒子旋转后
	// 重新同步，保证任意盒子姿态下视频上方向恒对齐世界竖直轴。
	// mesh 可显式传入：球体创建阶段 this._panoMesh 尚未赋值，缺省取字段会在下一行提前返回，
	// 导致一次性初始朝向修正从未执行（这正是"未进沉浸时从外部看球体朝向是错的"的根因）。
	_updatePanoSphereOrientation(mesh = this._panoMesh) {
		const panoMesh = mesh;
		if (!panoMesh) return;
		// getWorldQuaternion 内部会 updateWorldMatrix(true, false)，无条件重建本节点 matrix
		// 并递归刷新父链（three.core.js:14138 → :14327），故此处无需再调 updateMatrixWorld。
		const invVolQ = this.getWorldQuaternion(new THREE.Quaternion()).invert();
		const viewUpLocal = new THREE.Vector3(0, 0, 1).applyQuaternion(invVolQ);
		if (viewUpLocal.lengthSq() > 1e-6) {
			panoMesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), viewUpLocal.normalize());
		}
	}

	// 朝向变更后的统一收口：刷新世界矩阵 → 同步全景球朝向 → 派发 orientation_changed。
	// 所有直接写 this.quaternion / applyQuaternion 的方法都应调用它，避免两类静默失同步：
	// 盒子转了但全景球朝向没跟上（视频"上"偏离世界 +Z，沉浸内水平拖动画面斜向移动），
	// 以及侧栏 α/β/γ 不刷新（VolumePanel 只在 orientation_changed 时重绘）。
	// 三步顺序不可调换：updateMatrixWorld 必须最先（align* 系列在旋转后还要读世界坐标
	// 做后续平移计算），球体同步与事件监听都依赖已刷新的矩阵。
	_syncOrientationChanged() {
		this.updateMatrixWorld();
		if (this._panoMesh) this._updatePanoSphereOrientation();
		this.dispatchEvent({ type: 'orientation_changed', object: this });
	}

	setPanoramaVideoMaterial(material, sizeAdaptive) {
		// 重复设置时先清理旧的（不退出沉浸，仅替换资源）
		this.removePanoramaVideo(true);

		// 1) 视频元素（静音以便自动播放；进入沉浸后取消静音）
		const video = document.createElement('video');
		video.crossOrigin = 'anonymous';
		video.src = this.res.res;
		video.loop = true;
		video.muted = true;
		video.playsInline = true;
		this.video = video;

		// 2) 等距矩形纹理：THREE.SphereGeometry 默认 UV 即 equirect，无需自定义 shader
		const texture = new THREE.VideoTexture(video);
		if (THREE.SRGBColorSpace !== undefined) texture.colorSpace = THREE.SRGBColorSpace;
		this._panoTexture = texture;

		// 3) 球体：球是盒子的子节点，会被父级 this.scale 再乘一次。
		//    局部缩放必须按轴取父级缩放的倒数补偿，使世界半径恒 = 0.5*min(|scale|)，
		//    即盒子的内切球（与最短轴的两个盒面相切），任意非均匀盒子下世界形状仍为正球。
		const sx = Math.abs(this.scale.x) || 1;
		const sy = Math.abs(this.scale.y) || 1;
		const sz = Math.abs(this.scale.z) || 1;
		const radius = 0.5 * Math.min(sx, sy, sz);
		const sphereGeo = new THREE.SphereGeometry(1, 64, 32);
		const panoMat = new THREE.MeshBasicMaterial({
			map: texture,
			side: THREE.BackSide,
			transparent: this.resOpacity < 1,
			opacity: this.resOpacity
		});
		this._panoMat = panoMat;
		const panoMesh = new THREE.Mesh(sphereGeo, panoMat);
		panoMesh.name = 'ydbox_pano_sphere';
		panoMesh.scale.set(radius / sx, radius / sy, radius / sz);
		// 球体不单独参与 raycast（点击走 box → enter），避免误命中
		panoMesh.raycast = function () {};
		this.add(panoMesh);
		this._panoMesh = panoMesh;
		// 朝向修正：本版 SphereGeometry 的 UV 为 (u, 1-v)，即视频图像"上"对应球局部 +Y；
		// 把球局部 +Y 旋转到"世界 +Z 在 volume 局部系中的方向"，使视频上方向对齐世界竖直轴。
		// 必须在 add + _panoMesh 赋值之后调用：换算世界朝向需要父子关系已建立，
		// 且方法在 mesh 为空时提前返回（此前写在赋值之前，这次修正从未真正执行过）。
		// 盒子随后被 faceToCamera/编辑旋转摆斜时，由 _syncOrientationChanged 再次同步。
		this._updatePanoSphereOrientation(panoMesh);
		// 编辑旋转(TransformationTool 旋转手柄)后同步球体朝向；销毁时一并移除
		this._panoOrientListener = () => this._updatePanoSphereOrientation();
		this.addEventListener('orientation_changed', this._panoOrientListener);

		// 4) 沉浸入口：panoEnterEnabled 开启时"主动点击盒子/球体"进入沉浸（滑动不触发）。
		//    不在盒旁生成额外按钮；_refreshPanoEnterBtn 仅兜底清理旧版本遗留按钮。
		this._refreshPanoEnterBtn();
		//    安装手势追踪，用于在点击回调里区分"点击"与"滑动/拖拽"
		this._installPanoGestureTracking();

		// 5) 静音自动播放（进入沉浸态时再确保播放，绕过自动播放策略）
		const tryPlay = () => {
			const p = video.play();
			if (p && p.catch) p.catch(() => {});
		};
		if (this.res && this.res.autoPlay) {
			video.addEventListener('loadedmetadata', tryPlay, { once: true });
		}
		console.log('[pano] setPanoramaVideoMaterial ready, radius=' + radius.toFixed(3));
	}

	// 沉浸入口开关刷新：panoEnterEnabled 开启时"主动点击盒子/球体"进入沉浸（滑动不触发）。
	// 不在盒旁生成「进入」按钮（用户要求：仅点击盒/球本身即可）。
	// 与 setRes 解耦：可随时开关（测试页/运行时切换），无需重建资源。
	_refreshPanoEnterBtn() {
		// 兜底清理旧版本构建可能遗留的「进入」按钮（本版本不再创建）
		if (this._panoEnterBtn) {
			this.remove(this._panoEnterBtn);
			if (this.clickBtns) {
				const idx = this.clickBtns.indexOf(this._panoEnterBtn);
				if (idx >= 0) this.clickBtns.splice(idx, 1);
			}
			if (this.clickBtn === this._panoEnterBtn) this.clickBtn = null;
			this._panoEnterBtn = null;
		}
	}

	// 安装手势追踪（window 级 pointerdown/move/up/cancel）：记录本次指针手势是否发生过位移。
	// Potree InputHandler 在 mouseup 时只要命中对象==按下对象就派发 click（不检查位移），
	// 滑动结束落在球/盒上也会走到点击回调；这里用 ≥5px 位移阈值区分"点击"与"滑动/拖拽"。
	_installPanoGestureTracking() {
		if (this._panoGestureCleanup) return;
		this._panoGestureMoved = false;
		this._panoGestureStart = null;
		const onDown = (e) => {
			this._panoGestureMoved = false;
			this._panoGestureStart = { x: e.clientX, y: e.clientY };
		};
		const onMove = (e) => {
			if (!this._panoGestureStart) return;
			const dx = e.clientX - this._panoGestureStart.x;
			const dy = e.clientY - this._panoGestureStart.y;
			if (dx * dx + dy * dy >= 25) this._panoGestureMoved = true; // 位移≥5px 视为滑动
		};
		const onEnd = () => { this._panoGestureStart = null; };
		window.addEventListener('pointerdown', onDown, { passive: true });
		window.addEventListener('pointermove', onMove, { passive: true });
		window.addEventListener('pointerup', onEnd, { passive: true });
		window.addEventListener('pointercancel', onEnd, { passive: true });
		this._panoGestureCleanup = () => {
			window.removeEventListener('pointerdown', onDown);
			window.removeEventListener('pointermove', onMove);
			window.removeEventListener('pointerup', onEnd);
			window.removeEventListener('pointercancel', onEnd);
			this._panoGestureCleanup = null;
		};
	}
	_uninstallPanoGestureTracking() {
		if (this._panoGestureCleanup) this._panoGestureCleanup();
	}

	// 公开开关：控制全景视频是否允许点击进入沉浸式。返回当前值。
	setPanoEnterEnabled(v) {
		this.panoEnterEnabled = !!v;
		this._refreshPanoEnterBtn();
		return this.panoEnterEnabled;
	}

	// 移除全景视频球体与视频元素（切换资源/销毁时调用）。
	// skipExit=true 时即使处于沉浸态也不退出（用于重设资源，避免递归）。
	removePanoramaVideo(skipExit) {
		if (this._panoImmersive && !skipExit) {
			this.exitPanorama();
		}
		// 卸载手势追踪监听（点击/滑动过滤），复位状态
		this._uninstallPanoGestureTracking();
		this._panoGestureMoved = false;
		// 一并清理「进入」按钮（与球体同源，避免重设资源时残留旧按钮）
		if (this._panoEnterBtn) {
			this.remove(this._panoEnterBtn);
			if (this.clickBtns) {
				const idx = this.clickBtns.indexOf(this._panoEnterBtn);
				if (idx >= 0) this.clickBtns.splice(idx, 1);
			}
			if (this.clickBtn === this._panoEnterBtn) this.clickBtn = null;
			this._panoEnterBtn = null;
		}
		if (this._panoMesh) {
			this.remove(this._panoMesh);
			if (this._panoMesh.geometry) this._panoMesh.geometry.dispose();
			this._panoMesh = null;
		}
		// 移除编辑旋转后的朝向同步监听
		if (this._panoOrientListener) {
			this.removeEventListener('orientation_changed', this._panoOrientListener);
			this._panoOrientListener = null;
		}
		if (this._panoMat) {
			if (this._panoMat.map) this._panoMat.map.dispose();
			this._panoMat.dispose();
			this._panoMat = null;
		}
		this._panoTexture = null;
		if (this.video) {
			this.video.pause();
			this.video.src = '';
			this.video = null;
		}
	}

	// 进入沉浸式全景：相机移到球心，拖拽环视，隐藏点云与其他体积。ESC 或「退出」按钮退出。
	enterPanorama() {
		if (this._panoImmersive) return;
		const viewer = this.viewer;
		if (!viewer) { console.warn('[pano] 未找到 viewer，无法进入沉浸'); return; }
		const camera = viewer.scene.getActiveCamera();
		if (!camera) return;

		// 隐藏交互按钮（播放/翻页等），避免沉浸时漂浮在球内；退出时按进入前可见性恢复
		if (this.clickBtns && this.clickBtns.length) {
			this._panoBtnVisible = this.clickBtns.map(b => b.visible);
			this.clickBtns.forEach(b => b.visible = false);
		}

		// 保存现场：相机参数 + scene.view(位置/朝向/半径) + 可见性。
		// Potree 相机每帧由 scene.view 派生，退出时须连同 scene.view 一起恢复，
		// 否则下一帧同步会把相机放回沉浸位置（表现为退出后画面无变化）。
		const view = viewer.scene.view;
		const volMap = new Map();
		(viewer.scene.volumes || []).forEach(v => { if (v !== this) volMap.set(v, v.visible); });
		this._panoSaved = {
			position: camera.position.clone(),
			quaternion: camera.quaternion.clone(),
			up: camera.up.clone(),
			near: camera.near,
			far: camera.far,
			view: {
				position: view.position.clone(),
				yaw: view.yaw,
				pitch: view.pitch,
				radius: view.radius
			},
			controlsEnabled: viewer.controls ? viewer.controls.enabled : true,
			pcVisible: (viewer.scene.pointclouds || []).map(pc => pc.visible),
			volMap
		};

		// 球心（世界坐标）作为相机位置；记录世界朝向用于环视基准
		const center = new THREE.Vector3();
		this.getWorldPosition(center);
		this._panoCenter = center.clone();
		this._panoWorldQuat = this.getWorldQuaternion(new THREE.Quaternion());
		// 关键：以进入时刻的世界朝向重算球体视频朝向。球体创建时盒子尚未被
		// faceToCamera/编辑旋转摆斜，创建时做的朝向修正已失效；若不在此同步，
		// 视频"上"偏离世界 +Z，水平拖动（绕世界竖直轴纯偏航）时画面沿视频
		// 经纬线斜向移动——即用户报告的"左斜上/右斜上抬头"现象。
		this._updatePanoSphereOrientation();
		// 初始朝向 = 视频正面中心（球局部 +X 的世界方向）。球体朝向已按当前
		// 世界朝向修正，视频正面中心一般不再等于盒子局部 +X 方向（盒子被
		// faceToCamera 摆斜时二者有夹角）；指向视频正面中心，进入时看到的
		// 仍是修正前的同一画面（视频中心内容）。换算为 Potree 世界 yaw/pitch
		// 后：水平拖动=绕世界竖直轴纯偏航（不随盒子倾斜，无"旋转/斜上"感），
		// 垂直拖动=绕视线水平轴俯仰。
		const baseForward = new THREE.Vector3(1, 0, 0);
		if (this._panoMesh) baseForward.applyQuaternion(this._panoMesh.quaternion);
		baseForward.applyQuaternion(this._panoWorldQuat);
		this._panoBaseYaw = Math.atan2(baseForward.y, baseForward.x) - Math.PI / 2;
		this._panoYaw = (this.res && this.res.initYaw) ? Number(this.res.initYaw) : 0;
		this._panoPitch = Math.atan2(baseForward.z, Math.sqrt(baseForward.x * baseForward.x + baseForward.y * baseForward.y));

		// 接管相机：Potree 相机每帧由 scene.view 派生（OrbitControls.update +
		// viewer.update 同步），所以这里驱动 scene.view：position=球心、
		// yaw/pitch=环视朝向，由原生同步把相机放到球心并朝正确方向。
		// 先禁用轨道控制再接管相机：即使接管逻辑抛异常，OrbitControls 也不会
		// 在沉浸期活跃（其 update 每帧用 getPivot() 改写 view，会让相机漂移/斜上）。
		if (viewer.controls) viewer.controls.enabled = false;

		// 包围盒控制器（TransformationTool gizmo：选中黄色边框+缩放/旋转/平移手柄）不响应。
		// 进沉浸的那次点击（穿过视频球）已被 InputHandler 的选中守卫拦下（isPanoSpherePick），
		// 但盒子仍可能在这之前就被选中过（球外命中盒子的点击会正常选中，见 isPanoBoxOuterPick；
		// 或在开启 panoEnterEnabled 之前就已选中），故这里仍需清空 gizmo 选中：
		//   1) update() 每帧按 selection.length===1 决定 gizmo 显隐 → 清空后 scene.visible=false；
		//   2) scene.visible=false 后 InputHandler.getHoveredElements 的 traverseVisible 不再
		//      收集手柄节点 → 拖动/点击/悬停全部不响应。
		// 不再暂存原选中态：退出沉浸时统一清空选中（见 exitPanorama），否则退出后 gizmo 会弹出来。
		if (viewer.transformationTool) {
			viewer.transformationTool.selection = [];
			viewer.transformationTool.scene.visible = false; // 立即隐藏，避免下一帧 update 前闪现
		}

		this._updatePanoCamera();

		// 沉浸期点云隐藏 → updateVisibility 直接跳过 → lowestSpacing=Infinity →
		// viewer.update 不重算 near/far，这里设置的近远裁剪可稳定生效。
		const rWorld = this._panoWorldRadius();
		camera.near = Math.max(0.001, rWorld * 0.5);
		camera.far = Math.max(camera.far, rWorld * 10, 100);
		camera.updateProjectionMatrix();

		// 隐藏点云与其他体积
		(viewer.scene.pointclouds || []).forEach(pc => pc.visible = false);
		(viewer.scene.volumes || []).forEach(v => { if (v !== this) v.visible = false; });

		// 监听：ESC 退出 + 拖拽环视
		this._panoOnKey = (e) => { if (e.key === 'Escape') this.exitPanorama(); };
		window.addEventListener('keydown', this._panoOnKey);

		const dom = viewer.renderer.domElement;
		this._panoOnDown = (e) => {
			this._panoDragging = true;
			this._panoLastX = e.clientX; this._panoLastY = e.clientY;
			this._panoStartX = e.clientX; this._panoStartY = e.clientY; // 手势起点：方向主导判定基准
		};
		this._panoOnUp = () => { this._panoDragging = false; };
		this._panoOnMove = (e) => {
			if (!this._panoDragging) return;
			const dx = e.clientX - this._panoLastX;
			const dy = e.clientY - this._panoLastY;
			this._panoLastX = e.clientX; this._panoLastY = e.clientY;
			// 方向主导：以手势起点到当前点的累计位移判定主导方向——
			// 水平主导(|Σdx|≥|Σdy|)只转 yaw、丢弃 dy；垂直主导才俯仰、丢弃 dx。
			// 消除"水平拖动斜上移动"：用户水平拖动时手绘弧线的 dy 不再进 pitch，
			// 水平拖动=严格绕世界竖直轴纯水平环视（实测 pitch=0 时 fwd 恒水平、无滚转）。
			const tx = e.clientX - this._panoStartX;
			const ty = e.clientY - this._panoStartY;
			if (Math.abs(tx) >= Math.abs(ty)) {
				this._panoYaw -= dx * 0.005;
			} else {
				this._panoPitch -= dy * 0.005;
				const lim = Math.PI / 2 - 0.01;
				this._panoPitch = Math.max(-lim, Math.min(lim, this._panoPitch));
			}
			this._updatePanoCamera();
		};
		dom.addEventListener('pointerdown', this._panoOnDown);
		window.addEventListener('pointerup', this._panoOnUp);
		window.addEventListener('pointermove', this._panoOnMove);

		// 进入沉浸态后确保视频播放（绕过自动播放限制）
		if (this.video) {
			this.video.muted = false;
			const p = this.video.play();
			if (p && p.catch) p.catch(() => {});
		}

		this._panoImmersive = true;
		this._showPanoExitUI();
		console.log('[pano] 进入沉浸式（拖拽环视，ESC 退出）');
	}

	// 当前全景球的世界半径（各轴相同，由局部缩放 × 父级缩放得出）。
	_panoWorldRadius() {
		if (this._panoMesh && this.scale) {
			return this._panoMesh.scale.x * Math.abs(this.scale.x || 1);
		}
		return 1;
	}

	// 世界空间下的全景球：球心 = 盒子世界原点（球以默认 position 挂在 volume 下），
	// 半径 = 内切球半径（与最短轴的两个盒面相切）。
	_panoWorldSphere() {
		const center = new THREE.Vector3().setFromMatrixPosition(this.matrixWorld);
		return new THREE.Sphere(center, this._panoWorldRadius());
	}

	// 全景「观看态」：id=10 且已开启点击进入沉浸。此态下点击落点决定两种行为：
	//   命中内切球     → 进入沉浸（这次点击不选中盒子，否则退出沉浸后会残留 gizmo）；
	//   未命中球但命中盒 → 走普通选中逻辑，弹出 transform gizmo 供编辑。
	isPanoViewingMode() {
		return !!(this.res && this.res.id === 10 && this.panoEnterEnabled && this._panoMesh);
	}

	// 点击射线是否命中全景球（= 是否该进入沉浸）。
	// 全景球的 raycast 被禁用（点击统一走 box，见 setPanoramaVideoMaterial），所以「有没有点到球」
	// 只能在点击时刻补算：盒子非立方体、甚至立方体的面角处都存在球轮廓之外的可点区域，
	// 那些位置命中的是盒子而非球，应当选中盒子而不是进沉浸。
	isPanoSphereHit(ray) {
		if (!ray || !this.isPanoViewingMode()) return false;
		return ray.intersectsSphere(this._panoWorldSphere());
	}

	// 按 (yaw,pitch) 驱动 Potree 原生 scene.view：
	//   view.yaw   偏航 = 绕世界竖直轴（Potree 相机 rotation.z=yaw，天然无滚转）
	//   view.pitch 俯仰 = 绕视线水平轴
	// Potree 相机每帧由 scene.view 派生（viewer.update 同步 rotation.x=PI/2+pitch, rotation.z=yaw），
	// 故这里只需设 position + yaw/pitch，下一帧相机自动到位。
	// 旧实现绕"盒子局部 +Z"偏航：盒子被摆斜（如 faceToCamera 俯视/仰视放置）时绕倾斜轴旋转，
	// 水平拖动呈"画面旋转"感；改绕世界竖直轴后即为标准水平环视（视频"上"恒对齐世界 +Z，与球体朝向修正一致）。
	_updatePanoCamera() {
		const viewer = this.viewer;
		if (!viewer || !this._panoCenter) return;
		const view = viewer.scene.view;
		if (!view) return;
		view.position.copy(this._panoCenter);
		view.yaw = this._panoBaseYaw + this._panoYaw;
		view.pitch = this._panoPitch;
	}

	// 退出沉浸式：恢复相机、scene.view、可见性与轨道控制。
	exitPanorama() {
		if (!this._panoImmersive) return;
		const viewer = this.viewer;
		const camera = viewer && viewer.scene.getActiveCamera();

		// 移除监听
		if (this._panoOnKey) window.removeEventListener('keydown', this._panoOnKey);
		if (viewer && viewer.renderer && this._panoOnDown) {
			viewer.renderer.domElement.removeEventListener('pointerdown', this._panoOnDown);
		}
		if (this._panoOnUp) window.removeEventListener('pointerup', this._panoOnUp);
		if (this._panoOnMove) window.removeEventListener('pointermove', this._panoOnMove);
		this._panoOnKey = this._panoOnDown = this._panoOnUp = this._panoOnMove = null;
		this._panoDragging = false;

		if (viewer) {
			// 退出后不恢复包围盒控制器（TransformationTool gizmo）：沉浸退出即弹出 gizmo 不符合预期。
			// 用 deselectAll 统一清空，让 inputHandler.selection 与 transformationTool.selection
			// 保持同步（后者由 selection_changed 驱动）；没有 inputHandler 时退化为直接清空 gizmo。
			if (viewer.inputHandler) {
				viewer.inputHandler.deselectAll();
			} else if (viewer.transformationTool) {
				viewer.transformationTool.selection = [];
				viewer.transformationTool.scene.visible = false;
			}
			// 恢复点云可见性
			if (this._panoSaved) {
				(viewer.scene.pointclouds || []).forEach((pc, i) => {
					if (this._panoSaved.pcVisible[i] !== undefined) pc.visible = this._panoSaved.pcVisible[i];
				});
				// 恢复其他体积可见性
				this._panoSaved.volMap.forEach((vis, v) => { v.visible = vis; });
				// 恢复交互按钮（播放/翻页等）显隐
				if (this.clickBtns && this._panoBtnVisible) {
					this.clickBtns.forEach((b, i) => { if (this._panoBtnVisible[i] !== undefined) b.visible = this._panoBtnVisible[i]; });
				}
				// 恢复 scene.view(位置/朝向/半径)：下一帧 viewer.update 据此把相机放回原位
				const view = viewer.scene.view;
				if (view && this._panoSaved.view) {
					view.position.copy(this._panoSaved.view.position);
					view.yaw = this._panoSaved.view.yaw;
					view.pitch = this._panoSaved.view.pitch;
					view.radius = this._panoSaved.view.radius;
				}
			}
			// 恢复相机与轨道控制
			if (camera && this._panoSaved) {
				camera.position.copy(this._panoSaved.position);
				camera.quaternion.copy(this._panoSaved.quaternion);
				camera.up.copy(this._panoSaved.up);
				if (this._panoSaved.near !== undefined) camera.near = this._panoSaved.near;
				if (this._panoSaved.far !== undefined) camera.far = this._panoSaved.far;
				camera.updateProjectionMatrix();
				camera.updateMatrixWorld(true);
				if (viewer.controls) {
					viewer.controls.enabled = this._panoSaved.controlsEnabled;
				}
			}
		}
		this._panoBtnVisible = null;
		this._panoSaved = null;
		this._panoImmersive = false;
		this._hidePanoExitUI();
		console.log('[pano] 退出沉浸式');
	}

	// 沉浸式期间在屏幕顶部显示「退出全景 (ESC)」按钮。
	_showPanoExitUI() {
		this._hidePanoExitUI();
		const el = document.createElement('div');
		el.id = 'ydbox_pano_exit_ui';
		el.textContent = '退出全景 (ESC)';
		el.style.cssText = 'position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:99998;'
			+ 'padding:8px 18px;background:rgba(0,0,0,0.6);color:#fff;font:14px/1 system-ui,sans-serif;'
			+ 'border-radius:6px;cursor:pointer;user-select:none;';
		el.addEventListener('click', () => this.exitPanorama());
		document.body.appendChild(el);
		this._panoExitEl = el;
	}
	_hidePanoExitUI() {
		if (this._panoExitEl && this._panoExitEl.parentNode) {
			this._panoExitEl.parentNode.removeChild(this._panoExitEl);
		}
		this._panoExitEl = null;
	}
	// 加载FBX模型
	addFBXModel(sizeAdaptive) {
		var loader = new FBXLoader();
		loader.load(this.res.res, (object,) => {
			object.name = "yd_fbx_model"
			// 获取包围盒并且设置 缩放比使其兼容父级的scale为长宽高的情况
			const box = new THREE.Box3().setFromObject(object);
			const size = new THREE.Vector3();
			const center1 = new THREE.Vector3();
			box.getCenter(center1);
			box.getSize(size);
			// 保存原始中心点
			object.originalCenter = center1.clone();
			object.originalScale = object.scale.clone();

			const real_x = size.x / object.scale.x
			const real_y = size.y / object.scale.y
			const real_z = size.z / object.scale.z

			object.scale.set(1 / real_x, 1 / real_y, 1 / real_z);

			// 重新获取一下包围盒获取中心点
			const box2 = new THREE.Box3().setFromObject(object);
			const center = new THREE.Vector3();
			box2.getCenter(center);

			// 将模型平移到其几何中心
			object.position.sub(center);

			this.add(object);
			this.updateMatrixWorld();

			if (sizeAdaptive) {
				// 以x为媒介计算出 y z
				const y = real_y / real_x * this.scale.x
				const z = real_z / real_x * this.scale.x
				this.setScaleSize(this.scale.x, y, z, sizeAdaptive)
			}
			// 设置长宽高比
			this.resourceScale = new THREE.Vector3(1, real_y / real_x, real_z / real_x)
		});
	}
	// 移除并释放挂载在 box 上的 3D 模型子对象（fbx/glb/gltf/obj/stl 统一打 "yd_fbx_model" 标签）
	// 递归 dispose geometry/material/texture，避免切换/清空资源时旧模型残留造成显存泄漏
	removeYdModel() {
		const toRemove = []
		for (let i = 0; i < this.children.length; i++) {
			const child = this.children[i];
			if (child.name == "yd_fbx_model") {
				toRemove.push(child)
			}
		}
		for (const child of toRemove) {
			child.traverse((node) => {
				if (node.geometry) {
					node.geometry.dispose()
				}
				if (node.material) {
					const materials = Array.isArray(node.material) ? node.material : [node.material]
					for (const material of materials) {
						for (const key in material) {
							const value = material[key]
							if (value && value.isTexture) {
								value.dispose()
							}
						}
						material.dispose()
					}
				}
			})
			this.remove(child)
		}
	}
	// ==================== 出生点(id=11)星芒标记 ====================
	// 星芒贴图（静态共享缓存）：glow=中心径向光晕；rays=8 条芒线（4 长 + 4 短）。
	// 只生成一次、全实例共用；removeSpawnMarker 只 dispose 几何体/材质，不 dispose 贴图。
	static _getSpawnTexture(kind) {
		const key = '_spawnTex_' + kind;
		if (YDBoxVolume[key]) return YDBoxVolume[key];

		const size = 512;
		const canvas = document.createElement('canvas');
		canvas.width = size;
		canvas.height = size;
		const ctx = canvas.getContext('2d');
		const c = size / 2;

		if (kind === 'glow') {
			// 径向光晕：白核 → 青白 → 半透明青 → 全透明
			const g = ctx.createRadialGradient(c, c, 0, c, c, c);
			g.addColorStop(0, 'rgba(255,255,255,1)');
			g.addColorStop(0.25, 'rgba(210,250,255,0.9)');
			g.addColorStop(0.55, 'rgba(90,220,255,0.28)');
			g.addColorStop(1, 'rgba(90,220,255,0)');
			ctx.fillStyle = g;
			ctx.fillRect(0, 0, size, size);
		} else {
			// 8 条芒线：4 长（十字向）+ 4 短（对角向），两端尖、根部略宽的渐变四边形，
			// 用 lighter 叠加让芒线与光晕重叠处自然增亮
			ctx.globalCompositeOperation = 'lighter';
			const spikes = [];
			for (let i = 0; i < 4; i++) {
				spikes.push({ angle: i * Math.PI / 2, len: c * 0.98, w: c * 0.045 });
			}
			for (let i = 0; i < 4; i++) {
				spikes.push({ angle: Math.PI / 4 + i * Math.PI / 2, len: c * 0.55, w: c * 0.03 });
			}
			for (const s of spikes) {
				const dx = Math.cos(s.angle);
				const dy = Math.sin(s.angle);
				const px = -dy;
				const py = dx;
				const g = ctx.createLinearGradient(c, c, c + dx * s.len, c + dy * s.len);
				g.addColorStop(0, 'rgba(255,255,255,0.95)');
				g.addColorStop(0.35, 'rgba(140,235,255,0.55)');
				g.addColorStop(1, 'rgba(90,220,255,0)');
				ctx.fillStyle = g;
				ctx.beginPath();
				ctx.moveTo(c + px * s.w, c + py * s.w);
				ctx.lineTo(c + dx * s.len + px * s.w * 0.12, c + dy * s.len + py * s.w * 0.12);
				ctx.lineTo(c + dx * s.len - px * s.w * 0.12, c + dy * s.len - py * s.w * 0.12);
				ctx.lineTo(c - px * s.w, c - py * s.w);
				ctx.closePath();
				ctx.fill();
			}
		}

		const tex = new THREE.CanvasTexture(canvas);
		if (THREE.SRGBColorSpace !== undefined) {
			tex.colorSpace = THREE.SRGBColorSpace;
		}
		YDBoxVolume[key] = tex;
		return tex;
	}

	// 出生点(id=11)：装入星芒标记。三层相机朝向面片：中心光晕 + 两层反向自转芒线，
	// 加法混合不写深度。material/sizeAdaptive 仅与其它资源类型签名对齐（程序化生成）。
	setSpawnMarkerMaterial(material, sizeAdaptive) {
		if (this._disposed) return this._warnDisposed('setSpawnMarkerMaterial');
		this.removeSpawnMarker();

		const group = new THREE.Group();
		group.name = 'ydbox_spawn_marker';
		group.raycast = function () {};
		const makeQuad = (texture) => {
			const quad = new THREE.Mesh(
				new THREE.PlaneGeometry(1, 1),
				new THREE.MeshBasicMaterial({
					map: texture,
					transparent: true,
					opacity: this.resOpacity,
					depthWrite: false,
					blending: THREE.AdditiveBlending,
					side: THREE.DoubleSide,
				})
			);
			quad.raycast = function () {};
			return quad;
		};
		this._spawnCore = makeQuad(YDBoxVolume._getSpawnTexture('glow'));
		this._spawnRayA = makeQuad(YDBoxVolume._getSpawnTexture('rays'));
		this._spawnRayB = makeQuad(YDBoxVolume._getSpawnTexture('rays'));
		group.add(this._spawnCore, this._spawnRayA, this._spawnRayB);
		this._spawnMarker = group;
		this.add(group);
	}

	// 出生点(id=11)逐帧动画：星芒面向相机（billboard）+ 两层芒线反向自转 + 呼吸脉冲。
	// 由 VolumeTool.update 每帧调用。尺寸取盒子最小世界边等比例，并按世界缩放逐轴
	// 反除补偿父级缩放，缩放盒子时世界尺寸保持稳定。透明度 = resOpacity × 脉冲。
	_updateSpawnMarker(camera) {
		const group = this._spawnMarker;
		if (!group || this._disposed) {
			return;
		}
		const t = performance.now() / 1000;

		this.updateMatrixWorld();
		const tmpPos = new THREE.Vector3();
		const parentQuat = new THREE.Quaternion();
		const worldScale = new THREE.Vector3();
		this.matrixWorld.decompose(tmpPos, parentQuat, worldScale);
		const sx = Math.max(Math.abs(worldScale.x), 1e-6);
		const sy = Math.max(Math.abs(worldScale.y), 1e-6);
		const minDim = Math.max(Math.min(sx, sy, Math.abs(worldScale.z)), 1e-6);

		// 面向相机：局部旋转 = 父级(盒子)世界旋转的逆 × 相机世界旋转；芒线层再绕
		// 面向轴叠加自转。decompose 而非 setFromRotationMatrix，后者不剔除缩放。
		const base = parentQuat.invert().multiply(camera.getWorldQuaternion(new THREE.Quaternion()));
		const spinAxis = new THREE.Vector3(0, 0, 1);
		const apply = (quad, sizeWorld, spinAngle, opacityPulse) => {
			quad.quaternion.copy(base);
			if (spinAngle) {
				quad.quaternion.multiply(new THREE.Quaternion().setFromAxisAngle(spinAxis, spinAngle));
			}
			quad.scale.set(sizeWorld / sx, sizeWorld / sy, 1);
			quad.material.opacity = Math.min(1, this.resOpacity * opacityPulse);
		};

		apply(this._spawnCore, minDim * 0.4 * (1 + 0.1 * Math.sin(t * 2.4)), 0, 0.9 + 0.1 * Math.sin(t * 2.4));
		apply(this._spawnRayA, minDim * 0.92, t * 0.5, 0.75 + 0.25 * Math.sin(t * 1.8));
		apply(this._spawnRayB, minDim * 0.62, -t * 0.34, 0.6 + 0.3 * Math.sin(t * 1.3 + 2.1));
	}

	// 移除出生点(id=11)星芒标记子对象。几何体/材质逐实例 dispose；
	// 星芒贴图是静态共享缓存（全实例共用），不 dispose。
	removeSpawnMarker() {
		this._spawnMarker = null;
		this._spawnCore = null;
		this._spawnRayA = null;
		this._spawnRayB = null;
		for (let i = this.children.length - 1; i >= 0; i--) {
			const child = this.children[i];
			if (!child || child.name !== 'ydbox_spawn_marker') continue;
			child.traverse((node) => {
				if (node.geometry) node.geometry.dispose();
				if (node.material) node.material.dispose();
			});
			this.remove(child);
		}
	}
	// dispose 后误用的统一告警。只警告一次：raycast 走鼠标移动路径，每帧都可能命中，
	// 逐次 warn 会刷爆控制台并掩盖真正的首次现场。恒返回 true，便于调用方写成
	// `if (this._disposed) return this._warnDisposed('xxx'), undefined;` 之类的早退。
	_warnDisposed(where) {
		if (this._disposedWarned) return true;
		this._disposedWarned = true;
		console.warn(`[YDBoxVolume] ${where}: 该对象已 dispose，不能再使用。` +
			`若需隐藏请用 volume.visible = false（不要把 scene.removeVolume 当显隐用，它是销毁语义）。`);
		return true;
	}

	// 释放 YDBoxVolume 持有的全部资源，供 Scene.removeVolume 移除时调用，避免 video/audio/pdf/3D模型/材质纹理/几何泄漏。
	// 幂等：_disposed 标记提前返回，各步骤前另有判空，重复调用不报错。dispose 后对象不应再使用。
	dispose() {
		if (this._disposed) return;
		// 1. 停止并释放动态资源（video/audio/pdf/clickBtn/3D模型）
		//    复用 setRes 切换资源时的清理逻辑，但内联而非调 setRes，避免 setRes 重建材质造成多余分配。
		// 释放 loadResFromFile 创建的 blob URL（图片/视频/音频）
		if (this._objectUrls && this._objectUrls.length > 0) {
			for (const url of this._objectUrls) {
				URL.revokeObjectURL(url)
			}
			this._objectUrls = []
		}
		if (this.video) {
			this.video.pause()
			this.video.src = ''
			this.video = null
		}
		if (this.sound) {
			if (this.sound.isPlaying) {
				this.sound.stop()
			}
			if (this.sound.source) {
				this.sound.disconnect()
			}
			this.sound = null
		}
		if (this.listener) {
			this.remove(this.listener)
			this.listener = null
		}
			// PDF 文档(pdfjs worker + 页面缓存) 与页面纹理缓存（id=3）
			this.removePDF()
			// PPT 离屏渲染容器与幻灯片引用（id=5）
			this.removePPT()
			// 图集自动翻页计时器与图片纹理缓存（id=8）
			this.removeGallery()
			// 网页 iframe（id=9），必须在 this.viewer 仍可用时调用：
			// Scene.removeVolume 会在派发 volume_removed 之后才调 dispose，此时 viewer 引用仍在
			this.removeWeb()
			// 全景视频(id=10)：球体 + 视频（若处于沉浸态会先退出，恢复相机/可见性）
			this.removePanoramaVideo()
			// 出生点(id=11)：星芒标记子对象
			this.removeSpawnMarker()
			// 交互按钮（视频/音频单按钮，PPT/PDF/图集胶囊控制条）
			if (this.clickBtns && this.clickBtns.length) {
				for (const btn of this.clickBtns) {
					if (btn.material) {
						if (btn.material.map) btn.material.map.dispose()
						btn.material.dispose()
					}
					if (btn.geometry) btn.geometry.dispose()
					this.remove(btn)
				}
				this.clickBtns = []
			}
			this.clickBtn = null
		// 3D 模型子对象（id=4，fbx/glb/gltf/obj/stl 统一打 "yd_fbx_model" 标签）
		this.removeYdModel()

		// 2. 释放几何与材质
		if (this.box) {
			if (this.box.geometry) this.box.geometry.dispose()
		}
		if (this.frame) {
			if (this.frame.geometry) this.frame.geometry.dispose()
			if (this.frame.material) this.frame.material.dispose()
		}
		// 6 面材质（含其 map 纹理）
		if (this.materials) {
			for (const material of this.materials) {
				if (!material) continue
				if (material.map) material.map.dispose()
				material.dispose()
			}
		}
		if (this.materialsBackup) {
			for (const material of this.materialsBackup) {
				if (!material) continue
				if (material.map) material.map.dispose()
				material.dispose()
			}
		}
		if (this.boderMaterial) {
			this.boderMaterial.dispose()
		}
		// TextSprite 标签
		if (this.label) {
			if (this.label.dispose) {
				this.label.dispose()
			} else if (this.label.material) {
				if (this.label.material.map) this.label.material.map.dispose()
				this.label.material.dispose()
			}
		}

		// 3. 从父节点移除，断开场景图引用
		if (this.parent) {
			this.parent.remove(this)
		}

		// 4. 断引用助 GC
		this.res = null
		this.materials = null
		this.materialsBackup = null
		this.box = null
		this.frame = null
		this.label = null
		this.resourceScale = null

		// 5. 标记已析构。上面把 box/frame/label 置 null 后，update()/raycast() 等热路径
		//    再被调用就会解引用 null，报错点距真正的起因隔着好几层（典型是
		//    `volume.clip = x` → set clip → update() → this.box.geometry 崩）。
		//    有了这个标记，那些入口可以早退并给出指向正确 API 的提示。
		this._disposed = true
	}
	// 加载 3D 模型（按 URL 扩展名自动选择 loader）
	// 支持 .fbx(FBXLoader) / .glb/.gltf(GLTFLoader, 含 Draco 压缩) / .obj(OBJLoader) / .stl(STLLoader)
	// 未知扩展名回退到 FBXLoader，保持向后兼容
	addModel(sizeAdaptive, successCallback = null) {
		const url = this.res.res || ""
		// 取小写扩展名（去除 query string 与 hash）；用正则匹配末尾后缀，
		// 避免 blob:/data:/无点 URL 时 lastIndexOf 返回 -1 导致 slice(0) 取到整个 URL
		const cleanUrl = url.split('?')[0].split('#')[0]
		const extMatch = cleanUrl.match(/\.([a-z0-9]+)$/i)
		const ext = extMatch ? extMatch[1].toLowerCase() : ''
		const onLoad = (rawResult) => {
			// 不同 loader 回调返回类型不同：FBX/OBJ -> Object3D；GLTF -> { scene }；STL -> BufferGeometry
			let object = rawResult
			if (ext === 'glb' || ext === 'gltf') {
				object = rawResult.scene
			} else if (ext === 'stl') {
				const geometry = rawResult
				object = new THREE.Mesh(
					geometry,
					new THREE.MeshStandardMaterial({ color: 0xcccccc, metalness: 0.1, roughness: 0.8 })
				)
			}
			// 米锚测量（loader 原始姿态，躺系静态口径）→ 归一化闭环 → UpAxis 转正
			// （刚体 + 字段置换出站系口径，顺序依据见 _applyUpAxis）。
			// GLB 恒 Y-up（glTF 规范）按扩展名写声明；FBX 由 loader 读 GlobalSettings 写
			if (ext === 'glb' || ext === 'gltf') {
				object.userData.upAxis = 1
				object.userData.upAxisSign = 1
				// glTF 规范前向 +Z（契约 v3-11）
				object.userData.frontAxis = 2
				object.userData.frontAxisSign = 1
			}
			const upFix = this._prepareUpAxis(object)
			const frontFix = this._prepareFrontAxis(object)
			this._measureModelMeters(object)
			this.normalizeAndAttachModel(object, sizeAdaptive)
			if (upFix || frontFix) this._applyUpAxis(object, upFix, frontFix, sizeAdaptive)
			if (typeof successCallback === 'function') {
				successCallback(object)
			}
		}
		const onError = (error) => {
			console.error('[YDBoxVolume] addModel 加载失败:', url, error)
		}

		if (ext === 'glb' || ext === 'gltf') {
			const loader = new GLTFLoader()
			// 接入 DRACOLoader 单例以支持 KHR_draco_mesh_compression 压缩 glb
			loader.setDRACOLoader(getDracoLoader())
			loader.load(url, onLoad, undefined, onError)
		} else if (ext === 'obj') {
			const loader = new OBJLoader()
			loader.load(url, onLoad, undefined, onError)
		} else if (ext === 'stl') {
			const loader = new STLLoader()
			loader.load(url, onLoad, undefined, onError)
		} else {
			// 空扩展名（blob:/data:/无点 URL）直接报错，不误走 FBXLoader
			if (ext === '') {
				console.error('[YDBoxVolume] addModel 无法从 URL 推断扩展名，支持: fbx/glb/gltf/obj/stl:', url)
				return
			}
			// fbx 及其它未知扩展名统一走 FBXLoader（向后兼容）
			const loader = new FBXLoader()
			if (ext !== 'fbx') {
				console.warn('[YDBoxVolume] addModel 遇到未知扩展名 "' + ext + '"，回退到 FBXLoader:', url)
			}
			loader.load(url, onLoad, undefined, onError)
		}
	}
	// 从本地 File 对象加载 3D 模型（通过文件选择器选中的文件）
	// 支持 .fbx(FBXLoader) / .glb/.gltf(GLTFLoader, 含 Draco 压缩) / .obj(OBJLoader) / .stl(STLLoader)
	// 与 addModel 不同：本方法不经过 URL，直接用 FileReader 读成 ArrayBuffer/string 后调 loader.parse，
	// 适合用户从本地选择文件的场景（无需服务器托管）。
	// 注意：.gltf(非.glb) 和 .obj+.mtl 会引用外部资源(.bin/.png/.mtl)，单文件加载时无法解析这些外部依赖，
	//       建议优先使用自包含的 .glb / .stl / .fbx。
	loadModelFromFile(file, sizeAdaptive = false, successCallback = null) {
		const fileName = (file && file.name) ? file.name : ""
		const ext = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase()
		if (!ext) {
			console.error('[YDBoxVolume] loadModelFromFile 无法从文件名推断扩展名:', fileName)
			return
		}

		// 记录资源信息（与 setRes 路径一致，便于 resetCoordinatePoseBasedOnFbxModel / copy 等后续逻辑识别为模型类型）
		this.removeYdModel()
		this.res = { id: 4, res: fileName }

		// .gltf（非 .glb）单文件加载时外部 .bin/.png 引用因 path='' 无法解析，提示建议用 .glb
		if (ext === 'gltf') {
			console.warn('[YDBoxVolume] loadModelFromFile: .gltf 单文件加载时外部 .bin/.png 引用无法解析，建议使用自包含的 .glb')
		}

		const onParsed = (object) => {
			// 米锚 → 归一化 → UpAxis 转正（与 addModel onLoad 同款顺序）
			if (ext === 'glb' || ext === 'gltf') {
				object.userData.upAxis = 1
				object.userData.upAxisSign = 1
				// glTF 规范前向 +Z（契约 v3-11）
				object.userData.frontAxis = 2
				object.userData.frontAxisSign = 1
			}
			const upFix = this._prepareUpAxis(object)
			const frontFix = this._prepareFrontAxis(object)
			this._measureModelMeters(object)
			this.normalizeAndAttachModel(object, sizeAdaptive)
			if (upFix || frontFix) this._applyUpAxis(object, upFix, frontFix, sizeAdaptive)
			if (typeof successCallback === 'function') {
				successCallback(object)
			}
		}
		const onError = (error) => {
			console.error('[YDBoxVolume] loadModelFromFile 解析失败:', fileName, error)
		}

		const reader = new FileReader()
		// FileReader.onerror 收到的是 ProgressEvent，真实错误在 e.target.error（DOMException）
		reader.onerror = (e) => onError(e.target.error)
		reader.onload = (e) => {
			const data = e.target.result
			try {
				if (ext === 'glb' || ext === 'gltf') {
					const loader = new GLTFLoader()
					// 接入 DRACOLoader 单例以支持 KHR_draco_mesh_compression 压缩 glb
					loader.setDRACOLoader(getDracoLoader())
					// GLTFLoader.parse 是异步的（回调形式）
					loader.parse(data, '', (gltf) => {
						onParsed(gltf.scene)
					}, onError)
				} else if (ext === 'obj') {
					const object = new OBJLoader().parse(data)
					onParsed(object)
				} else if (ext === 'stl') {
					const geometry = new STLLoader().parse(data)
					const object = new THREE.Mesh(
						geometry,
						new THREE.MeshStandardMaterial({ color: 0xcccccc, metalness: 0.1, roughness: 0.8 })
					)
					onParsed(object)
				} else if (ext === 'fbx') {
					const object = new FBXLoader().parse(data, '')
					onParsed(object)
				} else {
					console.warn('[YDBoxVolume] loadModelFromFile 不支持的扩展名 "' + ext + '"，支持: fbx/glb/gltf/obj/stl')
				}
			} catch (error) {
				onError(error)
			}
		}

		// OBJ 是文本格式用 readAsText；gltf JSON 文本也用 readAsText；其余二进制格式用 readAsArrayBuffer
		if (ext === 'obj') {
			reader.readAsText(file)
		} else if (ext === 'gltf') {
			// .gltf 可能是 JSON 文本也可能是二进制（少数），先尝试按文本读，GLTFLoader.parse 内部会判断
			reader.readAsText(file)
		} else {
			reader.readAsArrayBuffer(file)
		}
	}
	// 从本地 File 对象加载资源（图片/视频/PDF/音频/文本/3D模型），通过文件选择器选中的文件。
	// 通用入口，按 id 分发：1=图片 2=视频 3=PDF 4=3D模型 5=PPT 6=音频 7=文本。
	// 图片/视频/音频用 URL.createObjectURL 生成 blob URL 走 setRes，blob URL 记录到 this._objectUrls，
	//   切换资源/销毁时由 setRes/dispose 统一 revoke 释放。
	// PDF 用 FileReader 读 ArrayBuffer 后走 setRes（setPDFMaterial 支持 ArrayBuffer）。
	// 文本用 file.text() 读取内容后走 setRes（id=7 的 res 是文本本身）。
	// 3D 模型委托 loadModelFromFile（FileReader + loader.parse）。
	loadResFromFile(file, id, sizeAdaptive = false, successCallback = null) {
		if (!file) {
			console.error('[YDBoxVolume] loadResFromFile: file 为空')
			return
		}
		// 3D 模型委托现有方法
		if (id === 4) {
			return this.loadModelFromFile(file, sizeAdaptive, successCallback)
		}
		// 文本：读取文件文本内容，直接走 setRes（id=7 的 res 是文本本身）
		if (id === 7) {
			file.text().then(text => {
				this.setRes({ id: 7, res: text }, sizeAdaptive, successCallback)
			}).catch(err => {
				console.error('[YDBoxVolume] loadResFromFile 文本读取失败:', err)
			})
			return
		}
		// PDF：FileReader 读 ArrayBuffer，设 res 后走 setRes -> setPDFMaterial（支持 ArrayBuffer）
		if (id === 3) {
			const reader = new FileReader()
			reader.onerror = (e) => console.error('[YDBoxVolume] loadResFromFile PDF 读取失败:', e.target.error)
			reader.onload = (e) => {
				this.setRes({ id: 3, res: e.target.result }, sizeAdaptive, successCallback)
			}
			reader.readAsArrayBuffer(file)
			return
		}
		// PPT(pptx)：FileReader 读 ArrayBuffer，设 res 后走 setRes -> setPPTMaterial（支持 ArrayBuffer）
		if (id === 5) {
			const reader = new FileReader()
			reader.onerror = (e) => console.error('[YDBoxVolume] loadResFromFile PPT 读取失败:', e.target.error)
			reader.onload = (e) => {
				this.setRes({ id: 5, res: e.target.result }, sizeAdaptive, successCallback)
			}
			reader.readAsArrayBuffer(file)
			return
		}
		// 图集(8)：接受 FileList / File[] / 单个 File，逐个转 blob URL 组成数组
		if (id === 8) {
			const list = (file instanceof File) ? [file] : Array.from(file || [])
			const images = list.filter(f => f && (!f.type || f.type.startsWith('image/')))
			if (!images.length) {
				console.error('[YDBoxVolume] loadResFromFile 图集：未选择任何图片')
				return
			}
			const urls = images.map(f => URL.createObjectURL(f))
			// 注意：必须先调 setRes（内部会 revoke 上一次的 _objectUrls），再 push 新 url。
			// 若 push 在 setRes 之前，setRes 开头会把刚创建的 url 一起 revoke 掉，导致加载 404。
			this.setRes({ id: 8, res: urls }, sizeAdaptive, successCallback)
			if (!this._objectUrls) this._objectUrls = []
			this._objectUrls.push(...urls)
			return
		}
		// 图片(1)/视频(2)/音频(6)/全景视频(10)：blob URL 走 setRes
		if (id === 1 || id === 2 || id === 6 || id === 10) {
			const url = URL.createObjectURL(file)
			// 注意：必须先调 setRes（内部会 revoke 上一次的 _objectUrls），再 push 新 url。
			// 若 push 在 setRes 之前，setRes 开头会把刚创建的 url 一起 revoke 掉，导致加载 404。
			this.setRes({ id, res: url }, sizeAdaptive, successCallback)
			if (!this._objectUrls) this._objectUrls = []
			this._objectUrls.push(url)
			return
		}
		console.warn('[YDBoxVolume] loadResFromFile 不支持的 id:', id, '支持: 1/2/3/4/5/6/7/8/10（网页 id=9 请直接用 setRes 传 URL）')
	}
	// UpAxis 转正·声明判定：读文件竖直轴声明（0=X/1=Y/2=Z 与 +1/−1 符号，FBXLoader
	// 写入；GLB/gltf 恒 (1,+1) 由两入口按扩展名写入；OBJ/STL 无声明缺省 (2,+1)）。
	// 返回需转正的组合，(2,+1)（Z-up 现状）返回 null 不转。只做判定不旋转——转正
	// 必须置于 normalizeAndAttachModel 之后（见 _applyUpAxis）。
	_prepareUpAxis(object) {
		const ud = object.userData || {}
		const axis = ud.upAxis === undefined ? 2 : ud.upAxis
		const sign = ud.upAxisSign === undefined ? 1 : ud.upAxisSign
		if (axis === 2 && sign === 1) return null
		return { axis, sign }
	}
	// FrontAxis 朝向·声明判定（契约 v3-11）：返回 { f }（文件声明前向单位向量，
	// loader 原始轴系），供 _applyUpAxis 算 φ；无需修正/防御跳过返回 null。
	// ①声明缺失/非法 → 按缺省约定兜底（暂定 up=1→(2,+1)、up=2/0→(1,−1)，mirror
	// 文档 11.6-1；三种组合下 R_axis·f 恰为 −Y，φ=0 零改动）；
	// ②front 与 up 共线（|dot|=1 病态声明）→ 跳过 + warn；
	// ③左手系声明（Up×Front 与 CoordAxis 反向，SDK 行为不可预测）→ 跳过 + warn
	// + 打 __frontSuspect（CoordAxis 未声明时无法核验，视为自洽放行）。
	_prepareFrontAxis(object) {
		const ud = object.userData || {}
		const upAxis = ud.upAxis === undefined ? 2 : ud.upAxis
		const upSign = ud.upAxisSign === undefined ? 1 : ud.upAxisSign
		const validAxis = v => v === 0 || v === 1 || v === 2
		const axisVec = (a, s) => a === 0
			? new THREE.Vector3(s, 0, 0)
			: (a === 1 ? new THREE.Vector3(0, s, 0) : new THREE.Vector3(0, 0, s))
		let fAxis, fSign
		if (validAxis(ud.frontAxis)) {
			fAxis = ud.frontAxis
			fSign = ud.frontAxisSign === undefined ? 1 : (ud.frontAxisSign >= 0 ? 1 : -1)
		} else {
			fAxis = upAxis === 1 ? 2 : 1
			fSign = upAxis === 1 ? 1 : -1
		}
		const upV = axisVec(upAxis, upSign)
		const fV = axisVec(fAxis, fSign)
		if (Math.abs(upV.dot(fV)) === 1) {
			console.warn('[YDBoxVolume] FrontAxis 与 UpAxis 共线（病态声明），跳过朝向修正:', object.name || `front=${fAxis},${fSign}`)
			return null
		}
		if (validAxis(ud.coordAxis)) {
			const coordSign = ud.coordAxisSign === undefined ? 1 : (ud.coordAxisSign >= 0 ? 1 : -1)
			const cr = new THREE.Vector3().crossVectors(upV, fV)
			if (cr.dot(axisVec(ud.coordAxis, coordSign)) < 0) {
				console.warn('[YDBoxVolume] UpAxis×FrontAxis 与 CoordAxis 反向（左手系声明），跳过朝向修正:', object.name || '')
				object.userData.__frontSuspect = true
				return null
			}
		}
		return { f: fV }
	}
	// UpAxis 转正·执行（契约 v3-11 扩展为 R_total = Rz(φ)·R_axis 复合转正）：
	// R_axis 把文件竖直轴转到 +Z（全枚举六组合：(1,±1)→Rx(±90°)、(0,±1)→Ry(∓90°)、
	// (2,−1)→Ry(180°)）；Rz(φ) 为朝向修正，把 R_axis·f_file 旋到规范前向 −Y_editor
	//（φ=atan2(−vx,−vy) 四象限现算，∈{0,±90°,180°}；φ=0 零改动）。绕世界原点左乘
	//（quaternion 与 position 同步），并按 R_total 实际轴映射置换米锚/比例字段分量。
	// 为什么置于 normalizeAndAttachModel 之后：normalize 的闭环（real=size/scale
	// 逐轴对角）依赖「loader 原始姿态下显示响应对角近似成立」；转正旋转进入骨骼
	// 矩阵链后，非均匀 rootScale 的轴作用被旋转混合反转，闭环必破（实测 samba
	// 转正后炸成细长条，单换回归一化 scale 即恢复完整站立人形——渲染与转正本身
	// 无恙）。归一化先按原姿态闭环，再对已居中（几何中心=原点）的模型绕世界原点
	// 刚体转正：盒适配精确保持、蒙皮经 bindMatrixInverse 补偿随骨骼链整体旋转。
	// Rz(φ) 与 Rx/Ry 同为正交轴旋转，论证沿用。
	_applyUpAxis(object, upFix, frontFix, sizeAdaptive) {
		let qTotal = new THREE.Quaternion()
		if (upFix) {
			// R_axis：绕世界原点的竖直轴转正（axis=1 绕 X，其余绕 Y）
			const angle = upFix.axis === 1
				? (upFix.sign > 0 ? 1 : -1) * Math.PI / 2
				: (upFix.axis === 0
					? (upFix.sign > 0 ? -1 : 1) * Math.PI / 2
					: Math.PI) // (2,−1)
			const rotAxis = upFix.axis === 1 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0)
			qTotal.setFromAxisAngle(rotAxis, angle)
			object.quaternion.premultiply(qTotal)
			object.position.applyQuaternion(qTotal)
			object.userData.__axisNormalized = true
		}
		if (frontFix) {
			const v = frontFix.f.clone().applyQuaternion(qTotal)
			// φ 四象限现算后吸附到 90° 整倍数（正交旋转下轴向量分量带 1e-16 级浮点噪声）
			const phi = Math.round(Math.atan2(-v.x, -v.y) / (Math.PI / 2)) * (Math.PI / 2)
			if (phi !== 0) {
				const qz = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), phi)
				object.quaternion.premultiply(qz)
				object.position.applyQuaternion(qz)
				object.userData.__frontApplied = true
				object.userData.__axisNormalized = true
				qTotal.premultiply(qz)
			}
		}
		if (object.userData.__axisNormalized) {
			// 蒙皮变形包围盒缓存（CPU 变形值）随姿态作废，置空待重算（frustum culling/reset 消费）
			object.traverse(o => {
				if (o.isSkinnedMesh) {
					o.boundingBox = null
					o.boundingSphere = null
				}
			})
		}
		// 站系字段按 R_total 实际轴映射置换（v3 泛化，取代按旋转类特判：(2,−1) 恒等、
		// φ=180° 恒等、φ=±90° 与 R_axis 复合成 3 循环，均由矩阵判读自然得出）。
		// map[i]=file 轴 i 映到的站系轴：extent 沿 file 轴 i 出现在站系轴 map[i]。
		// naturalSizeMeters 是无符号尺寸直接换；resourceScale=(1, ry/rx, rz/rx) 分母含 x，
		// 须从归一化时存下的 real 按 map 重算。
		const me = new THREE.Matrix4().makeRotationFromQuaternion(qTotal).elements
		const map = []
		for (let col = 0; col < 3; col++) {
			let best = 0
			let bestAbs = 0
			for (let row = 0; row < 3; row++) {
				const a = Math.abs(me[col * 4 + row])
				if (a > bestAbs) { bestAbs = a; best = row }
			}
			map.push(best)
		}
		const permute = v => {
			const out = new THREE.Vector3()
			for (let i = 0; i < 3; i++) out.setComponent(map[i], v.getComponent(i))
			return out
		}
		if (object.naturalSizeMeters) {
			object.naturalSizeMeters.copy(permute(object.naturalSizeMeters))
		}
		// sizeAdaptive 语义下盒尺寸由 normalize 按躺系 real 比例设置，模型转正为站系
		// 后盒必须同步置换，否则盒/模型轴序错配、静态模型被非均匀拉伸变形
		//（实测小猫大头压扁）。sizeAdaptive=false 的固定盒是用户/业务设定，不动。
		if (sizeAdaptive && this.resourceScale) {
			this.scale.copy(permute(this.scale))
		}
		const real = object.__normalizeReal
		if (real) {
			const sr = permute(real)
			this.resourceScale = new THREE.Vector3(1, sr.y / sr.x, sr.z / sr.x)
		}
	}
	// 模型米锚测量（静态几何）：不缩放不烘焙（scale/顶点/骨骼 bind 矩阵零接触），
	// 用静态 geometry 包围盒合成 naturalSizeMeters 存根对象，供消费方（mirror 的
	// 缩放系数米锚）与 resetCoordinatePoseBasedOnFbxModel 的盒子尺寸使用。
	// 测量系=加载时刻的世界系（loader 原始姿态）；UpAxis 转正置于本测量与归一化
	// 之后（顺序依据见 _applyUpAxis），站系口径由 _applyUpAxis 按转正旋转的置换
	// （Rx 类 y↔z、Ry 类 x↔z）得出。蒙皮模型用静态 geometry.boundingBox（非变形后顶点）且只在
	// 加载时测一次存值，与骨骼姿态/后续变换解耦，确定性成立。
	// 单位折算：带 unitScaleFactor 的 FBX 按 usf/100（1 文件单位 = usf 厘米）；
	// 无该字段的格式也按 f=1 产出（glTF 规范本身即米；OBJ/STL 单位未知，口径=
	// 加载原值）——消费方对所有格式统一走静态几何确定性口径，蒙皮模型不再
	// 回退到姿态漂移的变形 Box3。
	// 不采用「烘顶点/乘 root.scale」的换算：蒙皮模型的测量与渲染都走骨骼
	// 变形链——顶点缩放而 bind 矩阵不动会让蒙皮输出错乱（实测模型炸裂），
	// 且变形包围盒随骨骼姿态漂移（同文件两次加载 natural 可差 40%），二者
	// 都使任何「改变换」方案在蒙皮模型上不可靠；静态几何 bbox 与姿态解耦、
	// 确定性，是唯一稳定的米语义测量面。
	_measureModelMeters(object) {
		const usf = object.userData && object.userData.unitScaleFactor
		const f = Number.isFinite(usf) && usf > 0 ? usf / 100 : 1
		object.updateMatrixWorld(true)
		const box = new THREE.Box3()
		const childBox = new THREE.Box3()
		object.traverse(o => {
			const g = o.geometry
			if (!g || !g.attributes || !g.attributes.position) return
			if (!g.boundingBox) g.computeBoundingBox()
			childBox.copy(g.boundingBox).applyMatrix4(o.matrixWorld)
			box.union(childBox)
		})
		if (box.isEmpty()) return
		object.naturalSizeMeters = box.getSize(new THREE.Vector3()).multiplyScalar(f)
		object.userData.__unitNormalizedMeters = true // 打标：字段已产出（FBX 按 usf 折米，其余按加载原值）
	}
	// 将加载完成的模型 Object3D 归一化到单位盒并挂载为 box 子对象
	// 复用 addFBXModel 的约定：name 标签、originalCenter/originalScale、resourceScale，
	// 使 resetCoordinatePoseBasedOnFbxModel 和 copy 自动兼容
	// 注意：必须在 loader 原始姿态（UpAxis 转正之前）闭环——见 _applyUpAxis
	normalizeAndAttachModel(object, sizeAdaptive) {
		object.name = "yd_fbx_model"
		// 获取包围盒并且设置 缩放比使其兼容父级的scale为长宽高的情况
		const box = new THREE.Box3().setFromObject(object);
		const size = new THREE.Vector3();
		const center1 = new THREE.Vector3();
		box.getCenter(center1);
		box.getSize(size);
		// 保存原始中心点
		object.originalCenter = center1.clone();
		object.originalScale = object.scale.clone();

		const real_x = size.x / object.scale.x
		const real_y = size.y / object.scale.y
		const real_z = size.z / object.scale.z
		// 存原始几何尺寸供 UpAxis 转正重算站系 resourceScale（分母换轴时简单互换不成立）
		object.__normalizeReal = new THREE.Vector3(real_x, real_y, real_z)

		object.scale.set(1 / real_x, 1 / real_y, 1 / real_z);

		// 重新获取一下包围盒获取中心点
		const box2 = new THREE.Box3().setFromObject(object);
		const center = new THREE.Vector3();
		box2.getCenter(center);

		// 将模型平移到其几何中心
		object.position.sub(center);

		this.add(object);
		this.updateMatrixWorld();

		if (sizeAdaptive) {
			// 以x为媒介计算出 y z
			const y = real_y / real_x * this.scale.x
			const z = real_z / real_x * this.scale.x
			this.setScaleSize(this.scale.x, y, z, sizeAdaptive)
		}
		// 设置长宽高比
		this.resourceScale = new THREE.Vector3(1, real_y / real_x, real_z / real_x)
	}
	// 根据fbx模型信息重置坐标位姿
	resetCoordinatePoseBasedOnFbxModel() {
		let fbx = null
		for (let i = 0; i < this.children.length; i++) {
			const child = this.children[i];
		if (child.name == "yd_fbx_model") {
			// 创建 fbx 的副本
			fbx = child.clone()
			fbx.originalCenter = child.originalCenter.clone()
			fbx.originalScale = child.originalScale.clone()
			// Object3D.clone 不拷贝自定义字段，米锚需手动带过来（只读共享）
			fbx.naturalSizeMeters = child.naturalSizeMeters
			break
		}
		}
		if (fbx) {
			if (fbx.originalCenter && fbx.originalScale) {
				fbx.scale.copy(fbx.originalScale)
				fbx.position.copy(fbx.originalCenter)
				// 获取 FBX 的位置和旋转信息
				const position = fbx.position;
				const quaternion = fbx.quaternion;

				// 创建新的转换矩阵（FBX 到点云坐标系）
				const rotMatrix = new THREE.Matrix4().set(
					1, 0, 0, 0,
					0, 0, 1, 0,
					0, -1, 0, 0,
					0, 0, 0, 1
				);

				// 添加 Y 轴 180 度旋转的矩阵
				const yRotMatrix = new THREE.Matrix4().makeRotationY(Math.PI);
				rotMatrix.multiply(yRotMatrix);

				// 添加 Y 轴 180 度旋转的矩阵
				const zRotMatrix = new THREE.Matrix4().makeRotationZ(Math.PI);
				rotMatrix.multiply(zRotMatrix);

				// 转换位置
				const slamPosition = new THREE.Vector3(
					position.x,
					-position.z,
					position.y
				);

				// 转换旋转
				const rotationMatrix = new THREE.Matrix4().makeRotationFromQuaternion(quaternion);
				const finalRotation = new THREE.Matrix4()
					.multiplyMatrices(rotMatrix, rotationMatrix);

				const finalQuaternion = new THREE.Quaternion();
				finalQuaternion.setFromRotationMatrix(finalRotation);

				const size = new THREE.Vector3();
				// 盒子尺寸用静态几何米锚（naturalSizeMeters，所有格式产出），与骨骼
				// 姿态解耦；变形包围盒随姿态漂移，不可作尺寸语义。无字段时（旧引擎
				// 加载的存量模型）回退变形 Box3
				if (fbx.naturalSizeMeters) {
					size.copy(fbx.naturalSizeMeters)
				} else {
					const box = new THREE.Box3().setFromObject(fbx);
					box.getSize(size);
				}

				this.position.copy(slamPosition);
				this.quaternion.copy(finalQuaternion);
				this._syncOrientationChanged();
				this.setScaleSize(size.x, size.y, size.z, true)
			}
			// 清理克隆对象
			fbx.geometry?.dispose();
			fbx.material?.dispose();
		}
	}
	// 加载音频内容
	setAudioMaterial(material, sizeAdaptive) {
		const textureLoader = new THREE.TextureLoader();
		textureLoader.load(`${exports.resourcePath}/icons/audio.png`, (texture) => {
			// 重构178 THREE.sRGBEncoding → THREE.SRGBColorSpace
			// texture.colorSpace = THREE.sRGBEncoding;
			texture.colorSpace = THREE.SRGBColorSpace;
			texture.premultiplyAlpha = false;

			this.setMaterialMapAndBaseInfo(material, texture)

			// 获取图片的宽高
			const width = texture.image.width;
			const height = texture.image.height;
			this.resourceScale = new THREE.Vector2(1, height / width)

			if (sizeAdaptive) {
				const showHeight = height / width * this.scale.y
				this.setScaleSize(0.01, this.scale.y, showHeight, sizeAdaptive)
			}
			// 创建音频监听器
			this.listener = new THREE.AudioListener();
			this.add(this.listener);
			// 创建音频对象
			this.sound = new THREE.Audio(this.listener);
			// 加载音频文件
			const audioLoader = new THREE.AudioLoader();
			audioLoader.load(this.res.res, (buffer) => {
				this.sound.setBuffer(buffer);
			});

			this.createPlayerBtn({ str: "play.png", position: { x: 0.5, y: 0, z: -0.4 } })
		});
	}
	// 按盒子正面宽高比拟合文本：让文字尽量放大填满盒面（约 fill 高度），
	// 单行放不下时先放大到宽度上限，仍不够（低于下限）才按字符换行。
	// 返回 { canvasW, canvasH, lines, fontsize, lineHeight }，供 setTextMaterial /
	// 缩放盒子时按新宽高比重绘正面文字/占位文字贴图。等比缩放（宽高比不变）时不重绘，
	// 既保证非等比缩放下文字不被拉伸，又避免每帧无谓重建纹理。
	_redrawFaceTextOnScale() {
		if (!this.res) return;
		const isText = this.res.id === 7 && this.res.res != null && this.res.res !== '';
		const isPlaceholder = (this.res.res == null || this.res.res === '') &&
			this.res.name != null && this.res.name !== '';
		// 网页(id=9) 的正面是「URL 占位文字」贴图（网页本体在 CSS3D 层），同样需要按宽高比重绘
		const isWeb = this.res.id === 9 && typeof this.res.res === 'string' && this.res.res !== '';
		if (!isText && !isPlaceholder && !isWeb) return;

		const faceW = this.scale.y * this.ySize;
		const faceH = this.scale.z * this.zSize;
		const aspect = (faceW > 0 && faceH > 0) ? (faceW / faceH) : 1;
		// 宽高比几乎没变（等比缩放）时无需重绘
		if (this._lastTextAspect && Math.abs(aspect - this._lastTextAspect) / this._lastTextAspect < 0.02) return;

		const material = this.getMaterial(this.getFront());
		if (!material) return;
		if (isText) this.setTextMaterial(material, this.res.res);
		else if (isWeb) this.setPlaceholderTextMaterial(material, this.res.res);
		else this.setPlaceholderTextMaterial(material, this.res.name);
	}
	// 把文本按换行标记拆成"逻辑段"数组，段之间为硬断行、空串段=空行。
	// 支持的换行标记（split 分隔符，顺序敏感：\r\n 须优先于 \r/\n 避免 CRLF 被拆成两个空行）：
	//   \r\n           Windows 文本文件换行（0x0D 0x0A）
	//   \r             旧 Mac 换行（0x0D）
	//   \n             Unix/Linux/.txt 多行换行（0x0A）
	//   <br>/<br/>/<br />  HTML 换行标签，大小写不敏感（<BR> 也匹配）
	//   \\n            字面量反斜杠+n（lil-gui 单行输入框里无法输入真实换行时的替代写法）
	// 特殊字符处理：
	//   \t 制表符      转成 4 个空格（canvas 对 \t 宽度不可控，转空格更整齐）
	//   零宽字符/BOM   U+200B/U+200C/U+200D/U+FEFF 去除（干扰 measureText 折行、BOM 可能渲染成框）
	// 空行处理：连续标记产生的空行（含首尾）原样保留，用于段落间距。
	// 不支持：其它 HTML 标签如 <b>/<p>（canvas 不解析 HTML，原样显示为字面字符）。
	// 已知 trade-off：字面量 \\n 会把 "C:\newfolder" 这类含"反斜杠+n"的路径误判为换行，
	//   这是为单行输入框场景做的取舍，文件场景一般用真实换行符不受影响。
	_splitTextSegments(text) {
		if (text == null) return [''];
		// \t 转 4 空格、去除零宽字符与 BOM（在 split 之前统一清洗）
		const cleaned = String(text)
			.replace(/\t/g, '    ')
			.replace(/[\u200B\u200C\u200D\uFEFF]/g, '');
		const re = /\r\n|\r|\n|<br\s*\/?>|\\n/gi;
		const segs = cleaned.split(re);
		return segs.length ? segs : [''];
	}
	// 把一个逻辑段按"字形簇"(grapheme cluster)拆成数组，供 wrap 逐簇遍历折行。
	// 关键：emoji/家庭序列(ZWJ)/肤色修饰/旗帜等是多个 UTF-16 码元组成的单个字形，
	//   若用 seg[i] 逐码元遍历会拆散代理对导致乱码豆腐块。必须按字形簇整体移动。
	// 优先用 Intl.Segmenter（支持 ZWJ 序列合并）；不可用时降级为 [...str] 码点迭代
	//   （代理对完整不乱码，但 ZWJ 序列会被拆成多个簇，影响有限）。
	_graphemeSegments(seg) {
		if (seg.length === 0) return [''];
		if (typeof Intl !== 'undefined' && typeof Intl.Segmenter !== 'undefined') {
			const segmenter = YDBoxVolume._graphemeSegmenter
				|| (YDBoxVolume._graphemeSegmenter = new Intl.Segmenter('zh', { granularity: 'grapheme' }));
			return [...segmenter.segment(seg)].map(x => x.segment);
		}
		return [...seg];
	}

	// 按盒子正面宽高比拟合文本，供 setTextMaterial / setPlaceholderTextMaterial 复用。
	// canvas 尺寸绑定盒面宽高比，绘制时按比例居中不拉伸。
	_layoutFittedText(context, text, borderThickness, margin, minScale = 0.5) {
		// 盒子正面世界尺寸：正面情况下 scale.y 是宽度、scale.z 是高度（scale.x 是厚度），
		// 与 scale_changed 里按钮重排的轴向约定保持一致，据此确定 canvas 宽高比，避免文字被拉伸变形
		const faceW = this.scale.y * this.ySize;
		const faceH = this.scale.z * this.zSize;
		let faceAspect = (faceW > 0 && faceH > 0) ? (faceW / faceH) : 1;
		// 记录本次排版所用的宽高比，供缩放时判断是否需要重绘
		this._lastTextAspect = faceAspect;

		// 纹理预算：按宽高比在 TEX 内定 canvas 尺寸
		const TEX = 2048;
		const MIN = 256;
		let canvasW, canvasH;
		if (faceAspect >= 1) {
			canvasW = TEX;
			canvasH = Math.max(MIN, Math.round(TEX / faceAspect));
		} else {
			canvasH = TEX;
			canvasW = Math.max(MIN, Math.round(TEX * faceAspect));
		}

		// 四周留白系数：把 margin/border 造成的内边距缩到 1/6，让文字更贴边
		const pad = 1 / 6;
		const innerW = Math.max(1, canvasW - 2 * (margin + borderThickness) * pad);
		const innerH = Math.max(1, canvasH - 2 * borderThickness * pad);

		// 文字目标填充盒面高度的比例（0.985 ≈ 上下几乎贴边）
		const fill = 0.985;
		// 多行时的行高系数（含行间距）
		const LH = 1.2;

		// 单位字号下整段文字的宽度（宽度随字号线性变化，用参考字号测一次即可）
		// 注意：用户换行后应按"最长逻辑段"测量宽度，而非整段连成一串测量，
		// 否则 wPerFont 偏大导致字号偏小、单行判定失误。
		const segments = this._splitTextSegments(text);
		const REF = 100;
		context.font = 'Bold ' + REF + 'px ' + this.fontface;
		let maxSegW = 1e-6;
		for (const seg of segments) {
			const w = context.measureText(seg).width / REF;
			if (w > maxSegW) maxSegW = w;
		}
		const wPerFont = maxSegW;

		// 按逻辑段折行：段间硬断行（用户指定的换行点）、空段保留空行，
		// 段内按字形簇逐簇自动折行（中英/emoji 混排，避免拆散代理对乱码）。
		const wrap = (fs, limitW) => {
			context.font = 'Bold ' + fs + 'px ' + this.fontface;
			let out = [];
			for (const seg of segments) {
				if (seg.length === 0) { out.push(''); continue; }  // 空段=空行
				const clusters = this._graphemeSegments(seg);      // 按字形簇拆分（emoji 整体不拆散）
				let cur = '';
				for (const ch of clusters) {                       // 逐字形簇按宽度折行
					const t = cur + ch;
					if (context.measureText(t).width > limitW && cur.length > 0) {
						out.push(cur);
						cur = ch;
					} else {
						cur = t;
					}
				}
				if (cur.length > 0) out.push(cur);
			}
			return out.length ? out : [''];
		};

		// 单行下：宽度允许的最大字号 fW，高度允许（填满 fill）的字号 fH
		const fW = innerW / wPerFont;
		const fH = fill * innerH;

		// 用户指定的换行点决定的最少行数（每个逻辑段至少占一行，空段也算一行）
		const segCount = segments.length;
		const minLines = segCount;

		let fontsize, lines, lineHeight;
		if (segCount === 1 && fW >= fH * minScale) {
			// 单段且单行即可（宽度充足直接填满高度；或缩小后仍不低于下限）
			fontsize = Math.min(fW, fH);
			lines = [text];
			lineHeight = fontsize * LH;
		} else {
			// 需要换行：选行数 L 平衡宽/高，使字号尽量大且块高填满 fill*innerH
			// 宽度允许字号 ≈ L*fW，高度允许字号 ≈ fill*innerH/(L*LH)，两者相等时最优
			// 下限取 minLines，保证用户指定的换行点不被压成更少行
			let L = Math.max(minLines, Math.round(Math.sqrt((fill * innerH) / (LH * fW))));
			fontsize = Math.min(L * fW, (fill * innerH) / (L * LH));
			lines = wrap(fontsize, innerW);
			// 按实际行数校正，保证不超出盒面高度
			const realL = Math.max(1, lines.length);
			const maxByH = (fill * innerH) / (realL * LH);
			if (fontsize > maxByH) {
				fontsize = maxByH;
				lines = wrap(fontsize, innerW);
			}
			lineHeight = fontsize * LH;
		}

		return { canvasW, canvasH, lines, fontsize, lineHeight };
	}
	// 加载文本内容
	setTextMaterial(material, text, sizeAdaptive = false) {
		let canvas = document.createElement('canvas');
		var context = canvas.getContext('2d');

		const borderThickness = this.borderThickness * 50
		const margin = 50;

		const layout = this._layoutFittedText(context, text, borderThickness, margin, 0.5);
		const { canvasW, canvasH, lines, fontsize, lineHeight } = layout;

		context.canvas.width = canvasW;
		context.canvas.height = canvasH;
		context.font = 'Bold ' + fontsize + 'px ' + this.fontface;
		context.textAlign = 'center';
		context.textBaseline = 'middle';
		context.lineWidth = Math.max(1, fontsize * 0.04);
		// text color
		context.strokeStyle = 'rgba(0, 0, 0, 1.0)';
		context.fillStyle = 'rgba(' + this.textColor.r + ',' + this.textColor.g + ',' +
			this.textColor.b + ',' + this.textColor.a + ')';

		// 整体垂直居中、每行水平居中绘制
		const blockH = lineHeight * lines.length;
		const startY = canvasH / 2 - blockH / 2 + lineHeight / 2;
		for (let i = 0; i < lines.length; i++) {
			const y = startY + i * lineHeight;
			context.strokeText(lines[i], canvasW / 2, y);
			context.fillText(lines[i], canvasW / 2, y);
		}
		/////////////////////////////////////////////////
		let texture = new THREE.Texture(canvas);
		texture.minFilter = THREE.LinearFilter;
		texture.magFilter = THREE.LinearFilter;
		texture.needsUpdate = true;
		// 重构178 THREE.sRGBEncoding -> THREE.SRGBColorSpace
		// texture.colorSpace = THREE.sRGBEncoding;
		texture.colorSpace = THREE.SRGBColorSpace;

		this.setMaterialMapAndBaseInfo(material, texture)

		// 与图片/视频/PPT 一致：加载文本资源后把盒子厚度(scale.x)压到极薄，只保留正面文字。
		// 放在贴图绘制之后：此时 _lastTextAspect 已记录，且仅改厚度不改 y/z 宽高比，
		// scale_changed 监听会因宽高比未变而跳过重绘，避免多余的纹理重建。
		if (sizeAdaptive) {
			this.setScaleSize(0.01, this.scale.y, this.scale.z, sizeAdaptive)
		}
	}
	// 加载文本内容
	setPlaceholderTextMaterial(material, text) {
		let canvas = document.createElement('canvas');
		var context = canvas.getContext('2d');

		const borderThickness = this.borderThickness * 50
		const margin = 50;

		const layout = this._layoutFittedText(context, text, borderThickness, margin, 0.5);
		const { canvasW, canvasH, lines, fontsize, lineHeight } = layout;

		context.canvas.width = canvasW;
		context.canvas.height = canvasH;

		// 绘制斜线背景，标识这是占位材质
		context.fillStyle = 'rgba(230, 230, 230, 0.5)';
		context.fillRect(0, 0, canvasW, canvasH);

		// 绘制斜线（从右上到左下，形成网格效果）
		context.strokeStyle = 'rgba(180, 180, 180, 0.7)';
		context.lineWidth = 10;
		const lineGap = 200; // 斜线间距
		for (let i = 0; i < canvasW + canvasH; i += lineGap) {
			context.beginPath();
			context.moveTo(i, 0);
			context.lineTo(i - canvasH, canvasH);
			context.stroke();
		}

		// 居中绘制占位名称（整体垂直居中、每行水平居中）
		context.font = 'Bold ' + fontsize + 'px ' + this.fontface;
		context.textAlign = 'center';
		context.textBaseline = 'middle';
		context.lineWidth = Math.max(1, fontsize * 0.04);
		context.strokeStyle = 'rgba(0, 0, 0, 1.0)';
		context.fillStyle = 'rgba(230, 230, 230, 1)';

		const blockH = lineHeight * lines.length;
		const startY = canvasH / 2 - blockH / 2 + lineHeight / 2;
		for (let i = 0; i < lines.length; i++) {
			const y = startY + i * lineHeight;
			context.strokeText(lines[i], canvasW / 2, y);
			context.fillText(lines[i], canvasW / 2, y);
		}
		/////////////////////////////////////////////////
		// var texture = new THREE.CanvasTexture(canvas);
		let texture = new THREE.Texture(canvas);
		texture.minFilter = THREE.LinearFilter;
		texture.magFilter = THREE.LinearFilter;
		texture.needsUpdate = true;
		// 重构178 THREE.sRGBEncoding → THREE.SRGBColorSpace
		// texture.colorSpace = THREE.sRGBEncoding;
		texture.colorSpace = THREE.SRGBColorSpace;

		this.setMaterialMapAndBaseInfo(material, texture)

		this.resourceScale = new THREE.Vector3(1, 1, 1)
	}
	// 把 this.resOpacity 应用到正面资源材质。setMaterialMapAndBaseInfo / _applySlideTexture /
	// setResOpacity 三个入口共用这一套规则，保证「翻页、缩放重绘、切换资源」都不会把透明度冲掉。
	//
	// slideMode=true 表示 PDF(3)/PPT(5)/图集(8)：完全不透明时保持 transparent=false，
	//   与本功能上线前的行为逐像素一致 —— html2canvas 画出的幻灯片画布常带透明背景区，
	//   一旦开了 transparent 那些区域会突然变成可透视。
	// slideMode=false（图片/视频/音频/文本/占位）恒为 transparent=true：现状本就如此
	//   （继承 parameterBase），且 PNG 的 alpha 通道、占位图的 rgba(230,230,230,0.5) 底纹都依赖它。
	//   这里显式赋值同时修掉一个潜在 bug：PPT→图片 的切换不经过 setRes 里的材质重建，
	//   会残留上一个 slide 资源留下的 transparent=false，导致图片透明区失效。
	//
	// depthWrite 恒为 true（即使半透明也写深度）：box 是单个 Mesh + 6 个材质槽，
	//   front 是 group 0 最先绘制，而 renderer.sortObjects=false（viewer.js）。
	//   若 front 不写深度，随后绘制的其余 5 个黄色半透面（opacity 0.5）会叠在图片上造成黄色偏色；
	//   写了深度它们就会被 depthTest 剔除。半透明与点云的混合不受影响 ——
	//   VolumeTool.renderDepth 在 clearDepth 之前执行，此刻 framebuffer 里已是点云颜色。
	_applyResOpacityToMaterial(material, slideMode) {
		const opacity = this.resOpacity;
		material.opacity = opacity;
		material.transparent = slideMode ? (opacity < 1) : true;
		material.depthWrite = true;
	}

	// 设置资源内容透明度，即改即生效（不重新加载贴图）。
	// opacity: [0,1]，1=不透明。非法值忽略。
	setResOpacity(opacity) {
		const value = Number(opacity);
		if (!isFinite(value)) return;
		this.resOpacity = Math.min(1, Math.max(0, value));

		const id = this.res && this.res.id;
		if (id === 9) {
			// 网页内容是 canvas 之下的 DOM iframe，混合发生在打洞层写的 canvas alpha 上，
			// 不走材质。正面占位贴图故意不跟着变淡 —— 它在洞后面，跟着变会二次变淡。
			const layer = this._getHolePunchLayer();
			if (layer) layer.setOpacity(this, this.resOpacity);
			return;
		}

		// 全景视频(id=10)：透明度作用于球体材质
		if (id === 10) {
			if (this._panoMat) {
				this._panoMat.opacity = this.resOpacity;
				this._panoMat.transparent = this.resOpacity < 1;
				this._panoMat.needsUpdate = true;
			}
			return;
		}

		// 出生点(id=11)：逐帧动画里已按 resOpacity × 呼吸脉冲设置材质透明度，
		// 星芒是加法混合的独立子对象，不碰正面占位贴图
		if (id === 11) {
			return;
		}

		const material = this.getMaterial(this.getFront());
		if (!material) return;
		// PDF(3)/PPT(5)/图集(8) 走 _applySlideTexture，其余走 setMaterialMapAndBaseInfo
		this._applyResOpacityToMaterial(material, id === 3 || id === 5 || id === 8);
		material.needsUpdate = true;
	}

	getResOpacity() {
		return this.resOpacity;
	}

	// 设置材质贴图和基础信息
	setMaterialMapAndBaseInfo(material, texture) {
		if (material.map) {
			material.map.dispose()
		}
		material.map = texture;
		// 兜底标注色彩空间：three r180 默认 ColorManagement 开启且 renderer 输出为 SRGBColorSpace，
		// 若纹理 colorSpace 为默认 NoColorSpace，sRGB 图片/视频会被当线性数据再套一次 sRGB gamma 导致偏亮。
		// 这里统一兜底为 SRGBColorSpace，修复图片/视频/PDF 路径此前漏设导致的偏亮问题，
		// 与文本/PPT/音频路径各自已设的 SRGBColorSpace 保持一致（重复赋值无副作用）。
		texture.colorSpace = THREE.SRGBColorSpace;
		this._applyResOpacityToMaterial(material, false);
		// material.blending = THREE.NoBlending;
		// material.color.r = 1;
		// material.color.g = 1;
		// material.color.b = 1;
		material.color = null
		material.colorWrite = true;
		// 重构178 NoColors 废弃的静态变量
		material.vertexColors = 0;
		material.needsUpdate = true;

		// material.alphaTest = 0.1;
	}

	// ============= 这一部分和业务有很大关联 end ==========
	getTop() {
		return this.sides['top'];
	}
	getBottom() {
		return this.sides['bottom'];
	}

	getFront() {
		return this.sides['front'];
	}
	getBack() {
		return this.sides['back'];
	}
	getRight() {
		return this.sides['right'];
	}
	getLeft() {
		return this.sides['left'];
	}
	raycast(raycaster, intersects) {
		// dispose 后 this.box 为 null，而本方法由 InputHandler.getHoveredElements 经
		// raycaster.intersectObjects 在每次鼠标移动时调用，且那里的 filter 是
		// `o.visible || o instanceof YDBoxVolume`（InputHandler.js:757），特意让
		// YDBoxVolume 绕过 visible 检查。所以僵尸对象只要还残留在 interactables 里，
		// 下一次鼠标移动就崩，且报错与任何用户操作都对不上号 —— 最难定位的一条。
		if (this._disposed) {
			this._warnDisposed('raycast');
			return;
		}
		let is = [];
		this.box.raycast(raycaster, is);
		let faceIndexIntersect = -1;
		if (is.length > 0) {
			for (let i = 0; i < this.box.geometry.groups.length; i++) {
				var group = this.box.geometry.groups[i];
				var faceIndexTmp = is[0].faceIndex / 2;
				// if(faceIndexTmp >= group.start && faceIndexTmp < group.start+group.count)
				{
					faceIndexIntersect = faceIndexTmp;
					// var parameter0={
					// 	color: 0x00ffff,
					// 	transparent: true,
					// 	opacity: 0.5,
					// 	depthTest: true,
					// 	depthWrite: false};
					// this.setBaseMaterial(this.parameterBase);
					// this.setMaterial(faceIndexIntersect,parameter0);
				}
			}
			let I = is[0];
			intersects.push({
				distance: I.distance,
				object: this,
				point: I.point.clone(),
				faceIndex: faceIndexIntersect
			});
		}
	}
	getVolume() {
		return Math.abs(this.scale.x * this.scale.y * this.scale.z);
	}
	copy(source) {

		super.copy(source, false);

		this.clip = source.clip;
		// this.modelViewMatrix = new Matrix4();
		this.modelViewMatrix.copy(source.modelViewMatrix);
		this.Faces = source.Faces;
		this.constructor.counter = (this.constructor.counter === undefined) ? 0 : this.constructor.counter + 1;
		this.name = 'box_cp' + source.name + this.constructor.counter;
		// this.res = Object.create(source.res);
		if (source.res) {
			this.res = {
				id: source.res.id,
				res: source.res.res
			}
		}

		this.lock = source.lock;
		this.hideCloud = source.hideCloud;

		this.boderMaterial.copy(source.boderMaterial);

		this.xSize = source.xSize;
		this.ySize = source.ySize;
		this.zSize = source.zSize;

		// 下面 materials[frontId].copy() 会带过 opacity/transparent，但字段本身必须显式复制：
		// 否则拷贝出的盒子一翻页或一非等比缩放（都会重走 applier）就把透明度打回 1
		this.resOpacity = source.resOpacity;

		if (source.resourceScale) {
			if (source.resourceScale instanceof THREE.Vector2) {
				this.resourceScale = new THREE.Vector2().copy(source.resourceScale);
			} else {
				this.resourceScale = new THREE.Vector3().copy(source.resourceScale);
			}
		}

		this.material.copy(source.material);

		this.setBaseMaterial(source.parameterBase);

		const frontId = this.getFront();
		let frontMaterial = source.materials[frontId]
		if (frontMaterial && frontMaterial.color == null) {
			frontMaterial.color = "#FFFFFF"
			this.materials[frontId].copy(frontMaterial);
			frontMaterial.color = null;
			this.materials[frontId].color = null;
		} else {
			this.materials[frontId].copy(frontMaterial);
		}


		// if (source.materials[1] && source.materials[1].color == null) {
		// 	source.materials[1].color = "#FFFFFF"
		// 	this.materials[1].copy(source.materials[1]);
		// 	source.materials[1].color = null;
		// 	this.materials[1].color = null;
		// } else {
		// 	this.materials[1].copy(source.materials[1]);
		// }
		// if (source.materials[2] && source.materials[2].color == null) {
		// 	source.materials[2].color = "#FFFFFF"
		// 	this.materials[2].copy(source.materials[2]);
		// 	source.materials[2].color = null;
		// 	this.materials[2].color = null;
		// } else {
		// 	this.materials[2].copy(source.materials[2]);
		// }
		// if (source.materials[3] && source.materials[3].color == null) {
		// 	source.materials[3].color = "#FFFFFF"
		// 	this.materials[3].copy(source.materials[3]);
		// 	source.materials[3].color = null;
		// 	this.materials[3].color = null;
		// } else {
		// 	this.materials[3].copy(source.materials[3]);
		// }
		// if (source.materials[4] && source.materials[4].color == null) {
		// 	source.materials[4].color = "#FFFFFF"
		// 	this.materials[4].copy(source.materials[4]);
		// 	source.materials[4].color = null;
		// 	this.materials[4].color = null;
		// } else {
		// 	this.materials[4].copy(source.materials[4]);
		// }
		// if (source.materials[5] && source.materials[5].color == null) {
		// 	source.materials[5].color = "#FFFFFF"
		// 	this.materials[5].copy(source.materials[5]);
		// 	source.materials[5].color = null;
		// 	this.materials[5].color = null;
		// } else {
		// 	this.materials[5].copy(source.materials[5]);
		// }

		this.sides = source.sides;
		this.frame.copy(source.frame);
		// this.frame = new THREE.LineSegments(source.frame);
		// this.frame.mode  = source.frame;
		this.createGeometry();

		// this.frame = source.frame;

		// 全景视频(id=10)：拷贝后重建球体与视频（视频元素无法共享，需重新加载）
		if (source.res && source.res.id === 10) {
			this.setPanoramaVideoMaterial(this.getMaterial(this.getFront()), false);
		}

		// 出生点(id=11)：拷贝后重建星芒标记（Group 子对象不随 copy 复制；
		// 逐帧动画由 VolumeTool.update 驱动，入场景后自动生效）
		if (source.res && source.res.id === 11) {
			this.setSpawnMarkerMaterial(this.getMaterial(this.getFront()), false);
		}

		this.update();

		return this;

	}
	createPlane(v0, v1, v2, v3) {
		// 重构178 核心语法错误修复 Geometry废弃 改用直接计算
		// // 创建一个几何体
		// const geometry = new THREE.Geometry();

		// // 添加顶点
		// const vertices = [
		// 	new THREE.Vector3(v0.x, v0.y, v0.z), // 左下角
		// 	new THREE.Vector3(v1.x, v1.y, v1.z),  // 右下角
		// 	new THREE.Vector3(v2.x, v2.y, v2.z),   // 右上角
		// 	new THREE.Vector3(v3.x, v3.y, v3.z)   // 左上角
		// ];

		// geometry.vertices.push(...vertices);

		// // 添加面
		// geometry.faces.push(new THREE.Face3(0, 1, 2));
		// geometry.faces.push(new THREE.Face3(0, 2, 3));

		// // 计算法线
		// geometry.computeFaceNormals();
		// geometry.computeVertexNormals();

		// const normal = geometry.faces[0].normal.clone(); // 假设平面只有一个面，取第一个面的法线作为平面的法线向量
		// const constant = normal.dot(geometry.vertices[0]); // 使用平面的一个顶点来计算平面的常数项

		// // 创建THREE.Plane类的对象
		// const plane = new THREE.Plane(normal, constant);
		// return plane;

		// 直接通过向量计算平面法线，避免依赖废弃的 Geometry
		const v1v0 = new THREE.Vector3().subVectors(v1, v0);
		const v2v0 = new THREE.Vector3().subVectors(v2, v0);
		const normal = new THREE.Vector3().crossVectors(v1v0, v2v0).normalize();
		const constant = -normal.dot(v0);
		return new THREE.Plane(normal, constant);
	}
	createPlane3d(v0, v1, v2, v3) {
		// 重构178 核心语法错误修复 Geometry废弃 改用 BufferGeometry
		// // 创建一个几何体
		// const geometry = new THREE.Geometry();

		// // 添加顶点
		// const vertices = [
		// 	new THREE.Vector3(v0.x, v0.y, v0.z), // 左下角
		// 	new THREE.Vector3(v1.x, v1.y, v1.z),  // 右下角
		// 	new THREE.Vector3(v2.x, v2.y, v2.z),   // 右上角
		// 	new THREE.Vector3(v3.x, v3.y, v3.z)   // 左上角
		// ];

		// geometry.vertices.push(...vertices);

		// // 添加面
		// geometry.faces.push(new THREE.Face3(0, 1, 2));
		// geometry.faces.push(new THREE.Face3(0, 2, 3));

		// // 计算法线
		// geometry.computeFaceNormals();

		const geometry = new THREE.BufferGeometry();
		// 顶点数组（4个顶点）
		const vertices = new Float32Array([
			v0.x, v0.y, v0.z, // 0
			v1.x, v1.y, v1.z, // 1
			v2.x, v2.y, v2.z, // 2
			v3.x, v3.y, v3.z  // 3
		]);
		geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

		// 索引数组：2个三角面（0,1,2）和（0,2,3）
		const indices = new Uint16Array([0, 1, 2, 0, 2, 3]);
		geometry.setIndex(new THREE.Uint16BufferAttribute(indices, 1));

		geometry.computeVertexNormals();

		// 创建材质
		const material = new THREE.MeshBasicMaterial({ color: 0xff0000 });

		// 创建平面网格对象
		const planeMesh = new THREE.Mesh(geometry, material);

		return planeMesh;

	}
	alignFF(thisFaceId, base, baseFaceId) {
		var vertices1 = this.box.geometry.getAttribute('position');
		var normals1 = this.box.geometry.getAttribute('normal');

		var x1 = normals1.array[thisFaceId * 6 * 2];
		var y1 = normals1.array[thisFaceId * 6 * 2 + 1];
		var z1 = normals1.array[thisFaceId * 6 * 2 + 2];
		var normal1 = new THREE.Vector3(x1, y1, z1);

		var globalNormal1 = normal1.clone().applyQuaternion(this.getWorldQuaternion(new THREE.Quaternion()));
		var faces1 = this.box.geometry.groups[thisFaceId];
		var index1 = this.box.geometry.getIndex();
		var indexArr1 = index1.array;
		var triIndex1 = indexArr1.slice(faces1.start, faces1.start + faces1.count);
		var thistmp1 = triIndex1.slice(0, 2);
		var thistmp2 = triIndex1.slice(4);
		var faceIndex1 = [...thistmp1, ...thistmp2];
		var vector3Array1 = [];
		for (var i = 0; i < vertices1.array.length; i += 3) {
			var x = vertices1.array[i];
			var y = vertices1.array[i + 1];
			var z = vertices1.array[i + 2];
			var localVec = new THREE.Vector3(x, y, z);
			var globalVec = new THREE.Vector3(x, y, z);
			globalVec = this.localToWorld(localVec);
			vector3Array1.push(globalVec);
		}
		var center1 = new THREE.Vector3();
		faceIndex1.forEach(index => center1.add(vector3Array1[index]));
		center1.divideScalar(faceIndex1.length);

		var faces2 = base.box.geometry.groups[baseFaceId];
		var index2 = base.box.geometry.getIndex();
		var indexArr2 = index2.array;
		var triIndex2 = indexArr2.slice(faces2.start, faces2.start + faces2.count);
		var basetmp1 = triIndex2.slice(0, 2);
		var basetmp2 = triIndex2.slice(4);
		var faceIndex2 = [...basetmp1, ...basetmp2];
		var vertices2 = base.box.geometry.getAttribute('position');
		var vector3Array2 = [];
		for (var i = 0; i < vertices2.array.length; i += 3) {
			var x = vertices2.array[i];
			var y = vertices2.array[i + 1];
			var z = vertices2.array[i + 2];
			var localVec = new THREE.Vector3(x, y, z);
			var globalVec = new THREE.Vector3();
			globalVec = base.localToWorld(localVec);
			vector3Array2.push(globalVec);
		}
		var center2 = new THREE.Vector3();
		faceIndex2.forEach(index => center2.add(vector3Array2[index]));
		center2.divideScalar(faceIndex2.length);

		var normals2 = base.box.geometry.getAttribute('normal');
		var x2 = normals2.array[baseFaceId * 6 * 2];
		var y2 = normals2.array[baseFaceId * 6 * 2 + 1];
		var z2 = normals2.array[baseFaceId * 6 * 2 + 2];
		var normal2 = new THREE.Vector3(x2, y2, z2);


		var globalNormal2 = normal2.clone().applyQuaternion(base.getWorldQuaternion(new THREE.Quaternion()));

		//
		var angle = globalNormal1.angleTo(globalNormal2);

		if (angle !== 0) {
			var quaternion = new THREE.Quaternion();
			quaternion.setFromUnitVectors(globalNormal1, globalNormal2);
			this.applyQuaternion(quaternion);
			this._syncOrientationChanged();
		}
		var plane2 = this.createPlane(vector3Array2[faceIndex2[0]], vector3Array2[faceIndex2[1]], vector3Array2[faceIndex2[2]], vector3Array2[faceIndex2[3]]);

		//
		var vector3ArrayTmp = [];
		for (var i = 0; i < vertices1.array.length; i += 3) {
			var x = vertices1.array[i];
			var y = vertices1.array[i + 1];
			var z = vertices1.array[i + 2];
			var localVec = new THREE.Vector3(x, y, z);
			var globalVec = new THREE.Vector3();

			globalVec = this.localToWorld(localVec);
			vector3ArrayTmp.push(globalVec);
		}
		var planeTmP = this.createPlane(vector3ArrayTmp[faceIndex2[0]], vector3ArrayTmp[faceIndex2[1]], vector3ArrayTmp[faceIndex2[2]], vector3ArrayTmp[faceIndex2[3]]);
		var planeTmP3d = this.createPlane3d(vector3ArrayTmp[faceIndex2[0]], vector3ArrayTmp[faceIndex2[1]], vector3ArrayTmp[faceIndex2[2]], vector3ArrayTmp[faceIndex2[3]]);


		var centerTmp = new THREE.Vector3();
		faceIndex1.forEach(index => centerTmp.add(vector3ArrayTmp[index]));
		centerTmp.divideScalar(faceIndex1.length);


		const planeNormal = plane2.normal;

		const planeNormalTmp = planeTmP.normal;
		// var dotTmp = planeNormalTmp.dot(planeNormal);
		// var parallel = planeNormalTmp.angleTo(planeNormal);

		var centerdiff = new THREE.Vector3(center2.x - centerTmp.x, center2.y - centerTmp.y, center2.z - centerTmp.z);

		const projectionVector = new THREE.Vector3().copy(planeNormal).multiplyScalar(centerdiff.dot(planeNormal));

		this.position.add(projectionVector);

		return planeTmP3d;//for debug


	}
	alignFFM(thisFaceId, base, baseFaceId) {
		var vertices1 = this.box.geometry.getAttribute('position');
		var normals1 = this.box.geometry.getAttribute('normal');

		var x1 = normals1.array[thisFaceId * 6 * 2];
		var y1 = normals1.array[thisFaceId * 6 * 2 + 1];
		var z1 = normals1.array[thisFaceId * 6 * 2 + 2];
		var normal1 = new THREE.Vector3(x1, y1, z1);

		var globalNormal1 = normal1.clone().applyQuaternion(this.getWorldQuaternion(new THREE.Quaternion()));
		var faces1 = this.box.geometry.groups[thisFaceId];
		var index1 = this.box.geometry.getIndex();
		var indexArr1 = index1.array;
		var triIndex1 = indexArr1.slice(faces1.start, faces1.start + faces1.count);
		var thistmp1 = triIndex1.slice(0, 2);
		var thistmp2 = triIndex1.slice(4);
		var faceIndex1 = [...thistmp1, ...thistmp2];
		var vector3Array1 = [];
		for (var i = 0; i < vertices1.array.length; i += 3) {
			var x = vertices1.array[i];
			var y = vertices1.array[i + 1];
			var z = vertices1.array[i + 2];
			var localVec = new THREE.Vector3(x, y, z);
			var globalVec = new THREE.Vector3(x, y, z);
			globalVec = this.localToWorld(localVec);
			vector3Array1.push(globalVec);
		}
		var center1 = new THREE.Vector3();
		faceIndex1.forEach(index => center1.add(vector3Array1[index]));
		center1.divideScalar(faceIndex1.length);

		var faces2 = base.box.geometry.groups[baseFaceId];
		var index2 = base.box.geometry.getIndex();
		var indexArr2 = index2.array;
		var triIndex2 = indexArr2.slice(faces2.start, faces2.start + faces2.count);
		var basetmp1 = triIndex2.slice(0, 2);
		var basetmp2 = triIndex2.slice(4);
		var faceIndex2 = [...basetmp1, ...basetmp2];
		var vertices2 = base.box.geometry.getAttribute('position');
		var vector3Array2 = [];
		for (var i = 0; i < vertices2.array.length; i += 3) {
			var x = vertices2.array[i];
			var y = vertices2.array[i + 1];
			var z = vertices2.array[i + 2];
			var localVec = new THREE.Vector3(x, y, z);
			var globalVec = new THREE.Vector3();
			globalVec = base.localToWorld(localVec);
			vector3Array2.push(globalVec);
		}
		var thisBoxCenter = new THREE.Vector3((this.boundingBox.min.x + this.boundingBox.max.x) * 0.5, (this.boundingBox.min.y + this.boundingBox.max.y) * 0.5, (this.boundingBox.min.z + this.boundingBox.max.z) * 0.5);
		var thisBoxCenterGlobal = this.localToWorld(thisBoxCenter);
		var thisBoxCenter = new THREE.Vector3((base.boundingBox.min.x + base.boundingBox.max.x) * 0.5, (base.boundingBox.min.y + base.boundingBox.max.y) * 0.5, (base.boundingBox.min.z + base.boundingBox.max.z) * 0.5);
		var baseBoxCenterGlobal = base.localToWorld(thisBoxCenter);

		// var center2 = new THREE.Vector3();
		// faceIndex2.forEach(index => center2.add(vector3Array2[index]));
		// center2.divideScalar(faceIndex2.length);

		var normals2 = base.box.geometry.getAttribute('normal');
		var x2 = normals2.array[baseFaceId * 6 * 2];
		var y2 = normals2.array[baseFaceId * 6 * 2 + 1];
		var z2 = normals2.array[baseFaceId * 6 * 2 + 2];
		var normal2 = new THREE.Vector3(x2, y2, z2);


		var globalNormal2 = normal2.clone().applyQuaternion(base.getWorldQuaternion(new THREE.Quaternion()));

		//
		var angle = globalNormal1.angleTo(globalNormal2);

		if (angle !== 0) {
			var quaternion = new THREE.Quaternion();
			quaternion.setFromUnitVectors(globalNormal1, globalNormal2);
			this.applyQuaternion(quaternion);
			this._syncOrientationChanged();
		}
		var plane2 = this.createPlane(vector3Array2[faceIndex2[0]], vector3Array2[faceIndex2[1]], vector3Array2[faceIndex2[2]], vector3Array2[faceIndex2[3]]);

		//
		var vector3ArrayTmp = [];
		for (var i = 0; i < vertices1.array.length; i += 3) {
			var x = vertices1.array[i];
			var y = vertices1.array[i + 1];
			var z = vertices1.array[i + 2];
			var localVec = new THREE.Vector3(x, y, z);
			var globalVec = new THREE.Vector3();

			globalVec = this.localToWorld(localVec);
			vector3ArrayTmp.push(globalVec);
		}
		var planeTmP = this.createPlane(vector3ArrayTmp[faceIndex2[0]], vector3ArrayTmp[faceIndex2[1]], vector3ArrayTmp[faceIndex2[2]], vector3ArrayTmp[faceIndex2[3]]);
		var planeTmP3d = this.createPlane3d(vector3ArrayTmp[faceIndex2[0]], vector3ArrayTmp[faceIndex2[1]], vector3ArrayTmp[faceIndex2[2]], vector3ArrayTmp[faceIndex2[3]]);


		// var centerTmp = new THREE.Vector3();
		// faceIndex1.forEach(index => centerTmp.add(vector3ArrayTmp[index]));
		// centerTmp.divideScalar(faceIndex1.length);


		const planeNormal = plane2.normal;

		// const planeNormalTmp = planeTmP.normal;
		// var dotTmp = planeNormalTmp.dot(planeNormal);
		// var parallel = planeNormalTmp.angleTo(planeNormal);

		// var centerdiff = new THREE.Vector3(center2.x-centerTmp.x,center2.y-centerTmp.y,center2.z-centerTmp.z);
		var centerdiff = new THREE.Vector3(baseBoxCenterGlobal.x - thisBoxCenterGlobal.x, baseBoxCenterGlobal.y - thisBoxCenterGlobal.y, baseBoxCenterGlobal.z - thisBoxCenterGlobal.z);

		const projectionVector = new THREE.Vector3().copy(planeNormal).multiplyScalar(centerdiff.dot(planeNormal));

		this.position.add(projectionVector);

		return planeTmP3d;//for debug


	}
	getFaceIndex(volume, faceId) {
		var faces1 = volume.box.geometry.groups[faceId];
		var index2 = volume.box.geometry.getIndex();
		var indexArr2 = index2.array;
		var triIndex2 = indexArr2.slice(faces1.start, faces1.start + faces1.count);
		var tmp1 = triIndex2.slice(0, 2);
		var tmp2 = triIndex2.slice(4);
		var faceIndex = [...tmp1, ...tmp2];
		return faceIndex;
	}
	getFaceGlobalVert(volume) {
		var vertices1 = volume.box.geometry.getAttribute('position');
		// var thisfaces1 = this.box.geometry.groups[faceId];
		// var thisindex = this.box.geometry.getIndex();
		// var thisindexArr = thisindex.array;
		// var triIndex1 = thisindexArr.slice(thisfaces1.start, thisfaces1.start+thisfaces1.count);
		// var thistmp1 = triIndex1.slice(0,2);
		// var thistmp2 = triIndex1.slice(4);
		// var thisfaceIndex1 = [...thistmp1,...thistmp2];
		var vector3Array1 = [];
		for (var i = 0; i < vertices1.array.length; i += 3) {
			var x = vertices1.array[i];
			var y = vertices1.array[i + 1];
			var z = vertices1.array[i + 2];
			var localVec = new THREE.Vector3(x, y, z);
			var globalVec = new THREE.Vector3(x, y, z);
			globalVec = volume.localToWorld(localVec);
			vector3Array1.push(globalVec);
		}
		return vector3Array1

	}
	getFaceCenter(volume, faceId) {
		var thisfaces1 = volume.box.geometry.groups[faceId];
		var thisindex = volume.box.geometry.getIndex();
		var thisindexArr = thisindex.array;
		var triIndex1 = thisindexArr.slice(thisfaces1.start, thisfaces1.start + thisfaces1.count);
		var thistmp1 = triIndex1.slice(0, 2);
		var thistmp2 = triIndex1.slice(4);
		var thisfaceIndex1 = [...thistmp1, ...thistmp2];
		var vector3Array1 = this.getFaceGlobalVert(volume);
		var center1 = new THREE.Vector3();
		thisfaceIndex1.forEach(index => center1.add(vector3Array1[index]));
		center1.divideScalar(thisfaceIndex1.length);
		return center1;
	}
	getFaceNormal(faceId) {
		var normals1 = this.box.geometry.getAttribute('normal');

		var x1 = normals1.array[faceId * 6 * 2];
		var y1 = normals1.array[faceId * 6 * 2 + 1];
		var z1 = normals1.array[faceId * 6 * 2 + 2];
		var thisNormal1 = new THREE.Vector3(x1, y1, z1);

		var thisGlobalNormal1 = thisNormal1.clone().applyQuaternion(this.getWorldQuaternion(new THREE.Quaternion()));
		return thisGlobalNormal1;
	}
	alignFFM2Axis(thisFaceId1, thisFaceId2, base, baseFaceId1, baseFaceId2) {

		var normals1 = this.box.geometry.getAttribute('normal');

		var x1 = normals1.array[thisFaceId1 * 6 * 2];
		var y1 = normals1.array[thisFaceId1 * 6 * 2 + 1];
		var z1 = normals1.array[thisFaceId1 * 6 * 2 + 2];
		var thisNormal1 = new THREE.Vector3(x1, y1, z1);

		var thisGlobalNormal1 = thisNormal1.clone().applyQuaternion(this.getWorldQuaternion(new THREE.Quaternion()));



		// var basefaces1 = base.box.geometry.groups[baseFaceId1];
		// var index2 = base.box.geometry.getIndex();
		// var indexArr2 = index2.array;
		// var triIndex2 = indexArr2.slice(basefaces1.start, basefaces1.start+basefaces1.count);
		// var basetmp1 = triIndex2.slice(0,2);
		// var basetmp2 = triIndex2.slice(4);
		// var basefaceIndex = [...basetmp1,...basetmp2];

		// var vertices2 = base.box.geometry.getAttribute('position');
		// var vector3Array2 = [];
		// for (var i = 0; i < vertices2.array.length; i += 3) {
		// 	var x = vertices2.array[i];
		// 	var y = vertices2.array[i + 1];
		// 	var z = vertices2.array[i + 2];
		// 	var localVec = new THREE.Vector3(x, y, z);
		// 	var globalVec = new THREE.Vector3();
		// 	globalVec = base.localToWorld(localVec);
		// 	vector3Array2.push(globalVec);
		// }
		// var basevector3Array = this.getFaceGlobalVert(base);
		var thisBoxCenter = new THREE.Vector3((this.boundingBox.min.x + this.boundingBox.max.x) * 0.5, (this.boundingBox.min.y + this.boundingBox.max.y) * 0.5, (this.boundingBox.min.z + this.boundingBox.max.z) * 0.5);
		var thisBoxCenterGlobal = this.localToWorld(thisBoxCenter);
		var thisBoxCenter = new THREE.Vector3((base.boundingBox.min.x + base.boundingBox.max.x) * 0.5, (base.boundingBox.min.y + base.boundingBox.max.y) * 0.5, (base.boundingBox.min.z + base.boundingBox.max.z) * 0.5);
		var baseBoxCenterGlobal = base.localToWorld(thisBoxCenter);

		// var center2 = new THREE.Vector3();
		// faceIndex2.forEach(index => center2.add(basevector3Array[index]));
		// center2.divideScalar(faceIndex2.length);

		var normals2 = base.box.geometry.getAttribute('normal');
		var x2 = normals2.array[baseFaceId1 * 6 * 2];
		var y2 = normals2.array[baseFaceId1 * 6 * 2 + 1];
		var z2 = normals2.array[baseFaceId1 * 6 * 2 + 2];
		var baseNormal1 = new THREE.Vector3(x2, y2, z2);
		var baseGlobalNormal1 = baseNormal1.clone().applyQuaternion(base.getWorldQuaternion(new THREE.Quaternion()));

		var x22 = normals2.array[baseFaceId2 * 6 * 2];
		var y22 = normals2.array[baseFaceId2 * 6 * 2 + 1];
		var z22 = normals2.array[baseFaceId2 * 6 * 2 + 2];
		var baseNormal2 = new THREE.Vector3(x22, y22, z22);
		var baseGlobalNormal2 = baseNormal2.clone().applyQuaternion(base.getWorldQuaternion(new THREE.Quaternion()));
		//
		var angle = thisGlobalNormal1.angleTo(baseGlobalNormal1);

		if (angle !== 0) {
			var quaternion = new THREE.Quaternion();
			quaternion.setFromUnitVectors(thisGlobalNormal1, baseGlobalNormal1);
			this.applyQuaternion(quaternion);
			this._syncOrientationChanged();
		}

		var x12 = normals1.array[thisFaceId2 * 6 * 2];
		var y12 = normals1.array[thisFaceId2 * 6 * 2 + 1];
		var z12 = normals1.array[thisFaceId2 * 6 * 2 + 2];
		var thisNormal2 = new THREE.Vector3(x12, y12, z12);
		var thisGlobalNormal2 = thisNormal2.clone().applyQuaternion(this.getWorldQuaternion(new THREE.Quaternion()));

		var angle2 = thisGlobalNormal2.angleTo(baseGlobalNormal2);

		if (angle2 !== 0) {
			var quaternion = new THREE.Quaternion();
			quaternion.setFromUnitVectors(thisGlobalNormal2, baseGlobalNormal2);
			this.applyQuaternion(quaternion);
			this._syncOrientationChanged();
		}
		var basevector3Array = this.getFaceGlobalVert(base);
		var baseIndexface1 = this.getFaceIndex(base, baseFaceId1);
		var baseplaneFace1 = this.createPlane(basevector3Array[baseIndexface1[0]], basevector3Array[baseIndexface1[1]], basevector3Array[baseIndexface1[2]], basevector3Array[baseIndexface1[3]]);
		var baseIndexface2 = this.getFaceIndex(base, baseFaceId2);
		var baseplaneFace2 = this.createPlane(basevector3Array[baseIndexface2[0]], basevector3Array[baseIndexface2[1]], basevector3Array[baseIndexface2[2]], basevector3Array[baseIndexface2[3]]);

		//
		// var vector3ArrayTmp = [];
		// for (var i = 0; i < vertices1.array.length; i += 3) {
		// 	var x = vertices1.array[i];
		// 	var y = vertices1.array[i + 1];
		// 	var z = vertices1.array[i + 2];
		// 	var localVec = new THREE.Vector3(x, y, z);
		// 	var globalVec = new THREE.Vector3();

		// 	globalVec = this.localToWorld(localVec);
		// 	vector3ArrayTmp.push(globalVec);
		// }
		var vector3ArrayTmp = this.getFaceGlobalVert(this);
		// var planeTmP = this.createPlane(vector3ArrayTmp[baseIndexface1[0]],vector3ArrayTmp[baseIndexface1[1]],vector3ArrayTmp[baseIndexface1[2]],vector3ArrayTmp[baseIndexface1[3]]);
		var planeTmP3d = this.createPlane3d(vector3ArrayTmp[baseIndexface1[0]], vector3ArrayTmp[baseIndexface1[1]], vector3ArrayTmp[baseIndexface1[2]], vector3ArrayTmp[baseIndexface1[3]]);


		// var centerTmp = new THREE.Vector3();
		// faceIndex1.forEach(index => centerTmp.add(vector3ArrayTmp[index]));
		// centerTmp.divideScalar(faceIndex1.length);


		const planeNormalface1 = baseplaneFace1.normal;


		// const planeNormalTmp = planeTmP.normal;
		// var dotTmp = planeNormalTmp.dot(planeNormal);
		// var parallel = planeNormalTmp.angleTo(planeNormal);

		// var centerdiff = new THREE.Vector3(center2.x-centerTmp.x,center2.y-centerTmp.y,center2.z-centerTmp.z);
		var centerdiff = new THREE.Vector3(baseBoxCenterGlobal.x - thisBoxCenterGlobal.x, baseBoxCenterGlobal.y - thisBoxCenterGlobal.y, baseBoxCenterGlobal.z - thisBoxCenterGlobal.z);

		const projectionVector1 = new THREE.Vector3().copy(planeNormalface1).multiplyScalar(centerdiff.dot(planeNormalface1));

		this.position.add(projectionVector1);

		const planeNormalface2 = baseplaneFace2.normal;
		const projectionVector2 = new THREE.Vector3().copy(planeNormalface2).multiplyScalar(centerdiff.dot(planeNormalface2));
		this.position.add(projectionVector2);
		return planeTmP3d;//for debug


	}
	// alignBaseOn2ObjectM(base1,base2,base1faceid)
	// {
	// 	var alignFace2Id;
	// 	if(base1faceid === base1.getRight()||base1faceid === base1.getLeft())
	// 	{
	// 		alignFace2Id = base1.getFront();
	// 	}
	// 	if(base1faceid === base1.getFront()||base1faceid === base1.getBack())
	// 	{
	// 		alignFace2Id = base1.getRight();
	// 	}

	// 	var base1GlobalNormal = base1.getFaceNormal(base1faceid);
	// 	var base1BoxCenter = new THREE.Vector3((base1.boundingBox.min.x+base1.boundingBox.max.x)*0.5,(base1.boundingBox.min.y+base1.boundingBox.max.y)*0.5,(base1.boundingBox.min.z+base1.boundingBox.max.z)*0.5);
	// 	var base2BoxCenter = new THREE.Vector3((base2.boundingBox.min.x+base2.boundingBox.max.x)*0.5,(base2.boundingBox.min.y+base2.boundingBox.max.y)*0.5,(base2.boundingBox.min.z+base2.boundingBox.max.z)*0.5);
	// 	var thisBoxCenter = new THREE.Vector3((this.boundingBox.min.x+this.boundingBox.max.x)*0.5,(this.boundingBox.min.y+this.boundingBox.max.y)*0.5,(this.boundingBox.min.z+this.boundingBox.max.z)*0.5);

	// 	var base1BoxCenterGlobal = base1.localToWorld(base1BoxCenter);
	// 	var base2BoxCenterGlobal = base2.localToWorld(base2BoxCenter);
	// 	var middle = new THREE.Vector3((base1BoxCenterGlobal.x+base2BoxCenterGlobal.x)*0.5,(base1BoxCenterGlobal.y+base2BoxCenterGlobal.y)*0.5,(base1BoxCenterGlobal.z+base2BoxCenterGlobal.z)*0.5);

	// 	var thisBoxCenterGlobal = this.localToWorld(thisBoxCenter);
	// 	var centerdiff = new THREE.Vector3(middle.x-thisBoxCenterGlobal.x,middle.y-thisBoxCenterGlobal.y,middle.z-thisBoxCenterGlobal.z);

	// 	const projectionVector1 = new THREE.Vector3().copy(base1GlobalNormal).multiplyScalar(centerdiff.dot(base1GlobalNormal));
	// 	this.position.add(projectionVector1);


	// 	var base1Face2GlobalNormal = base1.getFaceNormal(alignFace2Id);
	// 	var basevector3Array = this.getFaceGlobalVert(base1);
	// 	var baseIndexface1 = this.getFaceIndex(base1,base1faceid);
	// 	var thisvector3Array = this.getFaceGlobalVert(this);
	// 	var baseIndexface1 = this.getFaceIndex(this,this.getFront());

	// 	var ptBaseFace = basevector3Array[baseIndexface1[0]];
	// 	var ptThisFace = thisvector3Array[baseIndexface1[0]];

	// 	var centerdiffFace = new THREE.Vector3(ptBaseFace.x-ptThisFace.x,ptBaseFace.y-ptThisFace.y,ptBaseFace.z-ptThisFace.z);
	// 	const projectionVector2 = new THREE.Vector3().copy(base1Face2GlobalNormal).multiplyScalar(centerdiffFace.dot(base1Face2GlobalNormal));
	// 	this.position.add(projectionVector2);
	// }
	alignBaseOn2ObjectM(base1, base2, base1faceid) {

		var base1GlobalNormal = base1.getFaceNormal(base1faceid);
		var base1BoxCenter = new THREE.Vector3((base1.boundingBox.min.x + base1.boundingBox.max.x) * 0.5, (base1.boundingBox.min.y + base1.boundingBox.max.y) * 0.5, (base1.boundingBox.min.z + base1.boundingBox.max.z) * 0.5);
		var base2BoxCenter = new THREE.Vector3((base2.boundingBox.min.x + base2.boundingBox.max.x) * 0.5, (base2.boundingBox.min.y + base2.boundingBox.max.y) * 0.5, (base2.boundingBox.min.z + base2.boundingBox.max.z) * 0.5);
		var thisBoxCenter = new THREE.Vector3((this.boundingBox.min.x + this.boundingBox.max.x) * 0.5, (this.boundingBox.min.y + this.boundingBox.max.y) * 0.5, (this.boundingBox.min.z + this.boundingBox.max.z) * 0.5);

		var base1BoxCenterGlobal = base1.localToWorld(base1BoxCenter);
		var base2BoxCenterGlobal = base2.localToWorld(base2BoxCenter);
		var middle = new THREE.Vector3((base1BoxCenterGlobal.x + base2BoxCenterGlobal.x) * 0.5, (base1BoxCenterGlobal.y + base2BoxCenterGlobal.y) * 0.5, (base1BoxCenterGlobal.z + base2BoxCenterGlobal.z) * 0.5);

		var thisBoxCenterGlobal = this.localToWorld(thisBoxCenter);
		var centerdiff = new THREE.Vector3(middle.x - thisBoxCenterGlobal.x, middle.y - thisBoxCenterGlobal.y, middle.z - thisBoxCenterGlobal.z);

		const projectionVector1 = new THREE.Vector3().copy(base1GlobalNormal).multiplyScalar(centerdiff.dot(base1GlobalNormal));
		this.position.add(projectionVector1);

	}
	/**
	  * 通过一些包围盒列表 获取包围盒列表的中心点及大小
	  * @param {Array} volumeList - 包围盒列表
	*/
	getCenterAndSizeByVolumeList(volumeList) {
		if (!Array.isArray(volumeList) || volumeList.length === 0) {
			return;
		}

		// 初始化最大最小值
		let minX = Infinity, minY = Infinity, minZ = Infinity;
		let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

		volumeList.forEach(volume => {
			var vertices2 = volume.box.geometry.getAttribute('position');
			for (var i = 0; i < vertices2.array.length; i += 3) {
				var x = vertices2.array[i];
				var y = vertices2.array[i + 1];
				var z = vertices2.array[i + 2];
				var localVec = new THREE.Vector3(x, y, z);
				var globalVec = new THREE.Vector3();
				globalVec = volume.localToWorld(localVec);

				// 更新最小值
				minX = Math.min(minX, globalVec.x);
				minY = Math.min(minY, globalVec.y);
				minZ = Math.min(minZ, globalVec.z);

				// 更新最大值
				maxX = Math.max(maxX, globalVec.x);
				maxY = Math.max(maxY, globalVec.y);
				maxZ = Math.max(maxZ, globalVec.z);
			}
		});

		// 计算中心点
		const centerX = (minX + maxX) / 2;
		const centerY = (minY + maxY) / 2;
		const centerZ = (minZ + maxZ) / 2;

		// 计算差值（尺寸）
		const diffX = maxX - minX;
		const diffY = maxY - minY;
		const diffZ = maxZ - minZ;

		return {
			center: new THREE.Vector3(centerX, centerY, centerZ),
			size: new THREE.Vector3(diffX, diffY, diffZ)
		};
	}

	/**
	  * 设置当前Volume的位姿为父节点的本地坐标系
	  * 需求：当前Volume的位姿是基于世界坐标系的，现需要将当前Volume添加到父节点中。
	  * 所以需要将当前Volume的位姿转换为父节点的坐标系，这样才能保证看到Volume是没有变化的
	  * @param {Volume} parentVolume - 父级
	*/
	setLocalPoseByParentVolume(parentVolume) {
		if (parentVolume === null) {
			return {}
		}
		// 计算父模型的逆变换矩阵
		const parentInverseMatrix = new THREE.Matrix4().copy(parentVolume.matrixWorld).invert();
		// 创建一个表示子模型完整世界变换的矩阵
		const worldMatrix = new THREE.Matrix4();
		worldMatrix.compose(
			this.position,  // 世界位置
			this.quaternion,  // 世界旋转（使用四元数）
			this.scale  // 世界缩放
		);

		// 计算子模型在父模型本地坐标系中的矩阵
		const localMatrix = new THREE.Matrix4().multiplyMatrices(parentInverseMatrix, worldMatrix);

		// 从本地矩阵中分解出位置、旋转和缩放
		const localPosition = new THREE.Vector3();
		const localQuaternion = new THREE.Quaternion();
		const localScale = new THREE.Vector3();

		localMatrix.decompose(localPosition, localQuaternion, localScale);
		// 应用本地变换到子模型 localPosition localQuaternion localScale 新的子模型位姿
		this.position.copy(localPosition);
		this.quaternion.copy(localQuaternion);
		this.scale.copy(localScale);
		this._syncOrientationChanged();
	}
	/**
	  * 获取当前Volume的位姿是世界坐标系
	  * 需求：上述方法的逆运算
	  * @param {Volume} parentVolume - 父级
	*/
	getWorldPoseByParentVolume(parentVolume) {
		if (parentVolume === null) {
			return {}
		}
		const parentMatrixWorld = parentVolume.matrixWorld
		// 创建一个表示子模型在父级坐标系中变换的矩阵
		const localMatrix = new THREE.Matrix4();
		localMatrix.compose(
			this.position,  // 父级坐标系中的位置
			this.quaternion,  // 父级坐标系中的旋转
			this.scale  // 父级坐标系中的缩放
		);

		// 计算子模型在世界坐标系中的矩阵
		// 注意：这里直接使用父级的世界矩阵，而非逆矩阵
		const worldMatrix = new THREE.Matrix4().multiplyMatrices(parentMatrixWorld, localMatrix);

		// 步骤1：从世界矩阵分解出原始的位置、旋转、缩放（可能含负缩放）
		const worldPosition = new THREE.Vector3()
		const worldQuaternion = new THREE.Quaternion()
		const worldScale = new THREE.Vector3()

		worldMatrix.decompose(worldPosition, worldQuaternion, worldScale)

		// 步骤2：提取缩放的绝对值（保证scale恒正），记录负缩放的轴
		const positiveScale = new THREE.Vector3(
			Math.abs(worldScale.x),
			Math.abs(worldScale.y),
			Math.abs(worldScale.z)
		)

		return {
			position: worldPosition,
			quaternion: worldQuaternion,
			scale: positiveScale
		}
	}
	setWorldPoseByParentVolume(parentVolume) {
		const { position, quaternion, scale } = this.getWorldPoseByParentVolume(parentVolume);
		// 应用世界变换到子模型
		this.position.copy(position);
		this.quaternion.copy(quaternion);
		this.scale.copy(scale);
		this._syncOrientationChanged();
	};

};
export class SphereVolume extends Volume {

	constructor(args = {}) {
		super(args);

		this.constructor.counter = (this.constructor.counter === undefined) ? 0 : this.constructor.counter + 1;
		this.name = 'sphere_' + this.constructor.counter;

		let sphereGeometry = new THREE.SphereGeometry(1, 32, 32);
		sphereGeometry.computeBoundingBox();

		this.material = new THREE.MeshBasicMaterial({
			color: 0x00ff00,
			transparent: true,
			opacity: 0.3,
			depthTest: true,
			depthWrite: false
		});
		this.sphere = new THREE.Mesh(sphereGeometry, this.material);
		this.sphere.visible = false;
		this.sphere.geometry.computeBoundingBox();
		this.boundingBox = this.sphere.geometry.boundingBox;
		this.add(this.sphere);

		this.label.visible = false;

		// 重构178 核心语法错误修复 Geometry废弃 改用BufferGeometry
		// let frameGeometry = new THREE.Geometry();
		let frameGeometry = new THREE.BufferGeometry();
		const verticesArray = [];
		{
			let steps = 64;
			let uSegments = 8;
			let vSegments = 5;
			let r = 1;

			for (let uSegment = 0; uSegment < uSegments; uSegment++) {

				let alpha = (uSegment / uSegments) * Math.PI * 2;
				let dirx = Math.cos(alpha);
				let diry = Math.sin(alpha);

				for (let i = 0; i <= steps; i++) {
					let v = (i / steps) * Math.PI * 2;
					let vNext = v + 2 * Math.PI / steps;

					let height = Math.sin(v);
					let xyAmount = Math.cos(v);

					let heightNext = Math.sin(vNext);
					let xyAmountNext = Math.cos(vNext);

					verticesArray.push(dirx * xyAmount, diry * xyAmount, height);
					verticesArray.push(dirx * xyAmountNext, diry * xyAmountNext, heightNext);
				}
			}

			// creates rings at poles, just because it's easier to implement
			for (let vSegment = 0; vSegment <= vSegments + 1; vSegment++) {

				//let height = (vSegment / (vSegments + 1)) * 2 - 1; // -1 to 1
				let uh = (vSegment / (vSegments + 1)); // -1 to 1
				uh = (1 - uh) * (-Math.PI / 2) + uh * (Math.PI / 2);
				let height = Math.sin(uh);

				for (let i = 0; i <= steps; i++) {
					let u = (i / steps) * Math.PI * 2;
					let uNext = u + 2 * Math.PI / steps;

					let dirx = Math.cos(u);
					let diry = Math.sin(u);

					let dirxNext = Math.cos(uNext);
					let diryNext = Math.sin(uNext);

					let xyAmount = Math.sqrt(1 - height * height);

					verticesArray.push(dirx * xyAmount, diry * xyAmount, height);
					verticesArray.push(dirxNext * xyAmount, diryNext * xyAmount, height);
				}
			}
		}
		// 3. 关键：将浮点数组转为 BufferAttribute，并绑定到 BufferGeometry
		// Float32Array 适配 WebGL 底层，3 表示每个顶点由 3 个分量（x,y,z）组成
		frameGeometry.setAttribute(
			'position',
			new THREE.BufferAttribute(new Float32Array(verticesArray), 3)
		);

		this.frame = new THREE.LineSegments(frameGeometry, new THREE.LineBasicMaterial({ color: 0x000000 }));
		this.add(this.frame);

		let frameMaterial = new THREE.MeshBasicMaterial({ wireframe: true, color: 0x000000 });
		this.frame = new THREE.Mesh(sphereGeometry, frameMaterial);

		this.update();
	}

	update() {
		this.boundingBox = this.sphere.geometry.boundingBox;
		this.boundingSphere = this.boundingBox.getBoundingSphere(new THREE.Sphere());

		// if (this._clip) {
		// 	this.sphere.visible = false;
		// 	this.label.visible = false;
		// } else {
		// 	this.sphere.visible = true;
		// 	this.label.visible = this.showVolumeLabel;
		// }
	}

	raycast(raycaster, intersects) {
		let is = [];
		this.sphere.raycast(raycaster, is);

		if (is.length > 0) {
			let I = is[0];
			intersects.push({
				distance: I.distance,
				object: this,
				point: I.point.clone()
			});
		}
	}

	// see https://en.wikipedia.org/wiki/Ellipsoid#Volume
	getVolume() {
		return (4 / 3) * Math.PI * this.scale.x * this.scale.y * this.scale.z;
	}

};